import { PUBLIC_EVENTS_DATASETS } from "./events-datasets-Cu37u43g.js";
import { agentTool, recordAgentToolResultCount } from "./utils-DHwF0RI3.js";
import { z } from "zod";

//#region src/internal/agents/tools/data/android.json
var namespace$72 = "android";
var description$72 = "The Android platform on which the Android application is running.\n";
var attributes$72 = {
	"android.os.api_level": {
		"description": "Uniquely identifies the framework API revision offered by a version (`os.version`) of the android operating system. More information can be found [here](https://developer.android.com/guide/topics/manifest/uses-sdk-element#ApiLevels).\n",
		"type": "string",
		"stability": "development",
		"examples": ["33", "32"]
	},
	"android.app.state": {
		"description": "This attribute represents the state of the application.\n",
		"type": "string",
		"note": "The Android lifecycle states are defined in [Activity lifecycle callbacks](https://developer.android.com/guide/components/activities/activity-lifecycle#lc), and from which the `OS identifiers` are derived.\n",
		"stability": "development",
		"examples": [
			"created",
			"background",
			"foreground"
		]
	}
};
var android_default = {
	namespace: namespace$72,
	description: description$72,
	attributes: attributes$72
};

//#endregion
//#region src/internal/agents/tools/data/app.json
var namespace$71 = "app";
var custom = true;
var description$71 = "Describes attributes related to client-side applications (e.g. web apps or mobile apps), plus Sentry MCP application-owned telemetry attributes.\n";
var attributes$71 = {
	"app.access.error.code": {
		"description": "Scrubber-safe OAuth error code returned by Sentry MCP or the OAuth provider.",
		"type": "string",
		"note": "Emitted under app.access.* so default Sentry data scrubbing does not redact values via sensitive substrings (auth/oauth, token, bearer, credentials). invalid_token responses are bucketed as invalid_access.",
		"examples": [
			"invalid_access",
			"invalid_grant",
			"invalid_client",
			"other"
		]
	},
	"app.access.error.reason": {
		"description": "Low-cardinality scrubber-safe bucket for an OAuth error description.",
		"type": "string",
		"note": "Values deliberately avoid sensitive substrings (token, bearer, auth/oauth, credentials).",
		"examples": [
			"missing_or_invalid_access",
			"invalid_access",
			"grant_not_found",
			"other"
		]
	},
	"app.access.grant.age_bucket": {
		"description": "Bounded MCP grant age at refresh or revocation.",
		"type": "string",
		"examples": [
			"lt_1h",
			"1d_7d",
			"gte_30d",
			"unknown"
		]
	},
	"app.access.grant.id_hash": {
		"description": "Deterministic non-secret fingerprint of a Sentry MCP OAuth grant ID used to correlate grant lifecycle logs.",
		"type": "string",
		"examples": ["a1b2c3d4"]
	},
	"app.access.grant.revoked_reason": {
		"description": "Reason a Sentry MCP OAuth wrapper grant was revoked.",
		"type": "string",
		"examples": [
			"stale_props_no_refresh",
			"upstream_rejected",
			"upstream_rejected_in_use"
		]
	},
	"app.access.grant.shape": {
		"description": "Shape of the Sentry MCP OAuth grant used during refresh.",
		"type": "string",
		"examples": ["refreshable"]
	},
	"app.access.method": {
		"description": "How the MCP request obtained upstream Sentry access.",
		"type": "string",
		"examples": ["mcp_grant", "sentry_access"]
	},
	"app.access.probe.reason": {
		"description": "Sentry MCP bucket for an indeterminate upstream OAuth access probe.",
		"type": "string",
		"examples": [
			"rate_limit",
			"server_error",
			"unknown"
		]
	},
	"app.access.probe.status_code": {
		"description": "HTTP status returned by the upstream Sentry access-validity probe.",
		"type": "string",
		"examples": [
			"200",
			"401",
			"429",
			"500"
		]
	},
	"app.access.refresh.outcome": {
		"description": "Outcome bucket for a Sentry MCP OAuth refresh/token-exchange path.",
		"type": "string",
		"note": "Attached to metric app.oauth.token_exchange. Attribute key avoids sensitive substrings for scrubber safety.",
		"examples": [
			"cached_valid_local",
			"cached_valid_probed",
			"upstream_rejected",
			"verification_indeterminate"
		]
	},
	"app.access.request.header_shape": {
		"description": "Low-cardinality shape of the Authorization header supplied to an access-protected request.",
		"type": "string",
		"examples": [
			"missing",
			"wrapper",
			"malformed",
			"non_scheme",
			"empty_scheme"
		]
	},
	"app.access.upstream.expires_in_bucket": {
		"description": "Bounded remaining time before the original upstream Sentry expiry at refresh or revocation.",
		"type": "string",
		"examples": [
			"expired",
			"lt_1h",
			"1d_7d",
			"unknown"
		]
	},
	"app.client.family": {
		"description": "Sentry MCP low-cardinality bucket for the MCP client family.",
		"type": "string",
		"note": "Resolved from the MCP client's User-Agent on direct MCP requests, or from the registered client name on browser-mediated OAuth endpoints.",
		"examples": [
			"claude-code",
			"cursor",
			"codex",
			"unknown"
		]
	},
	"app.consent.skill": {
		"description": "Sentry MCP skill granted during OAuth approval.",
		"type": "string",
		"examples": [
			"inspect",
			"docs",
			"project-management"
		]
	},
	"app.consent.skill.<skill>.granted": {
		"description": "Whether a normalized Sentry MCP skill was granted for a specific MCP request.",
		"type": "boolean",
		"note": "The dynamic <skill> segment uses the skill id with hyphens replaced by underscores.",
		"examples": ["app.consent.skill.project_management.granted"]
	},
	"app.constraint.organization_slug": {
		"description": "Sentry MCP organization slug constraint applied to the MCP session.",
		"type": "string",
		"examples": ["my-org"]
	},
	"app.constraint.project_slug": {
		"description": "Sentry MCP project slug constraint applied to the MCP session.",
		"type": "string",
		"examples": ["backend"]
	},
	"app.installation.id": {
		"description": "A unique identifier representing the installation of an application on a specific device\n",
		"type": "string",
		"note": "Its value SHOULD persist across launches of the same application installation, including through application upgrades.\nIt SHOULD change if the application is uninstalled or if all applications of the vendor are uninstalled.\nAdditionally, users might be able to reset this value (e.g. by clearing application data).\nIf an app is installed multiple times on the same device (e.g. in different accounts on Android), each `app.installation.id` SHOULD have a different value.\nIf multiple OpenTelemetry SDKs are used within the same application, they SHOULD use the same value for `app.installation.id`.\nHardware IDs (e.g. serial number, IMEI, MAC address) MUST NOT be used as the `app.installation.id`.\n\nFor iOS, this value SHOULD be equal to the [vendor identifier](https://developer.apple.com/documentation/uikit/uidevice/identifierforvendor).\n\nFor Android, examples of `app.installation.id` implementations include:\n\n- [Firebase Installation ID](https://firebase.google.com/docs/projects/manage-installations).\n- A globally unique UUID which is persisted across sessions in your application.\n- [App set ID](https://developer.android.com/identity/app-set-id).\n- [`Settings.getString(Settings.Secure.ANDROID_ID)`](https://developer.android.com/reference/android/provider/Settings.Secure#ANDROID_ID).\n\nMore information about Android identifier best practices can be found [here](https://developer.android.com/training/articles/user-data-ids).\n",
		"stability": "development",
		"examples": ["2ab2916d-a51f-4ac8-80ee-45ac31a28092"]
	},
	"app.rate_limit.scope": {
		"description": "Scope of a Sentry MCP local rate-limit response.",
		"type": "string",
		"examples": [
			"ip",
			"user",
			"sentry_access"
		],
		"note": "sentry_access is the scrubber-safe bucket for direct Sentry access-header rate limits."
	},
	"app.referrer.family": {
		"description": "Sentry MCP low-cardinality bucket for the external referrer host.",
		"type": "string",
		"examples": [
			"sentry-docs",
			"github",
			"unknown"
		]
	},
	"app.request.duration_ms": {
		"description": "Sentry MCP request duration in milliseconds.",
		"type": "number",
		"examples": ["42", "1250"]
	},
	"app.resource.type": {
		"description": "Sentry MCP resource type resolved by get_sentry_resource.",
		"type": "string",
		"examples": [
			"issue",
			"event",
			"trace",
			"replay"
		]
	},
	"app.response.reason": {
		"description": "Sentry MCP local response reason bucket.",
		"type": "string",
		"examples": ["local_rate_limit"]
	},
	"app.response.status_class": {
		"description": "HTTP response status family for Sentry MCP response metrics.",
		"type": "string",
		"examples": [
			"2xx",
			"4xx",
			"5xx"
		]
	},
	"app.route.group": {
		"description": "Sentry MCP coarse route family.",
		"type": "string",
		"examples": [
			"mcp",
			"oauth",
			"chat",
			"search"
		]
	},
	"app.screen.coordinate.x": {
		"description": "The x (horizontal) coordinate of a screen coordinate, in screen pixels.",
		"type": "number",
		"stability": "development",
		"examples": ["0", "131"]
	},
	"app.screen.coordinate.y": {
		"description": "The y (vertical) component of a screen coordinate, in screen pixels.\n",
		"type": "number",
		"stability": "development",
		"examples": ["12", "99"]
	},
	"app.server.mode.experimental": {
		"description": "Whether Sentry MCP experimental mode is enabled for the request or stdio session.",
		"type": "boolean",
		"note": "Metrics and tags record this as the low-cardinality string values `true` or `false`; spans record it as a boolean.",
		"examples": [true, false]
	},
	"app.server.version": {
		"description": "Sentry MCP server package version.",
		"type": "string",
		"examples": ["0.35.0"]
	},
	"app.transport": {
		"description": "Sentry MCP product transport label.",
		"type": "string",
		"examples": ["http", "stdio"]
	},
	"app.upstream.host": {
		"description": "Configured upstream Sentry host for Sentry MCP.",
		"type": "string",
		"examples": ["sentry.io"]
	},
	"app.url.full": {
		"description": "Configured Sentry MCP URL for stdio telemetry.",
		"type": "string",
		"note": "Do not include credentials or access tokens in this URL.",
		"examples": ["https://mcp.sentry.dev/mcp"]
	},
	"app.utm_source": {
		"description": "Sanitized in-product utm_source query parameter for Sentry MCP attribution.",
		"type": "string",
		"examples": ["docs", "product"]
	},
	"app.widget.id": {
		"description": "An identifier that uniquely differentiates this widget from other widgets in the same application.\n",
		"type": "string",
		"note": "A widget is an application component, typically an on-screen visual GUI element.\n",
		"stability": "development",
		"examples": ["f9bc787d-ff05-48ad-90e1-fca1d46130b3", "submit_order_1829"]
	},
	"app.widget.name": {
		"description": "The name of an application widget.",
		"type": "string",
		"note": "A widget is an application component, typically an on-screen visual GUI element.\n",
		"stability": "development",
		"examples": [
			"submit",
			"attack",
			"Clear Cart"
		]
	}
};
var app_default = {
	namespace: namespace$71,
	custom,
	description: description$71,
	attributes: attributes$71
};

//#endregion
//#region src/internal/agents/tools/data/artifact.json
var namespace$70 = "artifact";
var description$70 = "This group describes attributes specific to artifacts. Artifacts are files or other immutable objects that are intended for distribution. This definition aligns directly with the [SLSA](https://slsa.dev/spec/v1.0/terminology#package-model) package model.\n";
var attributes$70 = {
	"artifact.filename": {
		"description": "The human readable file name of the artifact, typically generated during build and release processes. Often includes the package name and version in the file name.\n",
		"type": "string",
		"note": "This file name can also act as the [Package Name](https://slsa.dev/spec/v1.0/terminology#package-model)\nin cases where the package ecosystem maps accordingly.\nAdditionally, the artifact [can be published](https://slsa.dev/spec/v1.0/terminology#software-supply-chain)\nfor others, but that is not a guarantee.\n",
		"stability": "development",
		"examples": [
			"golang-binary-amd64-v0.1.0",
			"docker-image-amd64-v0.1.0",
			"release-1.tar.gz",
			"file-name-package.tar.gz"
		]
	},
	"artifact.version": {
		"description": "The version of the artifact.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"v0.1.0",
			"1.2.1",
			"122691-build"
		]
	},
	"artifact.purl": {
		"description": "The [Package URL](https://github.com/package-url/purl-spec) of the [package artifact](https://slsa.dev/spec/v1.0/terminology#package-model) provides a standard way to identify and locate the packaged artifact.\n",
		"type": "string",
		"stability": "development",
		"examples": ["pkg:github/package-url/purl-spec@1209109710924", "pkg:npm/foo@12.12.3"]
	},
	"artifact.hash": {
		"description": "The full [hash value (see glossary)](https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.186-5.pdf), often found in checksum.txt on a release of the artifact and used to verify package integrity.\n",
		"type": "string",
		"note": "The specific algorithm used to create the cryptographic hash value is\nnot defined. In situations where an artifact has multiple\ncryptographic hashes, it is up to the implementer to choose which\nhash value to set here; this should be the most secure hash algorithm\nthat is suitable for the situation and consistent with the\ncorresponding attestation. The implementer can then provide the other\nhash values through an additional set of attribute extensions as they\ndeem necessary.\n",
		"stability": "development",
		"examples": ["9ff4c52759e2c4ac70b7d517bc7fcdc1cda631ca0045271ddd1b192544f8a3e9"]
	},
	"artifact.attestation.id": {
		"description": "The id of the build [software attestation](https://slsa.dev/attestation-model).\n",
		"type": "string",
		"stability": "development",
		"examples": ["123"]
	},
	"artifact.attestation.filename": {
		"description": "The provenance filename of the built attestation which directly relates to the build artifact filename. This filename SHOULD accompany the artifact at publish time. See the [SLSA Relationship](https://slsa.dev/spec/v1.0/distributing-provenance#relationship-between-artifacts-and-attestations) specification for more information.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"golang-binary-amd64-v0.1.0.attestation",
			"docker-image-amd64-v0.1.0.intoto.json1",
			"release-1.tar.gz.attestation",
			"file-name-package.tar.gz.intoto.json1"
		]
	},
	"artifact.attestation.hash": {
		"description": "The full [hash value (see glossary)](https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.186-5.pdf), of the built attestation. Some envelopes in the [software attestation space](https://github.com/in-toto/attestation/tree/main/spec) also refer to this as the **digest**.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1b31dfcd5b7f9267bf2ff47651df1cfb9147b9e4df1f335accf65b4cda498408"]
	}
};
var artifact_default = {
	namespace: namespace$70,
	description: description$70,
	attributes: attributes$70
};

//#endregion
//#region src/internal/agents/tools/data/aspnetcore.json
var namespace$69 = "aspnetcore";
var description$69 = "ASP.NET Core attributes";
var attributes$69 = {
	"aspnetcore.rate_limiting.policy": {
		"description": "Rate limiting policy name.",
		"type": "string",
		"stability": "stable",
		"examples": [
			"fixed",
			"sliding",
			"token"
		]
	},
	"aspnetcore.rate_limiting.result": {
		"description": "Rate-limiting result, shows whether the lease was acquired or contains a rejection reason",
		"type": "string",
		"stability": "stable",
		"examples": [
			"acquired",
			"endpoint_limiter",
			"global_limiter",
			"request_canceled"
		]
	},
	"aspnetcore.routing.is_fallback": {
		"description": "A value that indicates whether the matched route is a fallback route.",
		"type": "boolean",
		"stability": "stable",
		"examples": ["true"]
	},
	"aspnetcore.diagnostics.handler.type": {
		"description": "Full type name of the [`IExceptionHandler`](https://learn.microsoft.com/dotnet/api/microsoft.aspnetcore.diagnostics.iexceptionhandler) implementation that handled the exception.",
		"type": "string",
		"stability": "stable",
		"examples": ["Contoso.MyHandler"]
	},
	"aspnetcore.request.is_unhandled": {
		"description": "Flag indicating if request was handled by the application pipeline.",
		"type": "boolean",
		"stability": "stable",
		"examples": ["true"]
	},
	"aspnetcore.routing.match_status": {
		"description": "Match result - success or failure",
		"type": "string",
		"stability": "stable",
		"examples": ["success", "failure"]
	},
	"aspnetcore.diagnostics.exception.result": {
		"description": "ASP.NET Core exception middleware handling result",
		"type": "string",
		"stability": "stable",
		"examples": [
			"handled",
			"unhandled",
			"skipped",
			"aborted"
		]
	}
};
var aspnetcore_default = {
	namespace: namespace$69,
	description: description$69,
	attributes: attributes$69
};

//#endregion
//#region src/internal/agents/tools/data/aws.json
var namespace$68 = "aws";
var description$68 = "This section defines generic attributes for AWS services.\n";
var attributes$68 = {
	"aws.request_id": {
		"description": "The AWS request ID as returned in the response headers `x-amzn-requestid`, `x-amzn-request-id` or `x-amz-request-id`.",
		"type": "string",
		"stability": "development",
		"examples": ["79b9da39-b7ae-508a-a6bc-864b2829c622", "C9ER4AJX75574TDJ"]
	},
	"aws.extended_request_id": {
		"description": "The AWS extended request ID as returned in the response header `x-amz-id-2`.",
		"type": "string",
		"stability": "development",
		"examples": ["wzHcyEWfmOGDIE5QOhTAqFDoDWP3y8IUvpNINCwL9N4TEHbUw0/gZJ+VZTmCNCWR7fezEN3eCiQ="]
	}
};
var aws_default = {
	namespace: namespace$68,
	description: description$68,
	attributes: attributes$68
};

//#endregion
//#region src/internal/agents/tools/data/azure.json
var namespace$67 = "azure";
var description$67 = "This section defines generic attributes used by Azure Client Libraries.\n";
var attributes$67 = {
	"azure.service.request.id": {
		"description": "The unique identifier of the service request. It's generated by the Azure service and returned with the response.",
		"type": "string",
		"stability": "development",
		"examples": ["00000000-0000-0000-0000-000000000000"]
	},
	"azure.resource_provider.namespace": {
		"description": "[Azure Resource Provider Namespace](https://learn.microsoft.com/azure/azure-resource-manager/management/azure-services-resource-providers) as recognized by the client.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"Microsoft.Storage",
			"Microsoft.KeyVault",
			"Microsoft.ServiceBus"
		]
	},
	"azure.client.id": {
		"description": "The unique identifier of the client instance.",
		"type": "string",
		"stability": "development",
		"examples": ["3ba4827d-4422-483f-b59f-85b74211c11d", "storage-client-1"]
	}
};
var azure_default = {
	namespace: namespace$67,
	description: description$67,
	attributes: attributes$67
};

//#endregion
//#region src/internal/agents/tools/data/browser.json
var namespace$66 = "browser";
var description$66 = "The web browser attributes\n";
var attributes$66 = {
	"browser.brands": {
		"description": "Array of brand name and version separated by a space",
		"type": "string",
		"note": "This value is intended to be taken from the [UA client hints API](https://wicg.github.io/ua-client-hints/#interface) (`navigator.userAgentData.brands`).\n",
		"stability": "development",
		"examples": ["[\" Not A;Brand 99\",\"Chromium 99\",\"Chrome 99\"]"]
	},
	"browser.platform": {
		"description": "The platform on which the browser is running",
		"type": "string",
		"note": "This value is intended to be taken from the [UA client hints API](https://wicg.github.io/ua-client-hints/#interface) (`navigator.userAgentData.platform`). If unavailable, the legacy `navigator.platform` API SHOULD NOT be used instead and this attribute SHOULD be left unset in order for the values to be consistent.\nThe list of possible values is defined in the [W3C User-Agent Client Hints specification](https://wicg.github.io/ua-client-hints/#sec-ch-ua-platform). Note that some (but not all) of these values can overlap with values in the [`os.type` and `os.name` attributes](./os.md). However, for consistency, the values in the `browser.platform` attribute should capture the exact value that the user agent provides.\n",
		"stability": "development",
		"examples": [
			"Windows",
			"macOS",
			"Android"
		]
	},
	"browser.mobile": {
		"description": "A boolean that is true if the browser is running on a mobile device",
		"type": "boolean",
		"note": "This value is intended to be taken from the [UA client hints API](https://wicg.github.io/ua-client-hints/#interface) (`navigator.userAgentData.mobile`). If unavailable, this attribute SHOULD be left unset.\n",
		"stability": "development"
	},
	"browser.language": {
		"description": "Preferred language of the user using the browser",
		"type": "string",
		"note": "This value is intended to be taken from the Navigator API `navigator.language`.\n",
		"stability": "development",
		"examples": [
			"en",
			"en-US",
			"fr",
			"fr-FR"
		]
	}
};
var browser_default = {
	namespace: namespace$66,
	description: description$66,
	attributes: attributes$66
};

//#endregion
//#region src/internal/agents/tools/data/cassandra.json
var namespace$65 = "cassandra";
var description$65 = "This section defines attributes for Cassandra.\n";
var attributes$65 = {
	"cassandra.coordinator.dc": {
		"description": "The data center of the coordinating node for a query.\n",
		"type": "string",
		"stability": "development",
		"examples": ["us-west-2"]
	},
	"cassandra.coordinator.id": {
		"description": "The ID of the coordinating node for a query.\n",
		"type": "string",
		"stability": "development",
		"examples": ["be13faa2-8574-4d71-926d-27f16cf8a7af"]
	},
	"cassandra.consistency.level": {
		"description": "The consistency level of the query. Based on consistency values from [CQL](https://docs.datastax.com/en/cassandra-oss/3.0/cassandra/dml/dmlConfigConsistency.html).\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"all",
			"each_quorum",
			"quorum",
			"local_quorum",
			"one",
			"two",
			"three",
			"local_one",
			"any",
			"serial",
			"local_serial"
		]
	},
	"cassandra.query.idempotent": {
		"description": "Whether or not the query is idempotent.\n",
		"type": "boolean",
		"stability": "development"
	},
	"cassandra.page.size": {
		"description": "The fetch size used for paging, i.e. how many rows will be returned at once.\n",
		"type": "number",
		"stability": "development",
		"examples": ["5000"]
	},
	"cassandra.speculative_execution.count": {
		"description": "The number of times a query was speculatively executed. Not set or `0` if the query was not executed speculatively.\n",
		"type": "number",
		"stability": "development",
		"examples": ["0", "2"]
	}
};
var cassandra_default = {
	namespace: namespace$65,
	description: description$65,
	attributes: attributes$65
};

//#endregion
//#region src/internal/agents/tools/data/cicd.json
var namespace$64 = "cicd";
var description$64 = "This group describes attributes specific to pipelines within a Continuous Integration and Continuous Deployment (CI/CD) system. A [pipeline](https://wikipedia.org/wiki/Pipeline_(computing)) in this case is a series of steps that are performed in order to deliver a new version of software. This aligns with the [Britannica](https://www.britannica.com/dictionary/pipeline) definition of a pipeline where a **pipeline** is the system for developing and producing something. In the context of CI/CD, a pipeline produces or delivers software.\n";
var attributes$64 = {
	"cicd.pipeline.name": {
		"description": "The human readable name of the pipeline within a CI/CD system.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"Build and Test",
			"Lint",
			"Deploy Go Project",
			"deploy_to_environment"
		]
	},
	"cicd.pipeline.run.id": {
		"description": "The unique identifier of a pipeline run within a CI/CD system.\n",
		"type": "string",
		"stability": "development",
		"examples": ["120912"]
	},
	"cicd.pipeline.run.url.full": {
		"description": "The [URL](https://wikipedia.org/wiki/URL) of the pipeline run, providing the complete address in order to locate and identify the pipeline run.\n",
		"type": "string",
		"stability": "development",
		"examples": ["https://github.com/open-telemetry/semantic-conventions/actions/runs/9753949763?pr=1075"]
	},
	"cicd.pipeline.run.state": {
		"description": "The pipeline run goes through these states during its lifecycle.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"pending",
			"executing",
			"finalizing"
		]
	},
	"cicd.pipeline.task.name": {
		"description": "The human readable name of a task within a pipeline. Task here most closely aligns with a [computing process](https://wikipedia.org/wiki/Pipeline_(computing)) in a pipeline. Other terms for tasks include commands, steps, and procedures.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"Run GoLang Linter",
			"Go Build",
			"go-test",
			"deploy_binary"
		]
	},
	"cicd.pipeline.task.run.id": {
		"description": "The unique identifier of a task run within a pipeline.\n",
		"type": "string",
		"stability": "development",
		"examples": ["12097"]
	},
	"cicd.pipeline.task.run.url.full": {
		"description": "The [URL](https://wikipedia.org/wiki/URL) of the pipeline task run, providing the complete address in order to locate and identify the pipeline task run.\n",
		"type": "string",
		"stability": "development",
		"examples": ["https://github.com/open-telemetry/semantic-conventions/actions/runs/9753949763/job/26920038674?pr=1075"]
	},
	"cicd.pipeline.task.run.result": {
		"description": "The result of a task run.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"success",
			"failure",
			"error",
			"timeout",
			"cancellation",
			"skip"
		]
	},
	"cicd.pipeline.task.type": {
		"description": "The type of the task within a pipeline.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"build",
			"test",
			"deploy"
		]
	},
	"cicd.pipeline.result": {
		"description": "The result of a pipeline run.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"success",
			"failure",
			"error",
			"timeout",
			"cancellation",
			"skip"
		]
	},
	"cicd.pipeline.action.name": {
		"description": "The kind of action a pipeline run is performing.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"BUILD",
			"RUN",
			"SYNC"
		]
	},
	"cicd.worker.id": {
		"description": "The unique identifier of a worker within a CICD system.",
		"type": "string",
		"stability": "development",
		"examples": [
			"abc123",
			"10.0.1.2",
			"controller"
		]
	},
	"cicd.worker.name": {
		"description": "The name of a worker within a CICD system.",
		"type": "string",
		"stability": "development",
		"examples": [
			"agent-abc",
			"controller",
			"Ubuntu LTS"
		]
	},
	"cicd.worker.url.full": {
		"description": "The [URL](https://wikipedia.org/wiki/URL) of the worker, providing the complete address in order to locate and identify the worker.",
		"type": "string",
		"stability": "development",
		"examples": ["https://cicd.example.org/worker/abc123"]
	},
	"cicd.worker.state": {
		"description": "The state of a CICD worker / agent.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"available",
			"busy",
			"offline"
		]
	},
	"cicd.system.component": {
		"description": "The name of a component of the CICD system.",
		"type": "string",
		"stability": "development",
		"examples": [
			"controller",
			"scheduler",
			"agent"
		]
	}
};
var cicd_default = {
	namespace: namespace$64,
	description: description$64,
	attributes: attributes$64
};

//#endregion
//#region src/internal/agents/tools/data/client.json
var namespace$63 = "client";
var description$63 = "These attributes may be used to describe the client in a connection-based network interaction where there is one side that initiates the connection (the client is the side that initiates the connection). This covers all TCP network interactions since TCP is connection-based and one side initiates the connection (an exception is made for peer-to-peer communication over TCP where the \"user-facing\" surface of the protocol / API doesn't expose a clear notion of client and server). This also covers UDP network interactions where one side initiates the interaction, e.g. QUIC (HTTP/3) and DNS.\n";
var attributes$63 = {
	"client.address": {
		"description": "Client address - domain name if available without reverse DNS lookup; otherwise, IP address or Unix domain socket name.",
		"type": "string",
		"note": "When observed from the server side, and when communicating through an intermediary, `client.address` SHOULD represent the client address behind any intermediaries,  for example proxies, if it's available.\n",
		"stability": "stable",
		"examples": [
			"client.example.com",
			"10.1.2.80",
			"/tmp/my.sock"
		]
	},
	"client.port": {
		"description": "Client port number.",
		"type": "number",
		"note": "When observed from the server side, and when communicating through an intermediary, `client.port` SHOULD represent the client port behind any intermediaries,  for example proxies, if it's available.\n",
		"stability": "stable",
		"examples": ["65123"]
	}
};
var client_default = {
	namespace: namespace$63,
	description: description$63,
	attributes: attributes$63
};

//#endregion
//#region src/internal/agents/tools/data/cloud.json
var namespace$62 = "cloud";
var description$62 = "A cloud environment (e.g. GCP, Azure, AWS).\n";
var attributes$62 = {
	"cloud.provider": {
		"description": "Name of the cloud provider.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"alibaba_cloud",
			"aws",
			"azure",
			"gcp",
			"heroku",
			"ibm_cloud",
			"oracle_cloud",
			"tencent_cloud"
		]
	},
	"cloud.account.id": {
		"description": "The cloud account ID the resource is assigned to.\n",
		"type": "string",
		"stability": "development",
		"examples": ["111111111111", "opentelemetry"]
	},
	"cloud.region": {
		"description": "The geographical region within a cloud provider. When associated with a resource, this attribute specifies the region where the resource operates. When calling services or APIs deployed on a cloud, this attribute identifies the region where the called destination is deployed.\n",
		"type": "string",
		"note": "Refer to your provider's docs to see the available regions, for example [Alibaba Cloud regions](https://www.alibabacloud.com/help/doc-detail/40654.htm), [AWS regions](https://aws.amazon.com/about-aws/global-infrastructure/regions_az/), [Azure regions](https://azure.microsoft.com/global-infrastructure/geographies/), [Google Cloud regions](https://cloud.google.com/about/locations), or [Tencent Cloud regions](https://www.tencentcloud.com/document/product/213/6091).\n",
		"stability": "development",
		"examples": ["us-central1", "us-east-1"]
	},
	"cloud.resource_id": {
		"description": "Cloud provider-specific native identifier of the monitored cloud resource (e.g. an [ARN](https://docs.aws.amazon.com/general/latest/gr/aws-arns-and-namespaces.html) on AWS, a [fully qualified resource ID](https://learn.microsoft.com/rest/api/resources/resources/get-by-id) on Azure, a [full resource name](https://google.aip.dev/122#full-resource-names) on GCP)\n",
		"type": "string",
		"note": "On some cloud providers, it may not be possible to determine the full ID at startup,\nso it may be necessary to set `cloud.resource_id` as a span attribute instead.\n\nThe exact value to use for `cloud.resource_id` depends on the cloud provider.\nThe following well-known definitions MUST be used if you set this attribute and they apply:\n\n- **AWS Lambda:** The function [ARN](https://docs.aws.amazon.com/general/latest/gr/aws-arns-and-namespaces.html).\n  Take care not to use the \"invoked ARN\" directly but replace any\n  [alias suffix](https://docs.aws.amazon.com/lambda/latest/dg/configuration-aliases.html)\n  with the resolved function version, as the same runtime instance may be invocable with\n  multiple different aliases.\n- **GCP:** The [URI of the resource](https://cloud.google.com/iam/docs/full-resource-names)\n- **Azure:** The [Fully Qualified Resource ID](https://learn.microsoft.com/rest/api/resources/resources/get-by-id) of the invoked function,\n  *not* the function app, having the form\n  `/subscriptions/<SUBSCRIPTION_GUID>/resourceGroups/<RG>/providers/Microsoft.Web/sites/<FUNCAPP>/functions/<FUNC>`.\n  This means that a span attribute MUST be used, as an Azure function app can host multiple functions that would usually share\n  a TracerProvider.\n",
		"stability": "development",
		"examples": [
			"arn:aws:lambda:REGION:ACCOUNT_ID:function:my-function",
			"//run.googleapis.com/projects/PROJECT_ID/locations/LOCATION_ID/services/SERVICE_ID",
			"/subscriptions/<SUBSCRIPTION_GUID>/resourceGroups/<RG>/providers/Microsoft.Web/sites/<FUNCAPP>/functions/<FUNC>"
		]
	},
	"cloud.availability_zone": {
		"description": "Cloud regions often have multiple, isolated locations known as zones to increase availability. Availability zone represents the zone where the resource is running.\n",
		"type": "string",
		"note": "Availability zones are called \"zones\" on Alibaba Cloud and Google Cloud.\n",
		"stability": "development",
		"examples": ["us-east-1c"]
	},
	"cloud.platform": {
		"description": "The cloud platform in use.\n",
		"type": "string",
		"note": "The prefix of the service SHOULD match the one specified in `cloud.provider`.\n",
		"stability": "development",
		"examples": [
			"alibaba_cloud_ecs",
			"alibaba_cloud_fc",
			"alibaba_cloud_openshift",
			"aws_ec2",
			"aws_ecs",
			"aws_eks",
			"aws_lambda",
			"aws_elastic_beanstalk",
			"aws_app_runner",
			"aws_openshift",
			"azure.vm",
			"azure.container_apps",
			"azure.container_instances",
			"azure.aks",
			"azure.functions",
			"azure.app_service",
			"azure.openshift",
			"azure_vm",
			"azure_container_apps",
			"azure_container_instances",
			"azure_aks",
			"azure_functions",
			"azure_app_service",
			"azure_openshift",
			"gcp_bare_metal_solution",
			"gcp_compute_engine",
			"gcp_cloud_run",
			"gcp_kubernetes_engine",
			"gcp_cloud_functions",
			"gcp_app_engine",
			"gcp_openshift",
			"ibm_cloud_openshift",
			"oracle_cloud_compute",
			"oracle_cloud_oke",
			"tencent_cloud_cvm",
			"tencent_cloud_eks",
			"tencent_cloud_scf"
		]
	}
};
var cloud_default = {
	namespace: namespace$62,
	description: description$62,
	attributes: attributes$62
};

//#endregion
//#region src/internal/agents/tools/data/cloudevents.json
var namespace$61 = "cloudevents";
var description$61 = "This document defines attributes for CloudEvents.\n";
var attributes$61 = {
	"cloudevents.event_id": {
		"description": "The [event_id](https://github.com/cloudevents/spec/blob/v1.0.2/cloudevents/spec.md#id) uniquely identifies the event.\n",
		"type": "string",
		"stability": "development",
		"examples": ["123e4567-e89b-12d3-a456-426614174000", "0001"]
	},
	"cloudevents.event_source": {
		"description": "The [source](https://github.com/cloudevents/spec/blob/v1.0.2/cloudevents/spec.md#source-1) identifies the context in which an event happened.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"https://github.com/cloudevents",
			"/cloudevents/spec/pull/123",
			"my-service"
		]
	},
	"cloudevents.event_spec_version": {
		"description": "The [version of the CloudEvents specification](https://github.com/cloudevents/spec/blob/v1.0.2/cloudevents/spec.md#specversion) which the event uses.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1.0"]
	},
	"cloudevents.event_type": {
		"description": "The [event_type](https://github.com/cloudevents/spec/blob/v1.0.2/cloudevents/spec.md#type) contains a value describing the type of event related to the originating occurrence.\n",
		"type": "string",
		"stability": "development",
		"examples": ["com.github.pull_request.opened", "com.example.object.deleted.v2"]
	},
	"cloudevents.event_subject": {
		"description": "The [subject](https://github.com/cloudevents/spec/blob/v1.0.2/cloudevents/spec.md#subject) of the event in the context of the event producer (identified by source).\n",
		"type": "string",
		"stability": "development",
		"examples": ["mynewfile.jpg"]
	}
};
var cloudevents_default = {
	namespace: namespace$61,
	description: description$61,
	attributes: attributes$61
};

//#endregion
//#region src/internal/agents/tools/data/cloudfoundry.json
var namespace$60 = "cloudfoundry";
var description$60 = "CloudFoundry resource attributes.\n";
var attributes$60 = {
	"cloudfoundry.system.id": {
		"description": "A guid or another name describing the event source.\n",
		"type": "string",
		"note": "CloudFoundry defines the `source_id` in the [Loggregator v2 envelope](https://github.com/cloudfoundry/loggregator-api#v2-envelope).\nIt is used for logs and metrics emitted by CloudFoundry. It is\nsupposed to contain the component name, e.g. \"gorouter\", for\nCloudFoundry components.\n\nWhen system components are instrumented, values from the\n[Bosh spec](https://bosh.io/docs/jobs/#properties-spec)\nshould be used. The `system.id` should be set to\n`spec.deployment/spec.name`.\n",
		"stability": "development",
		"examples": ["cf/gorouter"]
	},
	"cloudfoundry.system.instance.id": {
		"description": "A guid describing the concrete instance of the event source.\n",
		"type": "string",
		"note": "CloudFoundry defines the `instance_id` in the [Loggregator v2 envelope](https://github.com/cloudfoundry/loggregator-api#v2-envelope).\nIt is used for logs and metrics emitted by CloudFoundry. It is\nsupposed to contain the vm id for CloudFoundry components.\n\nWhen system components are instrumented, values from the\n[Bosh spec](https://bosh.io/docs/jobs/#properties-spec)\nshould be used. The `system.instance.id` should be set to `spec.id`.\n",
		"stability": "development",
		"examples": ["218fc5a9-a5f1-4b54-aa05-46717d0ab26d"]
	},
	"cloudfoundry.app.name": {
		"description": "The name of the application.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.application_name`. This is the same value\nas reported by `cf apps`.\n",
		"stability": "development",
		"examples": ["my-app-name"]
	},
	"cloudfoundry.app.id": {
		"description": "The guid of the application.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.application_id`. This is the same value as\nreported by `cf app <app-name> --guid`.\n",
		"stability": "development",
		"examples": ["218fc5a9-a5f1-4b54-aa05-46717d0ab26d"]
	},
	"cloudfoundry.app.instance.id": {
		"description": "The index of the application instance. 0 when just one instance is active.\n",
		"type": "string",
		"note": "CloudFoundry defines the `instance_id` in the [Loggregator v2 envelope](https://github.com/cloudfoundry/loggregator-api#v2-envelope).\nIt is used for logs and metrics emitted by CloudFoundry. It is\nsupposed to contain the application instance index for applications\ndeployed on the runtime.\n\nApplication instrumentation should use the value from environment\nvariable `CF_INSTANCE_INDEX`.\n",
		"stability": "development",
		"examples": ["0", "1"]
	},
	"cloudfoundry.space.name": {
		"description": "The name of the CloudFoundry space the application is running in.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.space_name`. This is the same value as\nreported by `cf spaces`.\n",
		"stability": "development",
		"examples": ["my-space-name"]
	},
	"cloudfoundry.space.id": {
		"description": "The guid of the CloudFoundry space the application is running in.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.space_id`. This is the same value as\nreported by `cf space <space-name> --guid`.\n",
		"stability": "development",
		"examples": ["218fc5a9-a5f1-4b54-aa05-46717d0ab26d"]
	},
	"cloudfoundry.org.name": {
		"description": "The name of the CloudFoundry organization the app is running in.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.org_name`. This is the same value as\nreported by `cf orgs`.\n",
		"stability": "development",
		"examples": ["my-org-name"]
	},
	"cloudfoundry.org.id": {
		"description": "The guid of the CloudFoundry org the application is running in.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.org_id`. This is the same value as\nreported by `cf org <org-name> --guid`.\n",
		"stability": "development",
		"examples": ["218fc5a9-a5f1-4b54-aa05-46717d0ab26d"]
	},
	"cloudfoundry.process.id": {
		"description": "The UID identifying the process.\n",
		"type": "string",
		"note": "Application instrumentation should use the value from environment\nvariable `VCAP_APPLICATION.process_id`. It is supposed to be equal to\n`VCAP_APPLICATION.app_id` for applications deployed to the runtime.\nFor system components, this could be the actual PID.\n",
		"stability": "development",
		"examples": ["218fc5a9-a5f1-4b54-aa05-46717d0ab26d"]
	},
	"cloudfoundry.process.type": {
		"description": "The type of process.\n",
		"type": "string",
		"note": "CloudFoundry applications can consist of multiple jobs. Usually the\nmain process will be of type `web`. There can be additional background\ntasks or side-cars with different process types.\n",
		"stability": "development",
		"examples": ["web"]
	}
};
var cloudfoundry_default = {
	namespace: namespace$60,
	description: description$60,
	attributes: attributes$60
};

//#endregion
//#region src/internal/agents/tools/data/code.json
var namespace$59 = "code";
var description$59 = "These attributes provide context about source code\n";
var attributes$59 = {
	"code.function.name": {
		"description": "The method or function fully-qualified name without arguments. The value should fit the natural representation of the language runtime, which is also likely the same used within `code.stacktrace` attribute value. This attribute MUST NOT be used on the Profile signal since the data is already captured in 'message Function'. This constraint is imposed to prevent redundancy and maintain data integrity.\n",
		"type": "string",
		"note": "Values and format depends on each language runtime, thus it is impossible to provide an exhaustive list of examples.\nThe values are usually the same (or prefixes of) the ones found in native stack trace representation stored in\n`code.stacktrace` without information on arguments.\n\nExamples:\n\n* Java method: `com.example.MyHttpService.serveRequest`\n* Java anonymous class method: `com.mycompany.Main$1.myMethod`\n* Java lambda method: `com.mycompany.Main$$Lambda/0x0000748ae4149c00.myMethod`\n* PHP function: `GuzzleHttp\\Client::transfer`\n* Go function: `github.com/my/repo/pkg.foo.func5`\n* Elixir: `OpenTelemetry.Ctx.new`\n* Erlang: `opentelemetry_ctx:new`\n* Rust: `playground::my_module::my_cool_func`\n* C function: `fopen`\n",
		"stability": "stable",
		"examples": [
			"com.example.MyHttpService.serveRequest",
			"GuzzleHttp\\Client::transfer",
			"fopen"
		]
	},
	"code.file.path": {
		"description": "The source code file name that identifies the code unit as uniquely as possible (preferably an absolute file path). This attribute MUST NOT be used on the Profile signal since the data is already captured in 'message Function'. This constraint is imposed to prevent redundancy and maintain data integrity.\n",
		"type": "string",
		"stability": "stable",
		"examples": ["/usr/local/MyApplication/content_root/app/index.php"]
	},
	"code.line.number": {
		"description": "The line number in `code.file.path` best representing the operation. It SHOULD point within the code unit named in `code.function.name`. This attribute MUST NOT be used on the Profile signal since the data is already captured in 'message Line'. This constraint is imposed to prevent redundancy and maintain data integrity.\n",
		"type": "number",
		"stability": "stable",
		"examples": ["42"]
	},
	"code.column.number": {
		"description": "The column number in `code.file.path` best representing the operation. It SHOULD point within the code unit named in `code.function.name`. This attribute MUST NOT be used on the Profile signal since the data is already captured in 'message Line'. This constraint is imposed to prevent redundancy and maintain data integrity.\n",
		"type": "number",
		"stability": "stable",
		"examples": ["16"]
	},
	"code.stacktrace": {
		"description": "A stacktrace as a string in the natural representation for the language runtime. The representation is identical to [`exception.stacktrace`](/docs/exceptions/exceptions-spans.md#stacktrace-representation). This attribute MUST NOT be used on the Profile signal since the data is already captured in 'message Location'. This constraint is imposed to prevent redundancy and maintain data integrity.\n",
		"type": "string",
		"stability": "stable",
		"examples": ["at com.example.GenerateTrace.methodB(GenerateTrace.java:13)\\n at com.example.GenerateTrace.methodA(GenerateTrace.java:9)\\n at com.example.GenerateTrace.main(GenerateTrace.java:5)\n"]
	}
};
var code_default = {
	namespace: namespace$59,
	description: description$59,
	attributes: attributes$59
};

//#endregion
//#region src/internal/agents/tools/data/container.json
var namespace$58 = "container";
var description$58 = "A container instance.\n";
var attributes$58 = {
	"container.name": {
		"description": "Container name used by container runtime.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry-autoconf"]
	},
	"container.id": {
		"description": "Container ID. Usually a UUID, as for example used to [identify Docker containers](https://docs.docker.com/engine/containers/run/#container-identification). The UUID might be abbreviated.\n",
		"type": "string",
		"stability": "development",
		"examples": ["a3bf90e006b2"]
	},
	"container.runtime": {
		"description": "The container runtime managing this container.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"docker",
			"containerd",
			"rkt"
		]
	},
	"container.image.name": {
		"description": "Name of the image the container was built on.\n",
		"type": "string",
		"stability": "development",
		"examples": ["gcr.io/opentelemetry/operator"]
	},
	"container.image.tags": {
		"description": "Container image tags. An example can be found in [Docker Image Inspect](https://docs.docker.com/engine/api/v1.43/#tag/Image/operation/ImageInspect). Should be only the `<tag>` section of the full name for example from `registry.example.com/my-org/my-image:<tag>`.\n",
		"type": "string",
		"stability": "development",
		"examples": ["[\"v1.27.1\",\"3.5.7-0\"]"]
	},
	"container.image.id": {
		"description": "Runtime specific image identifier. Usually a hash algorithm followed by a UUID.\n",
		"type": "string",
		"note": "Docker defines a sha256 of the image id; `container.image.id` corresponds to the `Image` field from the Docker container inspect [API](https://docs.docker.com/engine/api/v1.43/#tag/Container/operation/ContainerInspect) endpoint.\nK8s defines a link to the container registry repository with digest `\"imageID\": \"registry.azurecr.io /namespace/service/dockerfile@sha256:bdeabd40c3a8a492eaf9e8e44d0ebbb84bac7ee25ac0cf8a7159d25f62555625\"`.\nThe ID is assigned by the container runtime and can vary in different environments. Consider using `oci.manifest.digest` if it is important to identify the same image in different environments/runtimes.\n",
		"stability": "development",
		"examples": ["sha256:19c92d0a00d1b66d897bceaa7319bee0dd38a10a851c60bcec9474aa3f01e50f"]
	},
	"container.image.repo_digests": {
		"description": "Repo digests of the container image as provided by the container runtime.\n",
		"type": "string",
		"note": "[Docker](https://docs.docker.com/engine/api/v1.43/#tag/Image/operation/ImageInspect) and [CRI](https://github.com/kubernetes/cri-api/blob/c75ef5b473bbe2d0a4fc92f82235efd665ea8e9f/pkg/apis/runtime/v1/api.proto#L1237-L1238) report those under the `RepoDigests` field.\n",
		"stability": "development",
		"examples": ["[\"example@sha256:afcc7f1ac1b49db317a7196c902e61c6c3c4607d63599ee1a82d702d249a0ccb\",\"internal.registry.example.com:5000/example@sha256:b69959407d21e8a062e0416bf13405bb2b71ed7a84dde4158ebafacfa06f5578\"]"]
	},
	"container.command": {
		"description": "The command used to run the container (i.e. the command name).\n",
		"type": "string",
		"note": "If using embedded credentials or sensitive data, it is recommended to remove them to prevent potential leakage.\n",
		"stability": "development",
		"examples": ["otelcontribcol"]
	},
	"container.command_line": {
		"description": "The full command run by the container as a single string representing the full command.\n",
		"type": "string",
		"stability": "development",
		"examples": ["otelcontribcol --config config.yaml"]
	},
	"container.command_args": {
		"description": "All the command arguments (including the command/executable itself) run by the container.\n",
		"type": "string",
		"stability": "development",
		"examples": ["[\"otelcontribcol\",\"--config\",\"config.yaml\"]"]
	},
	"container.label": {
		"description": "Container labels, `<key>` being the label name, the value being the label value.\n",
		"type": "string",
		"note": "For example, a docker container label `app` with value `nginx` SHOULD be recorded as the `container.label.app` attribute with value `\"nginx\"`.\n",
		"stability": "development",
		"examples": ["nginx"]
	},
	"container.csi.plugin.name": {
		"description": "The name of the CSI ([Container Storage Interface](https://github.com/container-storage-interface/spec)) plugin used by the volume.\n",
		"type": "string",
		"note": "This can sometimes be referred to as a \"driver\" in CSI implementations. This should represent the `name` field of the GetPluginInfo RPC.\n",
		"stability": "development",
		"examples": ["pd.csi.storage.gke.io"]
	},
	"container.csi.volume.id": {
		"description": "The unique volume ID returned by the CSI ([Container Storage Interface](https://github.com/container-storage-interface/spec)) plugin.\n",
		"type": "string",
		"note": "This can sometimes be referred to as a \"volume handle\" in CSI implementations. This should represent the `Volume.volume_id` field in CSI spec.\n",
		"stability": "development",
		"examples": ["projects/my-gcp-project/zones/my-gcp-zone/disks/my-gcp-disk"]
	}
};
var container_default = {
	namespace: namespace$58,
	description: description$58,
	attributes: attributes$58
};

//#endregion
//#region src/internal/agents/tools/data/cpu.json
var namespace$57 = "cpu";
var description$57 = "Attributes specific to a cpu instance.";
var attributes$57 = {
	"cpu.mode": {
		"description": "The mode of the CPU",
		"type": "string",
		"stability": "development",
		"examples": [
			"user",
			"system",
			"nice",
			"idle",
			"iowait",
			"interrupt",
			"steal",
			"kernel"
		]
	},
	"cpu.logical_number": {
		"description": "The logical CPU number [0..n-1]",
		"type": "number",
		"stability": "development",
		"examples": ["1"]
	}
};
var cpu_default = {
	namespace: namespace$57,
	description: description$57,
	attributes: attributes$57
};

//#endregion
//#region src/internal/agents/tools/data/cpython.json
var namespace$56 = "cpython";
var description$56 = "This document defines CPython related attributes.\n";
var attributes$56 = { "cpython.gc.generation": {
	"description": "Value of the garbage collector collection generation.",
	"type": "string",
	"stability": "development",
	"examples": [
		"0",
		"1",
		"2"
	]
} };
var cpython_default = {
	namespace: namespace$56,
	description: description$56,
	attributes: attributes$56
};

//#endregion
//#region src/internal/agents/tools/data/database.json
var namespace$55 = "database";
var description$55 = "This group defines the attributes used to describe telemetry in the context of databases.\n";
var attributes$55 = {
	"db.collection.name": {
		"description": "The name of a collection (table, container) within the database.",
		"type": "string",
		"note": "It is RECOMMENDED to capture the value as provided by the application\nwithout attempting to do any case normalization.\n\nThe collection name SHOULD NOT be extracted from `db.query.text`,\nwhen the database system supports query text with multiple collections\nin non-batch operations.\n\nFor batch operations, if the individual operations are known to have the same\ncollection name then that collection name SHOULD be used.\n",
		"stability": "stable",
		"examples": ["public.users", "customers"]
	},
	"db.namespace": {
		"description": "The name of the database, fully qualified within the server address and port.\n",
		"type": "string",
		"note": "If a database system has multiple namespace components, they SHOULD be concatenated from the most general to the most specific namespace component, using `|` as a separator between the components. Any missing components (and their associated separators) SHOULD be omitted.\nSemantic conventions for individual database systems SHOULD document what `db.namespace` means in the context of that system.\nIt is RECOMMENDED to capture the value as provided by the application without attempting to do any case normalization.\n",
		"stability": "stable",
		"examples": ["customers", "test.users"]
	},
	"db.operation.name": {
		"description": "The name of the operation or command being executed.\n",
		"type": "string",
		"note": "It is RECOMMENDED to capture the value as provided by the application\nwithout attempting to do any case normalization.\n\nThe operation name SHOULD NOT be extracted from `db.query.text`,\nwhen the database system supports query text with multiple operations\nin non-batch operations.\n\nIf spaces can occur in the operation name, multiple consecutive spaces\nSHOULD be normalized to a single space.\n\nFor batch operations, if the individual operations are known to have the same operation name\nthen that operation name SHOULD be used prepended by `BATCH `,\notherwise `db.operation.name` SHOULD be `BATCH` or some other database\nsystem specific term if more applicable.\n",
		"stability": "stable",
		"examples": [
			"findAndModify",
			"HMSET",
			"SELECT"
		]
	},
	"db.query.text": {
		"description": "The database query being executed.\n",
		"type": "string",
		"note": "For sanitization see [Sanitization of `db.query.text`](/docs/database/database-spans.md#sanitization-of-dbquerytext).\nFor batch operations, if the individual operations are known to have the same query text then that query text SHOULD be used, otherwise all of the individual query texts SHOULD be concatenated with separator `; ` or some other database system specific separator if more applicable.\nParameterized query text SHOULD NOT be sanitized. Even though parameterized query text can potentially have sensitive data, by using a parameterized query the user is giving a strong signal that any sensitive data will be passed as parameter values, and the benefit to observability of capturing the static part of the query text by default outweighs the risk.\n",
		"stability": "stable",
		"examples": ["SELECT * FROM wuser_table where username = ?", "SET mykey ?"]
	},
	"db.query.parameter": {
		"description": "A database query parameter, with `<key>` being the parameter name, and the attribute value being a string representation of the parameter value.\n",
		"type": "string",
		"note": "If a query parameter has no name and instead is referenced only by index,\nthen `<key>` SHOULD be the 0-based index.\n\n`db.query.parameter.<key>` SHOULD match\nup with the parameterized placeholders present in `db.query.text`.\n\n`db.query.parameter.<key>` SHOULD NOT be captured on batch operations.\n\nExamples:\n\n- For a query `SELECT * FROM users where username =  %s` with the parameter `\"jdoe\"`,\n  the attribute `db.query.parameter.0` SHOULD be set to `\"jdoe\"`.\n\n- For a query `\"SELECT * FROM users WHERE username = %(username)s;` with parameter\n  `username = \"jdoe\"`, the attribute `db.query.parameter.username` SHOULD be set to `\"jdoe\"`.\n",
		"stability": "development",
		"examples": ["someval", "55"]
	},
	"db.query.summary": {
		"description": "Low cardinality summary of a database query.\n",
		"type": "string",
		"note": "The query summary describes a class of database queries and is useful\nas a grouping key, especially when analyzing telemetry for database\ncalls involving complex queries.\n\nSummary may be available to the instrumentation through\ninstrumentation hooks or other means. If it is not available, instrumentations\nthat support query parsing SHOULD generate a summary following\n[Generating query summary](/docs/database/database-spans.md#generating-a-summary-of-the-query)\nsection.\n",
		"stability": "stable",
		"examples": [
			"SELECT wuser_table",
			"INSERT shipping_details SELECT orders",
			"get user by id"
		]
	},
	"db.stored_procedure.name": {
		"description": "The name of a stored procedure within the database.",
		"type": "string",
		"note": "It is RECOMMENDED to capture the value as provided by the application\nwithout attempting to do any case normalization.\n\nFor batch operations, if the individual operations are known to have the same\nstored procedure name then that stored procedure name SHOULD be used.\n",
		"stability": "stable",
		"examples": ["GetCustomer"]
	},
	"db.operation.parameter": {
		"description": "A database operation parameter, with `<key>` being the parameter name, and the attribute value being a string representation of the parameter value.\n",
		"type": "string",
		"note": "For example, a client-side maximum number of rows to read from the database\nMAY be recorded as the `db.operation.parameter.max_rows` attribute.\n\n`db.query.text` parameters SHOULD be captured using `db.query.parameter.<key>`\ninstead of `db.operation.parameter.<key>`.\n",
		"stability": "development",
		"examples": ["someval", "55"]
	},
	"db.operation.batch.size": {
		"description": "The number of queries included in a batch operation.",
		"type": "number",
		"note": "Operations are only considered batches when they contain two or more operations, and so `db.operation.batch.size` SHOULD never be `1`.\n",
		"stability": "stable",
		"examples": [
			"2",
			"3",
			"4"
		]
	},
	"db.response.status_code": {
		"description": "Database response status code.",
		"type": "string",
		"note": "The status code returned by the database. Usually it represents an error code, but may also represent partial success, warning, or differentiate between various types of successful outcomes.\nSemantic conventions for individual database systems SHOULD document what `db.response.status_code` means in the context of that system.\n",
		"stability": "stable",
		"examples": [
			"102",
			"ORA-17002",
			"08P01",
			"404"
		]
	},
	"db.response.returned_rows": {
		"description": "Number of rows returned by the operation.",
		"type": "number",
		"stability": "development",
		"examples": [
			"10",
			"30",
			"1000"
		]
	},
	"db.system.name": {
		"description": "The database management system (DBMS) product as identified by the client instrumentation.",
		"type": "string",
		"note": "The actual DBMS may differ from the one identified by the client. For example, when using PostgreSQL client libraries to connect to a CockroachDB, the `db.system.name` is set to `postgresql` based on the instrumentation's best knowledge.\n",
		"stability": "stable",
		"examples": [
			"other_sql",
			"softwareag.adabas",
			"actian.ingres",
			"aws.dynamodb",
			"aws.redshift",
			"azure.cosmosdb",
			"intersystems.cache",
			"cassandra",
			"clickhouse",
			"cockroachdb",
			"couchbase",
			"couchdb",
			"derby",
			"elasticsearch",
			"firebirdsql",
			"gcp.spanner",
			"geode",
			"h2database",
			"hbase",
			"hive",
			"hsqldb",
			"ibm.db2",
			"ibm.informix",
			"ibm.netezza",
			"influxdb",
			"instantdb",
			"mariadb",
			"memcached",
			"mongodb",
			"microsoft.sql_server",
			"mysql",
			"neo4j",
			"opensearch",
			"oracle.db",
			"postgresql",
			"redis",
			"sap.hana",
			"sap.maxdb",
			"sqlite",
			"teradata",
			"trino"
		]
	},
	"db.client.connection.state": {
		"description": "The state of a connection in the pool",
		"type": "string",
		"stability": "development",
		"examples": ["idle", "used"]
	},
	"db.client.connection.pool.name": {
		"description": "The name of the connection pool; unique within the instrumented application. In case the connection pool implementation doesn't provide a name, instrumentation SHOULD use a combination of parameters that would make the name unique, for example, combining attributes `server.address`, `server.port`, and `db.namespace`, formatted as `server.address:server.port/db.namespace`. Instrumentations that generate connection pool name following different patterns SHOULD document it.\n",
		"type": "string",
		"stability": "development",
		"examples": ["myDataSource"]
	}
};
var database_default = {
	namespace: namespace$55,
	description: description$55,
	attributes: attributes$55
};

//#endregion
//#region src/internal/agents/tools/data/db.json
var namespace$54 = "db";
var description$54 = "Database operations attributes";
var attributes$54 = {
	"db.system.name": {
		"description": "Identifies the database management system product",
		"type": "string",
		"examples": [
			"postgresql",
			"mysql",
			"microsoft.sql_server",
			"mongodb",
			"redis",
			"cassandra"
		]
	},
	"db.collection.name": {
		"description": "Name of a collection (table, container) within the database",
		"type": "string"
	},
	"db.namespace": {
		"description": "Fully qualified database name within server address and port",
		"type": "string"
	},
	"db.operation.name": {
		"description": "Name of the operation or command being executed",
		"type": "string",
		"examples": [
			"SELECT",
			"INSERT",
			"UPDATE",
			"DELETE",
			"CREATE",
			"DROP",
			"find",
			"insert",
			"update",
			"delete"
		]
	},
	"db.response.status_code": {
		"description": "Status code returned by the database",
		"type": "string"
	},
	"db.operation.batch.size": {
		"description": "Number of queries in a batch operation",
		"type": "number"
	},
	"db.query.summary": {
		"description": "Low-cardinality summary of a database query",
		"type": "string"
	},
	"db.query.text": {
		"description": "The actual database query being executed",
		"type": "string"
	},
	"db.stored_procedure.name": {
		"description": "Name of a stored procedure within the database",
		"type": "string"
	},
	"db.query.parameter.<key>": {
		"description": "Database query parameter values",
		"type": "string"
	}
};
var db_default = {
	namespace: namespace$54,
	description: description$54,
	attributes: attributes$54
};

//#endregion
//#region src/internal/agents/tools/data/deployment.json
var namespace$53 = "deployment";
var description$53 = "This document defines attributes for software deployments.\n";
var attributes$53 = {
	"deployment.name": {
		"description": "The name of the deployment.\n",
		"type": "string",
		"stability": "development",
		"examples": ["deploy my app", "deploy-frontend"]
	},
	"deployment.id": {
		"description": "The id of the deployment.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1208"]
	},
	"deployment.status": {
		"description": "The status of the deployment.\n",
		"type": "string",
		"stability": "development",
		"examples": ["failed", "succeeded"]
	},
	"deployment.environment.name": {
		"description": "Name of the [deployment environment](https://wikipedia.org/wiki/Deployment_environment) (aka deployment tier).\n",
		"type": "string",
		"note": "`deployment.environment.name` does not affect the uniqueness constraints defined through\nthe `service.namespace`, `service.name` and `service.instance.id` resource attributes.\nThis implies that resources carrying the following attribute combinations MUST be\nconsidered to be identifying the same service:\n\n- `service.name=frontend`, `deployment.environment.name=production`\n- `service.name=frontend`, `deployment.environment.name=staging`.\n",
		"stability": "development",
		"examples": ["staging", "production"]
	}
};
var deployment_default = {
	namespace: namespace$53,
	description: description$53,
	attributes: attributes$53
};

//#endregion
//#region src/internal/agents/tools/data/destination.json
var namespace$52 = "destination";
var description$52 = "These attributes may be used to describe the receiver of a network exchange/packet. These should be used when there is no client/server relationship between the two sides, or when that relationship is unknown. This covers low-level network interactions (e.g. packet tracing) where you don't know if there was a connection or which side initiated it. This also covers unidirectional UDP flows and peer-to-peer communication where the \"user-facing\" surface of the protocol / API doesn't expose a clear notion of client and server.\n";
var attributes$52 = {
	"destination.address": {
		"description": "Destination address - domain name if available without reverse DNS lookup; otherwise, IP address or Unix domain socket name.",
		"type": "string",
		"note": "When observed from the source side, and when communicating through an intermediary, `destination.address` SHOULD represent the destination address behind any intermediaries, for example proxies, if it's available.\n",
		"stability": "development",
		"examples": [
			"destination.example.com",
			"10.1.2.80",
			"/tmp/my.sock"
		]
	},
	"destination.port": {
		"description": "Destination port number",
		"type": "number",
		"stability": "development",
		"examples": ["3389", "2888"]
	}
};
var destination_default = {
	namespace: namespace$52,
	description: description$52,
	attributes: attributes$52
};

//#endregion
//#region src/internal/agents/tools/data/device.json
var namespace$51 = "device";
var description$51 = "Describes device attributes.\n";
var attributes$51 = {
	"device.id": {
		"description": "A unique identifier representing the device\n",
		"type": "string",
		"note": "Its value SHOULD be identical for all apps on a device and it SHOULD NOT change if an app is uninstalled and re-installed.\nHowever, it might be resettable by the user for all apps on a device.\nHardware IDs (e.g. vendor-specific serial number, IMEI or MAC address) MAY be used as values.\n\nMore information about Android identifier best practices can be found [here](https://developer.android.com/training/articles/user-data-ids).\n\n> [!WARNING]\n>\n> This attribute may contain sensitive (PII) information. Caution should be taken when storing personal data or anything which can identify a user. GDPR and data protection laws may apply,\n> ensure you do your own due diligence.\n>\n> Due to these reasons, this identifier is not recommended for consumer applications and will likely result in rejection from both Google Play and App Store.\n> However, it may be appropriate for specific enterprise scenarios, such as kiosk devices or enterprise-managed devices, with appropriate compliance clearance.\n> Any instrumentation providing this identifier MUST implement it as an opt-in feature.\n>\n> See [`app.installation.id`](/docs/registry/attributes/app.md#app-installation-id) for a more privacy-preserving alternative.\n",
		"stability": "development",
		"examples": ["123456789012345", "01:23:45:67:89:AB"]
	},
	"device.manufacturer": {
		"description": "The name of the device manufacturer\n",
		"type": "string",
		"note": "The Android OS provides this field via [Build](https://developer.android.com/reference/android/os/Build#MANUFACTURER). iOS apps SHOULD hardcode the value `Apple`.\n",
		"stability": "development",
		"examples": ["Apple", "Samsung"]
	},
	"device.model.identifier": {
		"description": "The model identifier for the device\n",
		"type": "string",
		"note": "It's recommended this value represents a machine-readable version of the model identifier rather than the market or consumer-friendly name of the device.\n",
		"stability": "development",
		"examples": ["iPhone3,4", "SM-G920F"]
	},
	"device.model.name": {
		"description": "The marketing name for the device model\n",
		"type": "string",
		"note": "It's recommended this value represents a human-readable version of the device model rather than a machine-readable alternative.\n",
		"stability": "development",
		"examples": ["iPhone 6s Plus", "Samsung Galaxy S6"]
	}
};
var device_default = {
	namespace: namespace$51,
	description: description$51,
	attributes: attributes$51
};

//#endregion
//#region src/internal/agents/tools/data/disk.json
var namespace$50 = "disk";
var description$50 = "These attributes may be used for any disk related operation.\n";
var attributes$50 = { "disk.io.direction": {
	"description": "The disk IO operation direction.",
	"type": "string",
	"stability": "development",
	"examples": ["read", "write"]
} };
var disk_default = {
	namespace: namespace$50,
	description: description$50,
	attributes: attributes$50
};

//#endregion
//#region src/internal/agents/tools/data/dns.json
var namespace$49 = "dns";
var description$49 = "This document defines the shared attributes used to report a DNS query.\n";
var attributes$49 = {
	"dns.question.name": {
		"description": "The name being queried.",
		"type": "string",
		"note": "If the name field contains non-printable characters (below 32 or above 126), those characters should be represented as escaped base 10 integers (\\DDD). Back slashes and quotes should be escaped. Tabs, carriage returns, and line feeds should be converted to \\t, \\r, and \\n respectively.\n",
		"stability": "development",
		"examples": ["www.example.com", "opentelemetry.io"]
	},
	"dns.answers": {
		"description": "The list of IPv4 or IPv6 addresses resolved during DNS lookup.",
		"type": "string",
		"stability": "development",
		"examples": ["[\"10.0.0.1\",\"2001:0db8:85a3:0000:0000:8a2e:0370:7334\"]"]
	}
};
var dns_default = {
	namespace: namespace$49,
	description: description$49,
	attributes: attributes$49
};

//#endregion
//#region src/internal/agents/tools/data/dotnet.json
var namespace$48 = "dotnet";
var description$48 = "This document defines .NET related attributes.\n";
var attributes$48 = { "dotnet.gc.heap.generation": {
	"description": "Name of the garbage collector managed heap generation.",
	"type": "string",
	"stability": "stable",
	"examples": [
		"gen0",
		"gen1",
		"gen2",
		"loh",
		"poh"
	]
} };
var dotnet_default = {
	namespace: namespace$48,
	description: description$48,
	attributes: attributes$48
};

//#endregion
//#region src/internal/agents/tools/data/elasticsearch.json
var namespace$47 = "elasticsearch";
var description$47 = "This section defines attributes for Elasticsearch.\n";
var attributes$47 = { "elasticsearch.node.name": {
	"description": "Represents the human-readable identifier of the node/instance to which a request was routed.\n",
	"type": "string",
	"stability": "development",
	"examples": ["instance-0000000001"]
} };
var elasticsearch_default = {
	namespace: namespace$47,
	description: description$47,
	attributes: attributes$47
};

//#endregion
//#region src/internal/agents/tools/data/enduser.json
var namespace$46 = "enduser";
var description$46 = "Describes the end user.\n";
var attributes$46 = {
	"enduser.id": {
		"description": "Unique identifier of an end user in the system. It maybe a username, email address, or other identifier.",
		"type": "string",
		"note": "Unique identifier of an end user in the system.\n\n> [!Warning]\n> This field contains sensitive (PII) information.\n",
		"stability": "development",
		"examples": ["username"]
	},
	"enduser.pseudo.id": {
		"description": "Pseudonymous identifier of an end user. This identifier should be a random value that is not directly linked or associated with the end user's actual identity.\n",
		"type": "string",
		"note": "Pseudonymous identifier of an end user.\n\n> [!Warning]\n> This field contains sensitive (linkable PII) information.\n",
		"stability": "development",
		"examples": ["QdH5CAWJgqVT4rOr0qtumf"]
	}
};
var enduser_default = {
	namespace: namespace$46,
	description: description$46,
	attributes: attributes$46
};

//#endregion
//#region src/internal/agents/tools/data/error.json
var namespace$45 = "error";
var description$45 = "This document defines the shared attributes used to report an error.\n";
var attributes$45 = {
	"error.type": {
		"description": "Describes a class of error the operation ended with.\n",
		"type": "string",
		"note": "The `error.type` SHOULD be predictable, and SHOULD have low cardinality.\n\nWhen `error.type` is set to a type (e.g., an exception type), its\ncanonical class name identifying the type within the artifact SHOULD be used.\n\nInstrumentations SHOULD document the list of errors they report.\n\nThe cardinality of `error.type` within one instrumentation library SHOULD be low.\nTelemetry consumers that aggregate data from multiple instrumentation libraries and applications\nshould be prepared for `error.type` to have high cardinality at query time when no\nadditional filters are applied.\n\nIf the operation has completed successfully, instrumentations SHOULD NOT set `error.type`.\n\nIf a specific domain defines its own set of error identifiers (such as HTTP or gRPC status codes),\nit's RECOMMENDED to:\n\n- Use a domain-specific attribute\n- Set `error.type` to capture all errors, regardless of whether they are defined within the domain-specific set or not.\n",
		"stability": "stable",
		"examples": ["_OTHER"]
	},
	"error.message": {
		"description": "A message providing more detail about an error in human-readable form.",
		"type": "string",
		"note": "`error.message` should provide additional context and detail about an error.\nIt is NOT RECOMMENDED to duplicate the value of `error.type` in `error.message`.\nIt is also NOT RECOMMENDED to duplicate the value of `exception.message` in `error.message`.\n\n`error.message` is NOT RECOMMENDED for metrics or spans due to its unbounded cardinality and overlap with span status.\n",
		"stability": "development",
		"examples": ["Unexpected input type: string", "The user has exceeded their storage quota"]
	}
};
var error_default = {
	namespace: namespace$45,
	description: description$45,
	attributes: attributes$45
};

//#endregion
//#region src/internal/agents/tools/data/faas.json
var namespace$44 = "faas";
var description$44 = "FaaS attributes";
var attributes$44 = {
	"faas.name": {
		"description": "The name of the single function that this runtime instance executes.\n",
		"type": "string",
		"note": "This is the name of the function as configured/deployed on the FaaS\nplatform and is usually different from the name of the callback\nfunction (which may be stored in the\n[`code.namespace`/`code.function.name`](/docs/general/attributes.md#source-code-attributes)\nspan attributes).\n\nFor some cloud providers, the above definition is ambiguous. The following\ndefinition of function name MUST be used for this attribute\n(and consequently the span name) for the listed cloud providers/products:\n\n- **Azure:**  The full name `<FUNCAPP>/<FUNC>`, i.e., function app name\n  followed by a forward slash followed by the function name (this form\n  can also be seen in the resource JSON for the function).\n  This means that a span attribute MUST be used, as an Azure function\n  app can host multiple functions that would usually share\n  a TracerProvider (see also the `cloud.resource_id` attribute).\n",
		"stability": "development",
		"examples": ["my-function", "myazurefunctionapp/some-function-name"]
	},
	"faas.version": {
		"description": "The immutable version of the function being executed.",
		"type": "string",
		"note": "Depending on the cloud provider and platform, use:\n\n- **AWS Lambda:** The [function version](https://docs.aws.amazon.com/lambda/latest/dg/configuration-versions.html)\n  (an integer represented as a decimal string).\n- **Google Cloud Run (Services):** The [revision](https://cloud.google.com/run/docs/managing/revisions)\n  (i.e., the function name plus the revision suffix).\n- **Google Cloud Functions:** The value of the\n  [`K_REVISION` environment variable](https://cloud.google.com/functions/docs/env-var#runtime_environment_variables_set_automatically).\n- **Azure Functions:** Not applicable. Do not set this attribute.\n",
		"stability": "development",
		"examples": ["26", "pinkfroid-00002"]
	},
	"faas.instance": {
		"description": "The execution environment ID as a string, that will be potentially reused for other invocations to the same function/function version.\n",
		"type": "string",
		"note": "- **AWS Lambda:** Use the (full) log stream name.\n",
		"stability": "development",
		"examples": ["2021/06/28/[$LATEST]2f399eb14537447da05ab2a2e39309de"]
	},
	"faas.max_memory": {
		"description": "The amount of memory available to the serverless function converted to Bytes.\n",
		"type": "number",
		"note": "It's recommended to set this attribute since e.g. too little memory can easily stop a Java AWS Lambda function from working correctly. On AWS Lambda, the environment variable `AWS_LAMBDA_FUNCTION_MEMORY_SIZE` provides this information (which must be multiplied by 1,048,576).\n",
		"stability": "development",
		"examples": ["134217728"]
	},
	"faas.trigger": {
		"description": "Type of the trigger which caused this function invocation.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"datasource",
			"http",
			"pubsub",
			"timer",
			"other"
		]
	},
	"faas.invoked_name": {
		"description": "The name of the invoked function.\n",
		"type": "string",
		"note": "SHOULD be equal to the `faas.name` resource attribute of the invoked function.\n",
		"stability": "development",
		"examples": ["my-function"]
	},
	"faas.invoked_provider": {
		"description": "The cloud provider of the invoked function.\n",
		"type": "string",
		"note": "SHOULD be equal to the `cloud.provider` resource attribute of the invoked function.\n",
		"stability": "development",
		"examples": [
			"alibaba_cloud",
			"aws",
			"azure",
			"gcp",
			"tencent_cloud"
		]
	},
	"faas.invoked_region": {
		"description": "The cloud region of the invoked function.\n",
		"type": "string",
		"note": "SHOULD be equal to the `cloud.region` resource attribute of the invoked function.\n",
		"stability": "development",
		"examples": ["eu-central-1"]
	},
	"faas.invocation_id": {
		"description": "The invocation ID of the current function invocation.\n",
		"type": "string",
		"stability": "development",
		"examples": ["af9d5aa4-a685-4c5f-a22b-444f80b3cc28"]
	},
	"faas.time": {
		"description": "A string containing the function invocation time in the [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format expressed in [UTC](https://www.w3.org/TR/NOTE-datetime).\n",
		"type": "string",
		"stability": "development",
		"examples": ["2020-01-23T13:47:06Z"]
	},
	"faas.cron": {
		"description": "A string containing the schedule period as [Cron Expression](https://docs.oracle.com/cd/E12058_01/doc/doc.1014/e12030/cron_expressions.htm).\n",
		"type": "string",
		"stability": "development",
		"examples": ["0/5 * * * ? *"]
	},
	"faas.coldstart": {
		"description": "A boolean that is true if the serverless function is executed for the first time (aka cold-start).\n",
		"type": "boolean",
		"stability": "development"
	},
	"faas.document.collection": {
		"description": "The name of the source on which the triggering operation was performed. For example, in Cloud Storage or S3 corresponds to the bucket name, and in Cosmos DB to the database name.\n",
		"type": "string",
		"stability": "development",
		"examples": ["myBucketName", "myDbName"]
	},
	"faas.document.operation": {
		"description": "Describes the type of the operation that was performed on the data.",
		"type": "string",
		"stability": "development",
		"examples": [
			"insert",
			"edit",
			"delete"
		]
	},
	"faas.document.time": {
		"description": "A string containing the time when the data was accessed in the [ISO 8601](https://www.iso.org/iso-8601-date-and-time-format.html) format expressed in [UTC](https://www.w3.org/TR/NOTE-datetime).\n",
		"type": "string",
		"stability": "development",
		"examples": ["2020-01-23T13:47:06Z"]
	},
	"faas.document.name": {
		"description": "The document name/table subjected to the operation. For example, in Cloud Storage or S3 is the name of the file, and in Cosmos DB the table name.\n",
		"type": "string",
		"stability": "development",
		"examples": ["myFile.txt", "myTableName"]
	}
};
var faas_default = {
	namespace: namespace$44,
	description: description$44,
	attributes: attributes$44
};

//#endregion
//#region src/internal/agents/tools/data/feature_flags.json
var namespace$43 = "feature_flags";
var description$43 = "This document defines attributes for Feature Flags.\n";
var attributes$43 = {
	"feature_flag.key": {
		"description": "The lookup key of the feature flag.",
		"type": "string",
		"stability": "release_candidate",
		"examples": ["logo-color"]
	},
	"feature_flag.provider.name": {
		"description": "Identifies the feature flag provider.",
		"type": "string",
		"stability": "release_candidate",
		"examples": ["Flag Manager"]
	},
	"feature_flag.result.variant": {
		"description": "A semantic identifier for an evaluated flag value.\n",
		"type": "string",
		"note": "A semantic identifier, commonly referred to as a variant, provides a means\nfor referring to a value without including the value itself. This can\nprovide additional context for understanding the meaning behind a value.\nFor example, the variant `red` maybe be used for the value `#c05543`.",
		"stability": "release_candidate",
		"examples": [
			"red",
			"true",
			"on"
		]
	},
	"feature_flag.context.id": {
		"description": "The unique identifier for the flag evaluation context. For example, the targeting key.\n",
		"type": "string",
		"stability": "release_candidate",
		"examples": ["5157782b-2203-4c80-a857-dbbd5e7761db"]
	},
	"feature_flag.version": {
		"description": "The version of the ruleset used during the evaluation. This may be any stable value which uniquely identifies the ruleset.\n",
		"type": "string",
		"stability": "release_candidate",
		"examples": ["1", "01ABCDEF"]
	},
	"feature_flag.set.id": {
		"description": "The identifier of the [flag set](https://openfeature.dev/specification/glossary/#flag-set) to which the feature flag belongs.\n",
		"type": "string",
		"stability": "release_candidate",
		"examples": [
			"proj-1",
			"ab98sgs",
			"service1/dev"
		]
	},
	"feature_flag.result.reason": {
		"description": "The reason code which shows how a feature flag value was determined.\n",
		"type": "string",
		"stability": "release_candidate",
		"examples": [
			"static",
			"default",
			"targeting_match",
			"split",
			"cached",
			"disabled",
			"unknown",
			"stale",
			"error"
		]
	},
	"feature_flag.result.value": {
		"description": "The evaluated value of the feature flag.",
		"type": "string",
		"note": "With some feature flag providers, feature flag results can be quite large or contain private or sensitive details.\nBecause of this, `feature_flag.result.variant` is often the preferred attribute if it is available.\n\nIt may be desirable to redact or otherwise limit the size and scope of `feature_flag.result.value` if possible.\nBecause the evaluated flag value is unstructured and may be any type, it is left to the instrumentation author to determine how best to achieve this.\n",
		"stability": "release_candidate",
		"examples": [
			"#ff0000",
			"true",
			"3"
		]
	}
};
var feature_flags_default = {
	namespace: namespace$43,
	description: description$43,
	attributes: attributes$43
};

//#endregion
//#region src/internal/agents/tools/data/file.json
var namespace$42 = "file";
var description$42 = "Describes file attributes.";
var attributes$42 = {
	"file.accessed": {
		"description": "Time when the file was last accessed, in ISO 8601 format.\n",
		"type": "string",
		"note": "This attribute might not be supported by some file systems — NFS, FAT32, in embedded OS, etc.\n",
		"stability": "development",
		"examples": ["2021-01-01T12:00:00Z"]
	},
	"file.attributes": {
		"description": "Array of file attributes.\n",
		"type": "string",
		"note": "Attributes names depend on the OS or file system. Here’s a non-exhaustive list of values expected for this attribute: `archive`, `compressed`, `directory`, `encrypted`, `execute`, `hidden`, `immutable`, `journaled`, `read`, `readonly`, `symbolic link`, `system`, `temporary`, `write`.\n",
		"stability": "development",
		"examples": ["[\"readonly\",\"hidden\"]"]
	},
	"file.created": {
		"description": "Time when the file was created, in ISO 8601 format.\n",
		"type": "string",
		"note": "This attribute might not be supported by some file systems — NFS, FAT32, in embedded OS, etc.\n",
		"stability": "development",
		"examples": ["2021-01-01T12:00:00Z"]
	},
	"file.changed": {
		"description": "Time when the file attributes or metadata was last changed, in ISO 8601 format.\n",
		"type": "string",
		"note": "`file.changed` captures the time when any of the file's properties or attributes (including the content) are changed, while `file.modified` captures the timestamp when the file content is modified.\n",
		"stability": "development",
		"examples": ["2021-01-01T12:00:00Z"]
	},
	"file.directory": {
		"description": "Directory where the file is located. It should include the drive letter, when appropriate.\n",
		"type": "string",
		"stability": "development",
		"examples": ["/home/user", "C:\\Program Files\\MyApp"]
	},
	"file.extension": {
		"description": "File extension, excluding the leading dot.\n",
		"type": "string",
		"note": "When the file name has multiple extensions (example.tar.gz), only the last one should be captured (\"gz\", not \"tar.gz\").\n",
		"stability": "development",
		"examples": ["png", "gz"]
	},
	"file.fork_name": {
		"description": "Name of the fork. A fork is additional data associated with a filesystem object.\n",
		"type": "string",
		"note": "On Linux, a resource fork is used to store additional data with a filesystem object. A file always has at least one fork for the data portion, and additional forks may exist.\nOn NTFS, this is analogous to an Alternate Data Stream (ADS), and the default data stream for a file is just called $DATA. Zone.Identifier is commonly used by Windows to track contents downloaded from the Internet. An ADS is typically of the form: C:\\path\\to\\filename.extension:some_fork_name, and some_fork_name is the value that should populate `fork_name`. `filename.extension` should populate `file.name`, and `extension` should populate `file.extension`. The full path, `file.path`, will include the fork name.\n",
		"stability": "development",
		"examples": ["Zone.Identifer"]
	},
	"file.group.id": {
		"description": "Primary Group ID (GID) of the file.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1000"]
	},
	"file.group.name": {
		"description": "Primary group name of the file.\n",
		"type": "string",
		"stability": "development",
		"examples": ["users"]
	},
	"file.inode": {
		"description": "Inode representing the file in the filesystem.\n",
		"type": "string",
		"stability": "development",
		"examples": ["256383"]
	},
	"file.mode": {
		"description": "Mode of the file in octal representation.\n",
		"type": "string",
		"stability": "development",
		"examples": ["0640"]
	},
	"file.modified": {
		"description": "Time when the file content was last modified, in ISO 8601 format.\n",
		"type": "string",
		"stability": "development",
		"examples": ["2021-01-01T12:00:00Z"]
	},
	"file.name": {
		"description": "Name of the file including the extension, without the directory.\n",
		"type": "string",
		"stability": "development",
		"examples": ["example.png"]
	},
	"file.owner.id": {
		"description": "The user ID (UID) or security identifier (SID) of the file owner.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1000"]
	},
	"file.owner.name": {
		"description": "Username of the file owner.\n",
		"type": "string",
		"stability": "development",
		"examples": ["root"]
	},
	"file.path": {
		"description": "Full path to the file, including the file name. It should include the drive letter, when appropriate.\n",
		"type": "string",
		"stability": "development",
		"examples": ["/home/alice/example.png", "C:\\Program Files\\MyApp\\myapp.exe"]
	},
	"file.size": {
		"description": "File size in bytes.\n",
		"type": "number",
		"stability": "development"
	},
	"file.symbolic_link.target_path": {
		"description": "Path to the target of a symbolic link.\n",
		"type": "string",
		"note": "This attribute is only applicable to symbolic links.\n",
		"stability": "development",
		"examples": ["/usr/bin/python3"]
	}
};
var file_default = {
	namespace: namespace$42,
	description: description$42,
	attributes: attributes$42
};

//#endregion
//#region src/internal/agents/tools/data/gcp.json
var namespace$41 = "gcp";
var description$41 = "Attributes for Google Cloud client libraries.\n";
var attributes$41 = { "gcp.client.service": {
	"description": "Identifies the Google Cloud service for which the official client library is intended.",
	"type": "string",
	"note": "Intended to be a stable identifier for Google Cloud client libraries that is uniform across implementation languages. The value should be derived from the canonical service domain for the service; for example, 'foo.googleapis.com' should result in a value of 'foo'.\n",
	"stability": "development",
	"examples": [
		"appengine",
		"run",
		"firestore",
		"alloydb",
		"spanner"
	]
} };
var gcp_default = {
	namespace: namespace$41,
	description: description$41,
	attributes: attributes$41
};

//#endregion
//#region src/internal/agents/tools/data/gen_ai.json
var namespace$40 = "gen_ai";
var description$40 = "This document defines the attributes used to describe telemetry in the context of Generative Artificial Intelligence (GenAI) Models requests and responses.\n";
var attributes$40 = {
	"gen_ai.provider.name": {
		"description": "The Generative AI provider as identified by the client or server instrumentation.",
		"type": "string",
		"note": "The attribute SHOULD be set based on the instrumentation's best\nknowledge and may differ from the actual model provider.\n\nMultiple providers, including Azure OpenAI, Gemini, and AI hosting platforms\nare accessible using the OpenAI REST API and corresponding client libraries,\nbut may proxy or host models from different providers.\n\nThe `gen_ai.request.model`, `gen_ai.response.model`, and `server.address`\nattributes may help identify the actual system in use.\n\nThe `gen_ai.provider.name` attribute acts as a discriminator that\nidentifies the GenAI telemetry format flavor specific to that provider\nwithin GenAI semantic conventions.\nIt SHOULD be set consistently with provider-specific attributes and signals.\nFor example, GenAI spans, metrics, and events related to AWS Bedrock\nshould have the `gen_ai.provider.name` set to `aws.bedrock` and include\napplicable `aws.bedrock.*` attributes and are not expected to include\n`openai.*` attributes.\n",
		"stability": "development",
		"examples": [
			"openai",
			"gcp.gen_ai",
			"gcp.vertex_ai",
			"gcp.gemini",
			"anthropic",
			"cohere",
			"azure.ai.inference",
			"azure.ai.openai",
			"ibm.watsonx.ai",
			"aws.bedrock",
			"perplexity",
			"x_ai",
			"deepseek",
			"groq",
			"mistral_ai"
		]
	},
	"gen_ai.request.model": {
		"description": "The name of the GenAI model a request is being made to.",
		"type": "string",
		"stability": "development",
		"examples": ["gpt-4"]
	},
	"gen_ai.request.max_tokens": {
		"description": "The maximum number of tokens the model generates for a request.",
		"type": "number",
		"stability": "development",
		"examples": ["100"]
	},
	"gen_ai.request.choice.count": {
		"description": "The target number of candidate completions to return.",
		"type": "number",
		"stability": "development",
		"examples": ["3"]
	},
	"gen_ai.request.temperature": {
		"description": "The temperature setting for the GenAI request.",
		"type": "number",
		"stability": "development",
		"examples": ["0"]
	},
	"gen_ai.request.top_p": {
		"description": "The top_p sampling setting for the GenAI request.",
		"type": "number",
		"stability": "development",
		"examples": ["1"]
	},
	"gen_ai.request.top_k": {
		"description": "The top_k sampling setting for the GenAI request.",
		"type": "number",
		"stability": "development",
		"examples": ["1"]
	},
	"gen_ai.request.stop_sequences": {
		"description": "List of sequences that the model will use to stop generating further tokens.",
		"type": "string",
		"stability": "development",
		"examples": ["[\"forest\",\"lived\"]"]
	},
	"gen_ai.request.frequency_penalty": {
		"description": "The frequency penalty setting for the GenAI request.",
		"type": "number",
		"stability": "development",
		"examples": ["0.1"]
	},
	"gen_ai.request.presence_penalty": {
		"description": "The presence penalty setting for the GenAI request.",
		"type": "number",
		"stability": "development",
		"examples": ["0.1"]
	},
	"gen_ai.request.encoding_formats": {
		"description": "The encoding formats requested in an embeddings operation, if specified.",
		"type": "string",
		"note": "In some GenAI systems the encoding formats are called embedding types. Also, some GenAI systems only accept a single format per request.\n",
		"stability": "development",
		"examples": ["[\"base64\"]", "[\"float\",\"binary\"]"]
	},
	"gen_ai.request.seed": {
		"description": "Requests with same seed value more likely to return same result.",
		"type": "number",
		"stability": "development",
		"examples": ["100"]
	},
	"gen_ai.response.id": {
		"description": "The unique identifier for the completion.",
		"type": "string",
		"stability": "development",
		"examples": ["chatcmpl-123"]
	},
	"gen_ai.response.model": {
		"description": "The name of the model that generated the response.",
		"type": "string",
		"stability": "development",
		"examples": ["gpt-4-0613"]
	},
	"gen_ai.response.finish_reasons": {
		"description": "Array of reasons the model stopped generating tokens, corresponding to each generation received.",
		"type": "string",
		"stability": "development",
		"examples": ["[\"stop\"]", "[\"stop\",\"length\"]"]
	},
	"gen_ai.usage.input_tokens": {
		"description": "The number of tokens used in the GenAI input (prompt).",
		"type": "number",
		"stability": "development",
		"examples": ["100"]
	},
	"gen_ai.usage.output_tokens": {
		"description": "The number of tokens used in the GenAI response (completion).",
		"type": "number",
		"stability": "development",
		"examples": ["180"]
	},
	"gen_ai.token.type": {
		"description": "The type of token being counted.",
		"type": "string",
		"stability": "development",
		"examples": [
			"input",
			"output",
			"output"
		]
	},
	"gen_ai.conversation.id": {
		"description": "The unique identifier for a conversation (session, thread), used to store and correlate messages within this conversation.",
		"type": "string",
		"stability": "development",
		"examples": ["conv_5j66UpCpwteGg4YSxUnt7lPY"]
	},
	"gen_ai.agent.id": {
		"description": "The unique identifier of the GenAI agent.",
		"type": "string",
		"stability": "development",
		"examples": ["asst_5j66UpCpwteGg4YSxUnt7lPY"]
	},
	"gen_ai.agent.name": {
		"description": "Human-readable name of the GenAI agent provided by the application.",
		"type": "string",
		"stability": "development",
		"examples": ["Math Tutor", "Fiction Writer"]
	},
	"gen_ai.agent.description": {
		"description": "Free-form description of the GenAI agent provided by the application.",
		"type": "string",
		"stability": "development",
		"examples": ["Helps with math problems", "Generates fiction stories"]
	},
	"gen_ai.tool.name": {
		"description": "Name of the tool utilized by the agent.",
		"type": "string",
		"stability": "development",
		"examples": ["Flights"]
	},
	"gen_ai.tool.call.id": {
		"description": "The tool call identifier.",
		"type": "string",
		"stability": "development",
		"examples": ["call_mszuSIzqtI65i1wAUOE8w5H4"]
	},
	"gen_ai.tool.description": {
		"description": "The tool description.",
		"type": "string",
		"stability": "development",
		"examples": ["Multiply two numbers"]
	},
	"gen_ai.tool.type": {
		"description": "Type of the tool utilized by the agent",
		"type": "string",
		"note": "Extension: A tool executed on the agent-side to directly call external APIs, bridging the gap between the agent and real-world systems.\n  Agent-side operations involve actions that are performed by the agent on the server or within the agent's controlled environment.\nFunction: A tool executed on the client-side, where the agent generates parameters for a predefined function, and the client executes the logic.\n  Client-side operations are actions taken on the user's end or within the client application.\nDatastore: A tool used by the agent to access and query structured or unstructured external data for retrieval-augmented tasks or knowledge updates.\n",
		"stability": "development",
		"examples": [
			"function",
			"extension",
			"datastore"
		]
	},
	"gen_ai.data_source.id": {
		"description": "The data source identifier.",
		"type": "string",
		"note": "Data sources are used by AI agents and RAG applications to store grounding data. A data source may be an external database, object store, document collection, website, or any other storage system used by the GenAI agent or application. The `gen_ai.data_source.id` SHOULD match the identifier used by the GenAI system rather than a name specific to the external storage, such as a database or object store. Semantic conventions referencing `gen_ai.data_source.id` MAY also leverage additional attributes, such as `db.*`, to further identify and describe the data source.\n",
		"stability": "development",
		"examples": ["H7STPQYOND"]
	},
	"gen_ai.operation.name": {
		"description": "The name of the operation being performed.",
		"type": "string",
		"note": "If one of the predefined values applies, but specific system uses a different name it's RECOMMENDED to document it in the semantic conventions for specific GenAI system and use system-specific name in the instrumentation. If a different name is not documented, instrumentation libraries SHOULD use applicable predefined value.\n",
		"stability": "development",
		"examples": [
			"chat",
			"generate_content",
			"text_completion",
			"embeddings",
			"create_agent",
			"invoke_agent",
			"execute_tool"
		]
	},
	"gen_ai.output.type": {
		"description": "Represents the content type requested by the client.",
		"type": "string",
		"note": "This attribute SHOULD be used when the client requests output of a specific type. The model may return zero or more outputs of this type.\nThis attribute specifies the output modality and not the actual output format. For example, if an image is requested, the actual output could be a URL pointing to an image file.\nAdditional output format details may be recorded in the future in the `gen_ai.output.{type}.*` attributes.\n",
		"stability": "development",
		"examples": [
			"text",
			"json",
			"image",
			"speech"
		]
	}
};
var gen_ai_default = {
	namespace: namespace$40,
	description: description$40,
	attributes: attributes$40
};

//#endregion
//#region src/internal/agents/tools/data/geo.json
var namespace$39 = "geo";
var description$39 = "Geo fields can carry data about a specific location related to an event. This geolocation information can be derived from techniques such as Geo IP, or be user-supplied.\nNote: Geo attributes are typically used under another namespace, such as client.* and describe the location of the corresponding entity (device, end-user, etc). Semantic conventions that reference geo attributes (as a root namespace) or embed them (under their own namespace) SHOULD document what geo attributes describe in the scope of that convention.\n";
var attributes$39 = {
	"geo.locality.name": {
		"description": "Locality name. Represents the name of a city, town, village, or similar populated place.\n",
		"type": "string",
		"stability": "development",
		"examples": ["Montreal", "Berlin"]
	},
	"geo.continent.code": {
		"description": "Two-letter code representing continent’s name.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"AF",
			"AN",
			"AS",
			"EU",
			"NA",
			"OC",
			"SA"
		]
	},
	"geo.country.iso_code": {
		"description": "Two-letter ISO Country Code ([ISO 3166-1 alpha2](https://wikipedia.org/wiki/ISO_3166-1#Codes)).\n",
		"type": "string",
		"stability": "development",
		"examples": ["CA"]
	},
	"geo.location.lon": {
		"description": "Longitude of the geo location in [WGS84](https://wikipedia.org/wiki/World_Geodetic_System#WGS84).\n",
		"type": "number",
		"stability": "development",
		"examples": ["-73.61483"]
	},
	"geo.location.lat": {
		"description": "Latitude of the geo location in [WGS84](https://wikipedia.org/wiki/World_Geodetic_System#WGS84).\n",
		"type": "number",
		"stability": "development",
		"examples": ["45.505918"]
	},
	"geo.postal_code": {
		"description": "Postal code associated with the location. Values appropriate for this field may also be known as a postcode or ZIP code and will vary widely from country to country.\n",
		"type": "string",
		"stability": "development",
		"examples": ["94040"]
	},
	"geo.region.iso_code": {
		"description": "Region ISO code ([ISO 3166-2](https://wikipedia.org/wiki/ISO_3166-2)).\n",
		"type": "string",
		"stability": "development",
		"examples": ["CA-QC"]
	}
};
var geo_default = {
	namespace: namespace$39,
	description: description$39,
	attributes: attributes$39
};

//#endregion
//#region src/internal/agents/tools/data/go.json
var namespace$38 = "go";
var description$38 = "This document defines Go related attributes.\n";
var attributes$38 = { "go.memory.type": {
	"description": "The type of memory.",
	"type": "string",
	"stability": "development",
	"examples": ["stack", "other"]
} };
var go_default = {
	namespace: namespace$38,
	description: description$38,
	attributes: attributes$38
};

//#endregion
//#region src/internal/agents/tools/data/graphql.json
var namespace$37 = "graphql";
var description$37 = "This document defines attributes for GraphQL.";
var attributes$37 = {
	"graphql.operation.name": {
		"description": "The name of the operation being executed.",
		"type": "string",
		"stability": "development",
		"examples": ["findBookById"]
	},
	"graphql.operation.type": {
		"description": "The type of the operation being executed.",
		"type": "string",
		"stability": "development",
		"examples": [
			"query",
			"mutation",
			"subscription"
		]
	},
	"graphql.document": {
		"description": "The GraphQL document being executed.",
		"type": "string",
		"note": "The value may be sanitized to exclude sensitive information.",
		"stability": "development",
		"examples": ["query findBookById { bookById(id: ?) { name } }"]
	}
};
var graphql_default = {
	namespace: namespace$37,
	description: description$37,
	attributes: attributes$37
};

//#endregion
//#region src/internal/agents/tools/data/hardware.json
var namespace$36 = "hardware";
var description$36 = "Attributes for hardware.\n";
var attributes$36 = {
	"hw.id": {
		"description": "An identifier for the hardware component, unique within the monitored host\n",
		"type": "string",
		"stability": "development",
		"examples": ["win32battery_battery_testsysa33_1"]
	},
	"hw.name": {
		"description": "An easily-recognizable name for the hardware component\n",
		"type": "string",
		"stability": "development",
		"examples": ["eth0"]
	},
	"hw.parent": {
		"description": "Unique identifier of the parent component (typically the `hw.id` attribute of the enclosure, or disk controller)\n",
		"type": "string",
		"stability": "development",
		"examples": ["dellStorage_perc_0"]
	},
	"hw.type": {
		"description": "Type of the component\n",
		"type": "string",
		"note": "Describes the category of the hardware component for which `hw.state` is being reported. For example, `hw.type=temperature` along with `hw.state=degraded` would indicate that the temperature of the hardware component has been reported as `degraded`.\n",
		"stability": "development",
		"examples": [
			"battery",
			"cpu",
			"disk_controller",
			"enclosure",
			"fan",
			"gpu",
			"logical_disk",
			"memory",
			"network",
			"physical_disk",
			"power_supply",
			"tape_drive",
			"temperature",
			"voltage"
		]
	},
	"hw.state": {
		"description": "The current state of the component\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"ok",
			"degraded",
			"failed"
		]
	}
};
var hardware_default = {
	namespace: namespace$36,
	description: description$36,
	attributes: attributes$36
};

//#endregion
//#region src/internal/agents/tools/data/heroku.json
var namespace$35 = "heroku";
var description$35 = "This document defines attributes for the Heroku platform on which application/s are running.\n";
var attributes$35 = {
	"heroku.release.creation_timestamp": {
		"description": "Time and date the release was created\n",
		"type": "string",
		"stability": "development",
		"examples": ["2022-10-23T18:00:42Z"]
	},
	"heroku.release.commit": {
		"description": "Commit hash for the current release\n",
		"type": "string",
		"stability": "development",
		"examples": ["e6134959463efd8966b20e75b913cafe3f5ec"]
	},
	"heroku.app.id": {
		"description": "Unique identifier for the application\n",
		"type": "string",
		"stability": "development",
		"examples": ["2daa2797-e42b-4624-9322-ec3f968df4da"]
	}
};
var heroku_default = {
	namespace: namespace$35,
	description: description$35,
	attributes: attributes$35
};

//#endregion
//#region src/internal/agents/tools/data/host.json
var namespace$34 = "host";
var description$34 = "A host is defined as a computing instance. For example, physical servers, virtual machines, switches or disk array.\n";
var attributes$34 = {
	"host.id": {
		"description": "Unique host ID. For Cloud, this must be the instance_id assigned by the cloud provider. For non-containerized systems, this should be the `machine-id`. See the table below for the sources to use to determine the `machine-id` based on operating system.\n",
		"type": "string",
		"stability": "development",
		"examples": ["fdbf79e8af94cb7f9e8df36789187052"]
	},
	"host.name": {
		"description": "Name of the host. On Unix systems, it may contain what the hostname command returns, or the fully qualified hostname, or another name specified by the user.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry-test"]
	},
	"host.type": {
		"description": "Type of host. For Cloud, this must be the machine type.\n",
		"type": "string",
		"stability": "development",
		"examples": ["n1-standard-1"]
	},
	"host.arch": {
		"description": "The CPU architecture the host system is running on.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"amd64",
			"arm32",
			"arm64",
			"ia64",
			"ppc32",
			"ppc64",
			"s390x",
			"x86"
		]
	},
	"host.image.name": {
		"description": "Name of the VM image or OS install the host was instantiated from.\n",
		"type": "string",
		"stability": "development",
		"examples": ["infra-ami-eks-worker-node-7d4ec78312", "CentOS-8-x86_64-1905"]
	},
	"host.image.id": {
		"description": "VM image ID or host OS image ID. For Cloud, this value is from the provider.\n",
		"type": "string",
		"stability": "development",
		"examples": ["ami-07b06b442921831e5"]
	},
	"host.image.version": {
		"description": "The version string of the VM image or host OS as defined in [Version Attributes](/docs/resource/README.md#version-attributes).\n",
		"type": "string",
		"stability": "development",
		"examples": ["0.1"]
	},
	"host.ip": {
		"description": "Available IP addresses of the host, excluding loopback interfaces.\n",
		"type": "string",
		"note": "IPv4 Addresses MUST be specified in dotted-quad notation. IPv6 addresses MUST be specified in the [RFC 5952](https://www.rfc-editor.org/rfc/rfc5952.html) format.\n",
		"stability": "development",
		"examples": ["[\"192.168.1.140\",\"fe80::abc2:4a28:737a:609e\"]"]
	},
	"host.mac": {
		"description": "Available MAC addresses of the host, excluding loopback interfaces.\n",
		"type": "string",
		"note": "MAC Addresses MUST be represented in [IEEE RA hexadecimal form](https://standards.ieee.org/wp-content/uploads/import/documents/tutorials/eui.pdf): as hyphen-separated octets in uppercase hexadecimal form from most to least significant.\n",
		"stability": "development",
		"examples": ["[\"AC-DE-48-23-45-67\",\"AC-DE-48-23-45-67-01-9F\"]"]
	},
	"host.cpu.vendor.id": {
		"description": "Processor manufacturer identifier. A maximum 12-character string.\n",
		"type": "string",
		"note": "[CPUID](https://wiki.osdev.org/CPUID) command returns the vendor ID string in EBX, EDX and ECX registers. Writing these to memory in this order results in a 12-character string.\n",
		"stability": "development",
		"examples": ["GenuineIntel"]
	},
	"host.cpu.family": {
		"description": "Family or generation of the CPU.\n",
		"type": "string",
		"stability": "development",
		"examples": ["6", "PA-RISC 1.1e"]
	},
	"host.cpu.model.id": {
		"description": "Model identifier. It provides more granular information about the CPU, distinguishing it from other CPUs within the same family.\n",
		"type": "string",
		"stability": "development",
		"examples": ["6", "9000/778/B180L"]
	},
	"host.cpu.model.name": {
		"description": "Model designation of the processor.\n",
		"type": "string",
		"stability": "development",
		"examples": ["11th Gen Intel(R) Core(TM) i7-1185G7 @ 3.00GHz"]
	},
	"host.cpu.stepping": {
		"description": "Stepping or core revisions.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1", "r1p1"]
	},
	"host.cpu.cache.l2.size": {
		"description": "The amount of level 2 memory cache available to the processor (in Bytes).\n",
		"type": "number",
		"stability": "development",
		"examples": ["12288000"]
	}
};
var host_default = {
	namespace: namespace$34,
	description: description$34,
	attributes: attributes$34
};

//#endregion
//#region src/internal/agents/tools/data/http.json
var namespace$33 = "http";
var description$33 = "This document defines semantic convention attributes in the HTTP namespace.";
var attributes$33 = {
	"http.request.body.size": {
		"description": "The size of the request payload body in bytes. This is the number of bytes transferred excluding headers and is often, but not always, present as the [Content-Length](https://www.rfc-editor.org/rfc/rfc9110.html#field.content-length) header. For requests using transport encoding, this should be the compressed size.\n",
		"type": "number",
		"stability": "development",
		"examples": ["3495"]
	},
	"http.request.header": {
		"description": "HTTP request headers, `<key>` being the normalized HTTP Header name (lowercase), the value being the header values.\n",
		"type": "string",
		"note": "Instrumentations SHOULD require an explicit configuration of which headers are to be captured.\nIncluding all request headers can be a security risk - explicit configuration helps avoid leaking sensitive information.\n\nThe `User-Agent` header is already captured in the `user_agent.original` attribute.\nUsers MAY explicitly configure instrumentations to capture them even though it is not recommended.\n\nThe attribute value MUST consist of either multiple header values as an array of strings\nor a single-item array containing a possibly comma-concatenated string, depending on the way\nthe HTTP library provides access to headers.\n\nExamples:\n\n- A header `Content-Type: application/json` SHOULD be recorded as the `http.request.header.content-type`\n  attribute with value `[\"application/json\"]`.\n- A header `X-Forwarded-For: 1.2.3.4, 1.2.3.5` SHOULD be recorded as the `http.request.header.x-forwarded-for`\n  attribute with value `[\"1.2.3.4\", \"1.2.3.5\"]` or `[\"1.2.3.4, 1.2.3.5\"]` depending on the HTTP library.\n",
		"stability": "stable",
		"examples": ["[\"application/json\"]", "[\"1.2.3.4\",\"1.2.3.5\"]"]
	},
	"http.request.method": {
		"description": "HTTP request method.",
		"type": "string",
		"note": "HTTP request method value SHOULD be \"known\" to the instrumentation.\nBy default, this convention defines \"known\" methods as the ones listed in [RFC9110](https://www.rfc-editor.org/rfc/rfc9110.html#name-methods)\nand the PATCH method defined in [RFC5789](https://www.rfc-editor.org/rfc/rfc5789.html).\n\nIf the HTTP request method is not known to instrumentation, it MUST set the `http.request.method` attribute to `_OTHER`.\n\nIf the HTTP instrumentation could end up converting valid HTTP request methods to `_OTHER`, then it MUST provide a way to override\nthe list of known HTTP methods. If this override is done via environment variable, then the environment variable MUST be named\nOTEL_INSTRUMENTATION_HTTP_KNOWN_METHODS and support a comma-separated list of case-sensitive known HTTP methods\n(this list MUST be a full override of the default known method, it is not a list of known methods in addition to the defaults).\n\nHTTP method names are case-sensitive and `http.request.method` attribute value MUST match a known HTTP method name exactly.\nInstrumentations for specific web frameworks that consider HTTP methods to be case insensitive, SHOULD populate a canonical equivalent.\nTracing instrumentations that do so, MUST also set `http.request.method_original` to the original value.\n",
		"stability": "stable",
		"examples": [
			"CONNECT",
			"DELETE",
			"GET",
			"HEAD",
			"OPTIONS",
			"PATCH",
			"POST",
			"PUT",
			"TRACE",
			"_OTHER"
		]
	},
	"http.request.method_original": {
		"description": "Original HTTP method sent by the client in the request line.",
		"type": "string",
		"stability": "stable",
		"examples": [
			"GeT",
			"ACL",
			"foo"
		]
	},
	"http.request.resend_count": {
		"description": "The ordinal number of request resending attempt (for any reason, including redirects).\n",
		"type": "number",
		"note": "The resend count SHOULD be updated each time an HTTP request gets resent by the client, regardless of what was the cause of the resending (e.g. redirection, authorization failure, 503 Server Unavailable, network issues, or any other).\n",
		"stability": "stable",
		"examples": ["3"]
	},
	"http.request.size": {
		"description": "The total size of the request in bytes. This should be the total number of bytes sent over the wire, including the request line (HTTP/1.1), framing (HTTP/2 and HTTP/3), headers, and request body if any.\n",
		"type": "number",
		"stability": "development",
		"examples": ["1437"]
	},
	"http.response.body.size": {
		"description": "The size of the response payload body in bytes. This is the number of bytes transferred excluding headers and is often, but not always, present as the [Content-Length](https://www.rfc-editor.org/rfc/rfc9110.html#field.content-length) header. For requests using transport encoding, this should be the compressed size.\n",
		"type": "number",
		"stability": "development",
		"examples": ["3495"]
	},
	"http.response.header": {
		"description": "HTTP response headers, `<key>` being the normalized HTTP Header name (lowercase), the value being the header values.\n",
		"type": "string",
		"note": "Instrumentations SHOULD require an explicit configuration of which headers are to be captured.\nIncluding all response headers can be a security risk - explicit configuration helps avoid leaking sensitive information.\n\nUsers MAY explicitly configure instrumentations to capture them even though it is not recommended.\n\nThe attribute value MUST consist of either multiple header values as an array of strings\nor a single-item array containing a possibly comma-concatenated string, depending on the way\nthe HTTP library provides access to headers.\n\nExamples:\n\n- A header `Content-Type: application/json` header SHOULD be recorded as the `http.request.response.content-type`\n  attribute with value `[\"application/json\"]`.\n- A header `My-custom-header: abc, def` header SHOULD be recorded as the `http.response.header.my-custom-header`\n  attribute with value `[\"abc\", \"def\"]` or `[\"abc, def\"]` depending on the HTTP library.\n",
		"stability": "stable",
		"examples": ["[\"application/json\"]", "[\"abc\",\"def\"]"]
	},
	"http.response.size": {
		"description": "The total size of the response in bytes. This should be the total number of bytes sent over the wire, including the status line (HTTP/1.1), framing (HTTP/2 and HTTP/3), headers, and response body and trailers if any.\n",
		"type": "number",
		"stability": "development",
		"examples": ["1437"]
	},
	"http.response.status_code": {
		"description": "[HTTP response status code](https://tools.ietf.org/html/rfc7231#section-6).",
		"type": "number",
		"stability": "stable",
		"examples": ["200"]
	},
	"http.route": {
		"description": "The matched route, that is, the path template in the format used by the respective server framework.\n",
		"type": "string",
		"note": "MUST NOT be populated when this is not supported by the HTTP server framework as the route attribute should have low-cardinality and the URI path can NOT substitute it.\nSHOULD include the [application root](/docs/http/http-spans.md#http-server-definitions) if there is one.\n",
		"stability": "stable",
		"examples": ["/users/:userID?", "{controller}/{action}/{id?}"]
	},
	"http.connection.state": {
		"description": "State of the HTTP connection in the HTTP connection pool.",
		"type": "string",
		"stability": "development",
		"examples": ["active", "idle"]
	}
};
var http_default = {
	namespace: namespace$33,
	description: description$33,
	attributes: attributes$33
};

//#endregion
//#region src/internal/agents/tools/data/ios.json
var namespace$32 = "ios";
var description$32 = "This group describes iOS-specific attributes.\n";
var attributes$32 = { "ios.app.state": {
	"description": "This attribute represents the state of the application.\n",
	"type": "string",
	"note": "The iOS lifecycle states are defined in the [UIApplicationDelegate documentation](https://developer.apple.com/documentation/uikit/uiapplicationdelegate), and from which the `OS terminology` column values are derived.\n",
	"stability": "development",
	"examples": [
		"active",
		"inactive",
		"background",
		"foreground",
		"terminate"
	]
} };
var ios_default = {
	namespace: namespace$32,
	description: description$32,
	attributes: attributes$32
};

//#endregion
//#region src/internal/agents/tools/data/jvm.json
var namespace$31 = "jvm";
var description$31 = "This document defines Java Virtual machine related attributes.\n";
var attributes$31 = {
	"jvm.gc.action": {
		"description": "Name of the garbage collector action.",
		"type": "string",
		"note": "Garbage collector action is generally obtained via [GarbageCollectionNotificationInfo#getGcAction()](https://docs.oracle.com/en/java/javase/11/docs/api/jdk.management/com/sun/management/GarbageCollectionNotificationInfo.html#getGcAction()).\n",
		"stability": "stable",
		"examples": ["end of minor GC", "end of major GC"]
	},
	"jvm.gc.cause": {
		"description": "Name of the garbage collector cause.",
		"type": "string",
		"note": "Garbage collector cause is generally obtained via [GarbageCollectionNotificationInfo#getGcCause()](https://docs.oracle.com/en/java/javase/11/docs/api/jdk.management/com/sun/management/GarbageCollectionNotificationInfo.html#getGcCause()).\n",
		"stability": "development",
		"examples": ["System.gc()", "Allocation Failure"]
	},
	"jvm.gc.name": {
		"description": "Name of the garbage collector.",
		"type": "string",
		"note": "Garbage collector name is generally obtained via [GarbageCollectionNotificationInfo#getGcName()](https://docs.oracle.com/en/java/javase/11/docs/api/jdk.management/com/sun/management/GarbageCollectionNotificationInfo.html#getGcName()).\n",
		"stability": "stable",
		"examples": ["G1 Young Generation", "G1 Old Generation"]
	},
	"jvm.memory.type": {
		"description": "The type of memory.",
		"type": "string",
		"stability": "stable",
		"examples": ["heap", "non_heap"]
	},
	"jvm.memory.pool.name": {
		"description": "Name of the memory pool.",
		"type": "string",
		"note": "Pool names are generally obtained via [MemoryPoolMXBean#getName()](https://docs.oracle.com/en/java/javase/11/docs/api/java.management/java/lang/management/MemoryPoolMXBean.html#getName()).\n",
		"stability": "stable",
		"examples": [
			"G1 Old Gen",
			"G1 Eden space",
			"G1 Survivor Space"
		]
	},
	"jvm.thread.daemon": {
		"description": "Whether the thread is daemon or not.",
		"type": "boolean",
		"stability": "stable"
	},
	"jvm.thread.state": {
		"description": "State of the thread.",
		"type": "string",
		"stability": "stable",
		"examples": [
			"new",
			"runnable",
			"blocked",
			"waiting",
			"timed_waiting",
			"terminated"
		]
	},
	"jvm.buffer.pool.name": {
		"description": "Name of the buffer pool.",
		"type": "string",
		"note": "Pool names are generally obtained via [BufferPoolMXBean#getName()](https://docs.oracle.com/en/java/javase/11/docs/api/java.management/java/lang/management/BufferPoolMXBean.html#getName()).\n",
		"stability": "development",
		"examples": ["mapped", "direct"]
	}
};
var jvm_default = {
	namespace: namespace$31,
	description: description$31,
	attributes: attributes$31
};

//#endregion
//#region src/internal/agents/tools/data/k8s.json
var namespace$30 = "k8s";
var description$30 = "Kubernetes resource attributes.\n";
var attributes$30 = {
	"k8s.cluster.name": {
		"description": "The name of the cluster.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry-cluster"]
	},
	"k8s.cluster.uid": {
		"description": "A pseudo-ID for the cluster, set to the UID of the `kube-system` namespace.\n",
		"type": "string",
		"note": "K8s doesn't have support for obtaining a cluster ID. If this is ever\nadded, we will recommend collecting the `k8s.cluster.uid` through the\nofficial APIs. In the meantime, we are able to use the `uid` of the\n`kube-system` namespace as a proxy for cluster ID. Read on for the\nrationale.\n\nEvery object created in a K8s cluster is assigned a distinct UID. The\n`kube-system` namespace is used by Kubernetes itself and will exist\nfor the lifetime of the cluster. Using the `uid` of the `kube-system`\nnamespace is a reasonable proxy for the K8s ClusterID as it will only\nchange if the cluster is rebuilt. Furthermore, Kubernetes UIDs are\nUUIDs as standardized by\n[ISO/IEC 9834-8 and ITU-T X.667](https://www.itu.int/ITU-T/studygroups/com17/oid.html).\nWhich states:\n\n> If generated according to one of the mechanisms defined in Rec.\n> ITU-T X.667 | ISO/IEC 9834-8, a UUID is either guaranteed to be\n> different from all other UUIDs generated before 3603 A.D., or is\n> extremely likely to be different (depending on the mechanism chosen).\n\nTherefore, UIDs between clusters should be extremely unlikely to\nconflict.\n",
		"stability": "development",
		"examples": ["218fc5a9-a5f1-4b54-aa05-46717d0ab26d"]
	},
	"k8s.node.name": {
		"description": "The name of the Node.\n",
		"type": "string",
		"stability": "development",
		"examples": ["node-1"]
	},
	"k8s.node.uid": {
		"description": "The UID of the Node.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1eb3a0c6-0477-4080-a9cb-0cb7db65c6a2"]
	},
	"k8s.node.label": {
		"description": "The label placed on the Node, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "Examples:\n\n- A label `kubernetes.io/arch` with value `arm64` SHOULD be recorded\n  as the `k8s.node.label.kubernetes.io/arch` attribute with value `\"arm64\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.node.label.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["arm64", ""]
	},
	"k8s.node.annotation": {
		"description": "The annotation placed on the Node, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "Examples:\n\n- An annotation `node.alpha.kubernetes.io/ttl` with value `0` SHOULD be recorded as\n  the `k8s.node.annotation.node.alpha.kubernetes.io/ttl` attribute with value `\"0\"`.\n- An annotation `data` with empty string value SHOULD be recorded as\n  the `k8s.node.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["0", ""]
	},
	"k8s.namespace.name": {
		"description": "The name of the namespace that the pod is running in.\n",
		"type": "string",
		"stability": "development",
		"examples": ["default"]
	},
	"k8s.namespace.label": {
		"description": "The label placed on the Namespace, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `kubernetes.io/metadata.name` with value `default` SHOULD be recorded\n  as the `k8s.namespace.label.kubernetes.io/metadata.name` attribute with value `\"default\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.namespace.label.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["default", ""]
	},
	"k8s.namespace.annotation": {
		"description": "The annotation placed on the Namespace, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `ttl` with value `0` SHOULD be recorded\n  as the `k8s.namespace.annotation.ttl` attribute with value `\"0\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.namespace.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["0", ""]
	},
	"k8s.pod.uid": {
		"description": "The UID of the Pod.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.pod.name": {
		"description": "The name of the Pod.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry-pod-autoconf"]
	},
	"k8s.pod.label": {
		"description": "The label placed on the Pod, the `<key>` being the label name, the value being the label value.\n",
		"type": "string",
		"note": "Examples:\n\n- A label `app` with value `my-app` SHOULD be recorded as\n  the `k8s.pod.label.app` attribute with value `\"my-app\"`.\n- A label `mycompany.io/arch` with value `x64` SHOULD be recorded as\n  the `k8s.pod.label.mycompany.io/arch` attribute with value `\"x64\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.pod.label.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": [
			"my-app",
			"x64",
			""
		]
	},
	"k8s.pod.annotation": {
		"description": "The annotation placed on the Pod, the `<key>` being the annotation name, the value being the annotation value.\n",
		"type": "string",
		"note": "Examples:\n\n- An annotation `kubernetes.io/enforce-mountable-secrets` with value `true` SHOULD be recorded as\n  the `k8s.pod.annotation.kubernetes.io/enforce-mountable-secrets` attribute with value `\"true\"`.\n- An annotation `mycompany.io/arch` with value `x64` SHOULD be recorded as\n  the `k8s.pod.annotation.mycompany.io/arch` attribute with value `\"x64\"`.\n- An annotation `data` with empty string value SHOULD be recorded as\n  the `k8s.pod.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": [
			"true",
			"x64",
			""
		]
	},
	"k8s.container.name": {
		"description": "The name of the Container from Pod specification, must be unique within a Pod. Container runtime usually uses different globally unique name (`container.name`).\n",
		"type": "string",
		"stability": "development",
		"examples": ["redis"]
	},
	"k8s.container.restart_count": {
		"description": "Number of times the container was restarted. This attribute can be used to identify a particular container (running or stopped) within a container spec.\n",
		"type": "number",
		"stability": "development"
	},
	"k8s.container.status.last_terminated_reason": {
		"description": "Last terminated reason of the Container.\n",
		"type": "string",
		"stability": "development",
		"examples": ["Evicted", "Error"]
	},
	"k8s.replicaset.uid": {
		"description": "The UID of the ReplicaSet.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.replicaset.name": {
		"description": "The name of the ReplicaSet.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.replicaset.label": {
		"description": "The label placed on the ReplicaSet, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `app` with value `guestbook` SHOULD be recorded\n  as the `k8s.replicaset.label.app` attribute with value `\"guestbook\"`.\n- A label `injected` with empty string value SHOULD be recorded as\n  the `k8s.replicaset.label.injected` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["guestbook", ""]
	},
	"k8s.replicaset.annotation": {
		"description": "The annotation placed on the ReplicaSet, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `replicas` with value `0` SHOULD be recorded\n  as the `k8s.replicaset.annotation.replicas` attribute with value `\"0\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.replicaset.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["0", ""]
	},
	"k8s.replicationcontroller.uid": {
		"description": "The UID of the replication controller.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.replicationcontroller.name": {
		"description": "The name of the replication controller.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.resourcequota.uid": {
		"description": "The UID of the resource quota.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.resourcequota.name": {
		"description": "The name of the resource quota.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.deployment.uid": {
		"description": "The UID of the Deployment.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.deployment.name": {
		"description": "The name of the Deployment.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.deployment.label": {
		"description": "The label placed on the Deployment, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `replicas` with value `0` SHOULD be recorded\n  as the `k8s.deployment.label.app` attribute with value `\"guestbook\"`.\n- A label `injected` with empty string value SHOULD be recorded as\n  the `k8s.deployment.label.injected` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["guestbook", ""]
	},
	"k8s.deployment.annotation": {
		"description": "The annotation placed on the Deployment, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `replicas` with value `1` SHOULD be recorded\n  as the `k8s.deployment.annotation.replicas` attribute with value `\"1\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.deployment.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["1", ""]
	},
	"k8s.statefulset.uid": {
		"description": "The UID of the StatefulSet.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.statefulset.name": {
		"description": "The name of the StatefulSet.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.statefulset.label": {
		"description": "The label placed on the StatefulSet, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `replicas` with value `0` SHOULD be recorded\n  as the `k8s.statefulset.label.app` attribute with value `\"guestbook\"`.\n- A label `injected` with empty string value SHOULD be recorded as\n  the `k8s.statefulset.label.injected` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["guestbook", ""]
	},
	"k8s.statefulset.annotation": {
		"description": "The annotation placed on the StatefulSet, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `replicas` with value `1` SHOULD be recorded\n  as the `k8s.statefulset.annotation.replicas` attribute with value `\"1\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.statefulset.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["1", ""]
	},
	"k8s.daemonset.uid": {
		"description": "The UID of the DaemonSet.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.daemonset.name": {
		"description": "The name of the DaemonSet.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.daemonset.label": {
		"description": "The label placed on the DaemonSet, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `app` with value `guestbook` SHOULD be recorded\n  as the `k8s.daemonset.label.app` attribute with value `\"guestbook\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.daemonset.label.injected` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["guestbook", ""]
	},
	"k8s.daemonset.annotation": {
		"description": "The annotation placed on the DaemonSet, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `replicas` with value `1` SHOULD be recorded\n  as the `k8s.daemonset.annotation.replicas` attribute with value `\"1\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.daemonset.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["1", ""]
	},
	"k8s.hpa.uid": {
		"description": "The UID of the horizontal pod autoscaler.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.hpa.name": {
		"description": "The name of the horizontal pod autoscaler.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.hpa.scaletargetref.kind": {
		"description": "The kind of the target resource to scale for the HorizontalPodAutoscaler.\n",
		"type": "string",
		"note": "This maps to the `kind` field in the `scaleTargetRef` of the HPA spec.\n",
		"stability": "development",
		"examples": ["Deployment", "StatefulSet"]
	},
	"k8s.hpa.scaletargetref.name": {
		"description": "The name of the target resource to scale for the HorizontalPodAutoscaler.\n",
		"type": "string",
		"note": "This maps to the `name` field in the `scaleTargetRef` of the HPA spec.\n",
		"stability": "development",
		"examples": ["my-deployment", "my-statefulset"]
	},
	"k8s.hpa.scaletargetref.api_version": {
		"description": "The API version of the target resource to scale for the HorizontalPodAutoscaler.\n",
		"type": "string",
		"note": "This maps to the `apiVersion` field in the `scaleTargetRef` of the HPA spec.\n",
		"stability": "development",
		"examples": ["apps/v1", "autoscaling/v2"]
	},
	"k8s.hpa.metric.type": {
		"description": "The type of metric source for the horizontal pod autoscaler.\n",
		"type": "string",
		"note": "This attribute reflects the `type` field of spec.metrics[] in the HPA.\n",
		"stability": "development",
		"examples": ["Resource", "ContainerResource"]
	},
	"k8s.job.uid": {
		"description": "The UID of the Job.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.job.name": {
		"description": "The name of the Job.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.job.label": {
		"description": "The label placed on the Job, the `<key>` being the label name, the value being the label value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `jobtype` with value `ci` SHOULD be recorded\n  as the `k8s.job.label.jobtype` attribute with value `\"ci\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.job.label.automated` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["ci", ""]
	},
	"k8s.job.annotation": {
		"description": "The annotation placed on the Job, the `<key>` being the annotation name, the value being the annotation value, even if the value is empty.\n",
		"type": "string",
		"note": "\nExamples:\n\n- A label `number` with value `1` SHOULD be recorded\n  as the `k8s.job.annotation.number` attribute with value `\"1\"`.\n- A label `data` with empty string value SHOULD be recorded as\n  the `k8s.job.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["1", ""]
	},
	"k8s.cronjob.uid": {
		"description": "The UID of the CronJob.\n",
		"type": "string",
		"stability": "development",
		"examples": ["275ecb36-5aa8-4c2a-9c47-d8bb681b9aff"]
	},
	"k8s.cronjob.name": {
		"description": "The name of the CronJob.\n",
		"type": "string",
		"stability": "development",
		"examples": ["opentelemetry"]
	},
	"k8s.cronjob.label": {
		"description": "The label placed on the CronJob, the `<key>` being the label name, the value being the label value.\n",
		"type": "string",
		"note": "Examples:\n\n- A label `type` with value `weekly` SHOULD be recorded as the\n  `k8s.cronjob.label.type` attribute with value `\"weekly\"`.\n- A label `automated` with empty string value SHOULD be recorded as\n  the `k8s.cronjob.label.automated` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["weekly", ""]
	},
	"k8s.cronjob.annotation": {
		"description": "The cronjob annotation placed on the CronJob, the `<key>` being the annotation name, the value being the annotation value.\n",
		"type": "string",
		"note": "Examples:\n\n- An annotation `retries` with value `4` SHOULD be recorded as the\n  `k8s.cronjob.annotation.retries` attribute with value `\"4\"`.\n- An annotation `data` with empty string value SHOULD be recorded as\n  the `k8s.cronjob.annotation.data` attribute with value `\"\"`.\n",
		"stability": "development",
		"examples": ["4", ""]
	},
	"k8s.volume.name": {
		"description": "The name of the K8s volume.\n",
		"type": "string",
		"stability": "development",
		"examples": ["volume0"]
	},
	"k8s.volume.type": {
		"description": "The type of the K8s volume.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"persistentVolumeClaim",
			"configMap",
			"downwardAPI",
			"emptyDir",
			"secret",
			"local"
		]
	},
	"k8s.namespace.phase": {
		"description": "The phase of the K8s namespace.\n",
		"type": "string",
		"note": "This attribute aligns with the `phase` field of the\n[K8s NamespaceStatus](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#namespacestatus-v1-core)\n",
		"stability": "development",
		"examples": ["active", "terminating"]
	},
	"k8s.node.condition.type": {
		"description": "The condition type of a K8s Node.\n",
		"type": "string",
		"note": "K8s Node conditions as described\nby [K8s documentation](https://v1-32.docs.kubernetes.io/docs/reference/node/node-status/#condition).\n\nThis attribute aligns with the `type` field of the\n[NodeCondition](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#nodecondition-v1-core)\n\nThe set of possible values is not limited to those listed here. Managed Kubernetes environments,\nor custom controllers MAY introduce additional node condition types.\nWhen this occurs, the exact value as reported by the Kubernetes API SHOULD be used.\n",
		"stability": "development",
		"examples": [
			"Ready",
			"DiskPressure",
			"MemoryPressure",
			"PIDPressure",
			"NetworkUnavailable"
		]
	},
	"k8s.node.condition.status": {
		"description": "The status of the condition, one of True, False, Unknown.\n",
		"type": "string",
		"note": "This attribute aligns with the `status` field of the\n[NodeCondition](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#nodecondition-v1-core)\n",
		"stability": "development",
		"examples": [
			"true",
			"false",
			"unknown"
		]
	},
	"k8s.container.status.state": {
		"description": "The state of the container. [K8s ContainerState](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#containerstate-v1-core)\n",
		"type": "string",
		"stability": "experimental",
		"examples": [
			"terminated",
			"running",
			"waiting"
		]
	},
	"k8s.container.status.reason": {
		"description": "The reason for the container state. Corresponds to the `reason` field of the: [K8s ContainerStateWaiting](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#containerstatewaiting-v1-core) or [K8s ContainerStateTerminated](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#containerstateterminated-v1-core)\n",
		"type": "string",
		"stability": "experimental",
		"examples": [
			"ContainerCreating",
			"CrashLoopBackOff",
			"CreateContainerConfigError",
			"ErrImagePull",
			"ImagePullBackOff",
			"OOMKilled",
			"Completed",
			"Error",
			"ContainerCannotRun"
		]
	},
	"k8s.hugepage.size": {
		"description": "The size (identifier) of the K8s huge page.\n",
		"type": "string",
		"stability": "development",
		"examples": ["2Mi"]
	},
	"k8s.storageclass.name": {
		"description": "The name of K8s [StorageClass](https://kubernetes.io/docs/reference/generated/kubernetes-api/v1.30/#storageclass-v1-storage-k8s-io) object.\n",
		"type": "string",
		"stability": "development",
		"examples": ["gold.storageclass.storage.k8s.io"]
	},
	"k8s.resourcequota.resource_name": {
		"description": "The name of the K8s resource a resource quota defines.\n",
		"type": "string",
		"note": "The value for this attribute can be either the full `count/<resource>[.<group>]` string (e.g., count/deployments.apps, count/pods), or, for certain core Kubernetes resources, just the resource name (e.g., pods, services, configmaps). Both forms are supported by Kubernetes for object count quotas. See [Kubernetes Resource Quotas documentation](https://kubernetes.io/docs/concepts/policy/resource-quotas/#object-count-quota) for more details.\n",
		"stability": "development",
		"examples": ["count/replicationcontrollers"]
	}
};
var k8s_default = {
	namespace: namespace$30,
	description: description$30,
	attributes: attributes$30
};

//#endregion
//#region src/internal/agents/tools/data/linux.json
var namespace$29 = "linux";
var description$29 = "Describes Linux Memory attributes";
var attributes$29 = { "linux.memory.slab.state": {
	"description": "The Linux Slab memory state",
	"type": "string",
	"stability": "development",
	"examples": ["reclaimable", "unreclaimable"]
} };
var linux_default = {
	namespace: namespace$29,
	description: description$29,
	attributes: attributes$29
};

//#endregion
//#region src/internal/agents/tools/data/log.json
var namespace$28 = "log";
var description$28 = "This document defines log attributes\n";
var attributes$28 = { "log.iostream": {
	"description": "The stream associated with the log. See below for a list of well-known values.\n",
	"type": "string",
	"stability": "development",
	"examples": ["stdout", "stderr"]
} };
var log_default = {
	namespace: namespace$28,
	description: description$28,
	attributes: attributes$28
};

//#endregion
//#region src/internal/agents/tools/data/mcp.json
var namespace$27 = "mcp";
var description$27 = "Model Context Protocol semantic convention attributes";
var attributes$27 = {
	"mcp.method.name": {
		"description": "The name of the request or notification method.",
		"type": "string",
		"examples": [
			"initialize",
			"tools/call",
			"resources/read"
		]
	},
	"mcp.protocol.version": {
		"description": "The version of the Model Context Protocol used.",
		"type": "string",
		"examples": ["2025-06-18"]
	},
	"mcp.resource.uri": {
		"description": "The value of the resource URI.",
		"type": "string",
		"examples": ["postgres://database/customers/schema", "file:///home/user/documents/report.pdf"]
	},
	"mcp.session.id": {
		"description": "Identifies an MCP session.",
		"type": "string",
		"examples": ["191c4850af6c49e08843a3f6c80e5046"]
	}
};
var mcp_default = {
	namespace: namespace$27,
	description: description$27,
	attributes: attributes$27
};

//#endregion
//#region src/internal/agents/tools/data/messaging.json
var namespace$26 = "messaging";
var description$26 = "Attributes describing telemetry around messaging systems and messaging activities.";
var attributes$26 = {
	"messaging.batch.message_count": {
		"description": "The number of messages sent, received, or processed in the scope of the batching operation.",
		"type": "number",
		"note": "Instrumentations SHOULD NOT set `messaging.batch.message_count` on spans that operate with a single message. When a messaging client library supports both batch and single-message API for the same operation, instrumentations SHOULD use `messaging.batch.message_count` for batching APIs and SHOULD NOT use it for single-message APIs.\n",
		"stability": "development",
		"examples": [
			"0",
			"1",
			"2"
		]
	},
	"messaging.client.id": {
		"description": "A unique identifier for the client that consumes or produces a message.\n",
		"type": "string",
		"stability": "development",
		"examples": ["client-5", "myhost@8742@s8083jm"]
	},
	"messaging.consumer.group.name": {
		"description": "The name of the consumer group with which a consumer is associated.\n",
		"type": "string",
		"note": "Semantic conventions for individual messaging systems SHOULD document whether `messaging.consumer.group.name` is applicable and what it means in the context of that system.\n",
		"stability": "development",
		"examples": ["my-group", "indexer"]
	},
	"messaging.destination.name": {
		"description": "The message destination name",
		"type": "string",
		"note": "Destination name SHOULD uniquely identify a specific queue, topic or other entity within the broker. If\nthe broker doesn't have such notion, the destination name SHOULD uniquely identify the broker.\n",
		"stability": "development",
		"examples": ["MyQueue", "MyTopic"]
	},
	"messaging.destination.subscription.name": {
		"description": "The name of the destination subscription from which a message is consumed.",
		"type": "string",
		"note": "Semantic conventions for individual messaging systems SHOULD document whether `messaging.destination.subscription.name` is applicable and what it means in the context of that system.\n",
		"stability": "development",
		"examples": ["subscription-a"]
	},
	"messaging.destination.template": {
		"description": "Low cardinality representation of the messaging destination name",
		"type": "string",
		"note": "Destination names could be constructed from templates. An example would be a destination name involving a user name or product id. Although the destination name in this case is of high cardinality, the underlying template is of low cardinality and can be effectively used for grouping and aggregation.\n",
		"stability": "development",
		"examples": ["/customers/{customerId}"]
	},
	"messaging.destination.anonymous": {
		"description": "A boolean that is true if the message destination is anonymous (could be unnamed or have auto-generated name).",
		"type": "boolean",
		"stability": "development"
	},
	"messaging.destination.temporary": {
		"description": "A boolean that is true if the message destination is temporary and might not exist anymore after messages are processed.",
		"type": "boolean",
		"stability": "development"
	},
	"messaging.destination.partition.id": {
		"description": "The identifier of the partition messages are sent to or received from, unique within the `messaging.destination.name`.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1"]
	},
	"messaging.message.conversation_id": {
		"description": "The conversation ID identifying the conversation to which the message belongs, represented as a string. Sometimes called \"Correlation ID\".\n",
		"type": "string",
		"stability": "development",
		"examples": ["MyConversationId"]
	},
	"messaging.message.envelope.size": {
		"description": "The size of the message body and metadata in bytes.\n",
		"type": "number",
		"note": "This can refer to both the compressed or uncompressed size. If both sizes are known, the uncompressed\nsize should be used.\n",
		"stability": "development",
		"examples": ["2738"]
	},
	"messaging.message.id": {
		"description": "A value used by the messaging system as an identifier for the message, represented as a string.",
		"type": "string",
		"stability": "development",
		"examples": ["452a7c7c7c7048c2f887f61572b18fc2"]
	},
	"messaging.message.body.size": {
		"description": "The size of the message body in bytes.\n",
		"type": "number",
		"note": "This can refer to both the compressed or uncompressed body size. If both sizes are known, the uncompressed\nbody size should be used.\n",
		"stability": "development",
		"examples": ["1439"]
	},
	"messaging.operation.type": {
		"description": "A string identifying the type of the messaging operation.\n",
		"type": "string",
		"note": "If a custom value is used, it MUST be of low cardinality.",
		"stability": "development",
		"examples": [
			"create",
			"send",
			"receive",
			"process",
			"settle",
			"deliver",
			"publish"
		]
	},
	"messaging.operation.name": {
		"description": "The system-specific name of the messaging operation.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"ack",
			"nack",
			"send"
		]
	},
	"messaging.system": {
		"description": "The messaging system as identified by the client instrumentation.",
		"type": "string",
		"note": "The actual messaging system may differ from the one known by the client. For example, when using Kafka client libraries to communicate with Azure Event Hubs, the `messaging.system` is set to `kafka` based on the instrumentation's best knowledge.\n",
		"stability": "development",
		"examples": [
			"activemq",
			"aws_sqs",
			"eventgrid",
			"eventhubs",
			"servicebus",
			"gcp_pubsub",
			"jms",
			"kafka",
			"rabbitmq",
			"rocketmq",
			"pulsar"
		]
	}
};
var messaging_default = {
	namespace: namespace$26,
	description: description$26,
	attributes: attributes$26
};

//#endregion
//#region src/internal/agents/tools/data/network.json
var namespace$25 = "network";
var description$25 = "These attributes may be used for any network related operation.\n";
var attributes$25 = {
	"network.carrier.icc": {
		"description": "The ISO 3166-1 alpha-2 2-character country code associated with the mobile carrier network.",
		"type": "string",
		"stability": "development",
		"examples": ["DE"]
	},
	"network.carrier.mcc": {
		"description": "The mobile carrier country code.",
		"type": "string",
		"stability": "development",
		"examples": ["310"]
	},
	"network.carrier.mnc": {
		"description": "The mobile carrier network code.",
		"type": "string",
		"stability": "development",
		"examples": ["001"]
	},
	"network.carrier.name": {
		"description": "The name of the mobile carrier.",
		"type": "string",
		"stability": "development",
		"examples": ["sprint"]
	},
	"network.connection.subtype": {
		"description": "This describes more details regarding the connection.type. It may be the type of cell technology connection, but it could be used for describing details about a wifi connection.",
		"type": "string",
		"stability": "development",
		"examples": [
			"gprs",
			"edge",
			"umts",
			"cdma",
			"evdo_0",
			"evdo_a",
			"cdma2000_1xrtt",
			"hsdpa",
			"hsupa",
			"hspa",
			"iden",
			"evdo_b",
			"lte",
			"ehrpd",
			"hspap",
			"gsm",
			"td_scdma",
			"iwlan",
			"nr",
			"nrnsa",
			"lte_ca"
		]
	},
	"network.connection.type": {
		"description": "The internet connection type.",
		"type": "string",
		"stability": "development",
		"examples": [
			"wifi",
			"wired",
			"cell",
			"unavailable",
			"unknown"
		]
	},
	"network.local.address": {
		"description": "Local address of the network connection - IP address or Unix domain socket name.",
		"type": "string",
		"stability": "stable",
		"examples": ["10.1.2.80", "/tmp/my.sock"]
	},
	"network.local.port": {
		"description": "Local port number of the network connection.",
		"type": "number",
		"stability": "stable",
		"examples": ["65123"]
	},
	"network.peer.address": {
		"description": "Peer address of the network connection - IP address or Unix domain socket name.",
		"type": "string",
		"stability": "stable",
		"examples": ["10.1.2.80", "/tmp/my.sock"]
	},
	"network.peer.port": {
		"description": "Peer port number of the network connection.",
		"type": "number",
		"stability": "stable",
		"examples": ["65123"]
	},
	"network.protocol.name": {
		"description": "[OSI application layer](https://wikipedia.org/wiki/Application_layer) or non-OSI equivalent.",
		"type": "string",
		"note": "The value SHOULD be normalized to lowercase.",
		"stability": "stable",
		"examples": [
			"amqp",
			"http",
			"mqtt"
		]
	},
	"network.protocol.version": {
		"description": "The actual version of the protocol used for network communication.",
		"type": "string",
		"note": "If protocol version is subject to negotiation (for example using [ALPN](https://www.rfc-editor.org/rfc/rfc7301.html)), this attribute SHOULD be set to the negotiated version. If the actual protocol version is not known, this attribute SHOULD NOT be set.\n",
		"stability": "stable",
		"examples": ["1.1", "2"]
	},
	"network.transport": {
		"description": "[OSI transport layer](https://wikipedia.org/wiki/Transport_layer) or [inter-process communication method](https://wikipedia.org/wiki/Inter-process_communication).\n",
		"type": "string",
		"note": "The value SHOULD be normalized to lowercase.\n\nConsider always setting the transport when setting a port number, since\na port number is ambiguous without knowing the transport. For example\ndifferent processes could be listening on TCP port 12345 and UDP port 12345.\n",
		"stability": "stable",
		"examples": [
			"tcp",
			"udp",
			"pipe",
			"unix",
			"quic"
		]
	},
	"network.type": {
		"description": "[OSI network layer](https://wikipedia.org/wiki/Network_layer) or non-OSI equivalent.",
		"type": "string",
		"note": "The value SHOULD be normalized to lowercase.",
		"stability": "stable",
		"examples": ["ipv4", "ipv6"]
	},
	"network.io.direction": {
		"description": "The network IO operation direction.",
		"type": "string",
		"stability": "development",
		"examples": ["transmit", "receive"]
	},
	"network.interface.name": {
		"description": "The network interface name.",
		"type": "string",
		"stability": "development",
		"examples": ["lo", "eth0"]
	},
	"network.connection.state": {
		"description": "The state of network connection",
		"type": "string",
		"note": "Connection states are defined as part of the [rfc9293](https://datatracker.ietf.org/doc/html/rfc9293#section-3.3.2)",
		"stability": "development",
		"examples": [
			"closed",
			"close_wait",
			"closing",
			"established",
			"fin_wait_1",
			"fin_wait_2",
			"last_ack",
			"listen",
			"syn_received",
			"syn_sent",
			"time_wait"
		]
	}
};
var network_default = {
	namespace: namespace$25,
	description: description$25,
	attributes: attributes$25
};

//#endregion
//#region src/internal/agents/tools/data/nodejs.json
var namespace$24 = "nodejs";
var description$24 = "Describes Node.js related attributes.";
var attributes$24 = { "nodejs.eventloop.state": {
	"description": "The state of event loop time.",
	"type": "string",
	"stability": "development",
	"examples": ["active", "idle"]
} };
var nodejs_default = {
	namespace: namespace$24,
	description: description$24,
	attributes: attributes$24
};

//#endregion
//#region src/internal/agents/tools/data/oci.json
var namespace$23 = "oci";
var description$23 = "An OCI image manifest.\n";
var attributes$23 = { "oci.manifest.digest": {
	"description": "The digest of the OCI image manifest. For container images specifically is the digest by which the container image is known.\n",
	"type": "string",
	"note": "Follows [OCI Image Manifest Specification](https://github.com/opencontainers/image-spec/blob/main/manifest.md), and specifically the [Digest property](https://github.com/opencontainers/image-spec/blob/main/descriptor.md#digests).\nAn example can be found in [Example Image Manifest](https://github.com/opencontainers/image-spec/blob/main/manifest.md#example-image-manifest).\n",
	"stability": "development",
	"examples": ["sha256:e4ca62c0d62f3e886e684806dfe9d4e0cda60d54986898173c1083856cfda0f4"]
} };
var oci_default = {
	namespace: namespace$23,
	description: description$23,
	attributes: attributes$23
};

//#endregion
//#region src/internal/agents/tools/data/opentracing.json
var namespace$22 = "opentracing";
var description$22 = "Attributes used by the OpenTracing Shim layer.";
var attributes$22 = { "opentracing.ref_type": {
	"description": "Parent-child Reference type",
	"type": "string",
	"note": "The causal relationship between a child Span and a parent Span.\n",
	"stability": "development",
	"examples": ["child_of", "follows_from"]
} };
var opentracing_default = {
	namespace: namespace$22,
	description: description$22,
	attributes: attributes$22
};

//#endregion
//#region src/internal/agents/tools/data/os.json
var namespace$21 = "os";
var description$21 = "The operating system (OS) on which the process represented by this resource is running.\n";
var attributes$21 = {
	"os.type": {
		"description": "The operating system type.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"windows",
			"linux",
			"darwin",
			"freebsd",
			"netbsd",
			"openbsd",
			"dragonflybsd",
			"hpux",
			"aix",
			"solaris",
			"z_os",
			"zos"
		]
	},
	"os.description": {
		"description": "Human readable (not intended to be parsed) OS version information, like e.g. reported by `ver` or `lsb_release -a` commands.\n",
		"type": "string",
		"stability": "development",
		"examples": ["Microsoft Windows [Version 10.0.18363.778]", "Ubuntu 18.04.1 LTS"]
	},
	"os.name": {
		"description": "Human readable operating system name.",
		"type": "string",
		"stability": "development",
		"examples": [
			"iOS",
			"Android",
			"Ubuntu"
		]
	},
	"os.version": {
		"description": "The version string of the operating system as defined in [Version Attributes](/docs/resource/README.md#version-attributes).\n",
		"type": "string",
		"stability": "development",
		"examples": ["14.2.1", "18.04.1"]
	},
	"os.build_id": {
		"description": "Unique identifier for a particular build or compilation of the operating system.",
		"type": "string",
		"stability": "development",
		"examples": [
			"TQ3C.230805.001.B2",
			"20E247",
			"22621"
		]
	}
};
var os_default = {
	namespace: namespace$21,
	description: description$21,
	attributes: attributes$21
};

//#endregion
//#region src/internal/agents/tools/data/otel.json
var namespace$20 = "otel";
var description$20 = "Attributes reserved for OpenTelemetry";
var attributes$20 = {
	"otel.status_code": {
		"description": "Name of the code, either \"OK\" or \"ERROR\". MUST NOT be set if the status code is UNSET.",
		"type": "string",
		"stability": "stable",
		"examples": ["OK", "ERROR"]
	},
	"otel.status_description": {
		"description": "Description of the Status if it has a value, otherwise not set.",
		"type": "string",
		"stability": "stable",
		"examples": ["resource not found"]
	},
	"otel.span.sampling_result": {
		"description": "The result value of the sampler for this span",
		"type": "string",
		"stability": "development",
		"examples": [
			"DROP",
			"RECORD_ONLY",
			"RECORD_AND_SAMPLE"
		]
	},
	"otel.span.parent.origin": {
		"description": "Determines whether the span has a parent span, and if so, [whether it is a remote parent](https://opentelemetry.io/docs/specs/otel/trace/api/#isremote)",
		"type": "string",
		"stability": "development",
		"examples": [
			"none",
			"local",
			"remote"
		]
	}
};
var otel_default = {
	namespace: namespace$20,
	description: description$20,
	attributes: attributes$20
};

//#endregion
//#region src/internal/agents/tools/data/peer.json
var namespace$19 = "peer";
var description$19 = "Operations that access some remote service.\n";
var attributes$19 = { "peer.service": {
	"description": "The [`service.name`](/docs/resource/README.md#service) of the remote service. SHOULD be equal to the actual `service.name` resource attribute of the remote service if any.\n",
	"type": "string",
	"stability": "development",
	"examples": ["AuthTokenCache"]
} };
var peer_default = {
	namespace: namespace$19,
	description: description$19,
	attributes: attributes$19
};

//#endregion
//#region src/internal/agents/tools/data/process.json
var namespace$18 = "process";
var description$18 = "An operating system process.\n";
var attributes$18 = {
	"process.pid": {
		"description": "Process identifier (PID).\n",
		"type": "number",
		"stability": "development",
		"examples": ["1234"]
	},
	"process.parent_pid": {
		"description": "Parent Process identifier (PPID).\n",
		"type": "number",
		"stability": "development",
		"examples": ["111"]
	},
	"process.vpid": {
		"description": "Virtual process identifier.\n",
		"type": "number",
		"note": "The process ID within a PID namespace. This is not necessarily unique across all processes on the host but it is unique within the process namespace that the process exists within.\n",
		"stability": "development",
		"examples": ["12"]
	},
	"process.session_leader.pid": {
		"description": "The PID of the process's session leader. This is also the session ID (SID) of the process.\n",
		"type": "number",
		"stability": "development",
		"examples": ["14"]
	},
	"process.group_leader.pid": {
		"description": "The PID of the process's group leader. This is also the process group ID (PGID) of the process.\n",
		"type": "number",
		"stability": "development",
		"examples": ["23"]
	},
	"process.executable.build_id.gnu": {
		"description": "The GNU build ID as found in the `.note.gnu.build-id` ELF section (hex string).\n",
		"type": "string",
		"stability": "development",
		"examples": ["c89b11207f6479603b0d49bf291c092c2b719293"]
	},
	"process.executable.build_id.go": {
		"description": "The Go build ID as retrieved by `go tool buildid <go executable>`.\n",
		"type": "string",
		"stability": "development",
		"examples": ["foh3mEXu7BLZjsN9pOwG/kATcXlYVCDEFouRMQed_/WwRFB1hPo9LBkekthSPG/x8hMC8emW2cCjXD0_1aY"]
	},
	"process.executable.build_id.htlhash": {
		"description": "Profiling specific build ID for executables. See the OTel specification for Profiles for more information.\n",
		"type": "string",
		"stability": "development",
		"examples": ["600DCAFE4A110000F2BF38C493F5FB92"]
	},
	"process.executable.name": {
		"description": "The name of the process executable. On Linux based systems, this SHOULD be set to the base name of the target of `/proc/[pid]/exe`. On Windows, this SHOULD be set to the base name of `GetProcessImageFileNameW`.\n",
		"type": "string",
		"stability": "development",
		"examples": ["otelcol"]
	},
	"process.executable.path": {
		"description": "The full path to the process executable. On Linux based systems, can be set to the target of `proc/[pid]/exe`. On Windows, can be set to the result of `GetProcessImageFileNameW`.\n",
		"type": "string",
		"stability": "development",
		"examples": ["/usr/bin/cmd/otelcol"]
	},
	"process.command": {
		"description": "The command used to launch the process (i.e. the command name). On Linux based systems, can be set to the zeroth string in `proc/[pid]/cmdline`. On Windows, can be set to the first parameter extracted from `GetCommandLineW`.\n",
		"type": "string",
		"stability": "development",
		"examples": ["cmd/otelcol"]
	},
	"process.command_line": {
		"description": "The full command used to launch the process as a single string representing the full command. On Windows, can be set to the result of `GetCommandLineW`. Do not set this if you have to assemble it just for monitoring; use `process.command_args` instead. SHOULD NOT be collected by default unless there is sanitization that excludes sensitive data.\n",
		"type": "string",
		"stability": "development",
		"examples": ["C:\\cmd\\otecol --config=\"my directory\\config.yaml\""]
	},
	"process.command_args": {
		"description": "All the command arguments (including the command/executable itself) as received by the process. On Linux-based systems (and some other Unixoid systems supporting procfs), can be set according to the list of null-delimited strings extracted from `proc/[pid]/cmdline`. For libc-based executables, this would be the full argv vector passed to `main`. SHOULD NOT be collected by default unless there is sanitization that excludes sensitive data.\n",
		"type": "string",
		"stability": "development",
		"examples": ["[\"cmd/otecol\",\"--config=config.yaml\"]"]
	},
	"process.args_count": {
		"description": "Length of the process.command_args array\n",
		"type": "number",
		"note": "This field can be useful for querying or performing bucket analysis on how many arguments were provided to start a process. More arguments may be an indication of suspicious activity.\n",
		"stability": "development",
		"examples": ["4"]
	},
	"process.owner": {
		"description": "The username of the user that owns the process.\n",
		"type": "string",
		"stability": "development",
		"examples": ["root"]
	},
	"process.user.id": {
		"description": "The effective user ID (EUID) of the process.\n",
		"type": "number",
		"stability": "development",
		"examples": ["1001"]
	},
	"process.user.name": {
		"description": "The username of the effective user of the process.\n",
		"type": "string",
		"stability": "development",
		"examples": ["root"]
	},
	"process.real_user.id": {
		"description": "The real user ID (RUID) of the process.\n",
		"type": "number",
		"stability": "development",
		"examples": ["1000"]
	},
	"process.real_user.name": {
		"description": "The username of the real user of the process.\n",
		"type": "string",
		"stability": "development",
		"examples": ["operator"]
	},
	"process.saved_user.id": {
		"description": "The saved user ID (SUID) of the process.\n",
		"type": "number",
		"stability": "development",
		"examples": ["1002"]
	},
	"process.saved_user.name": {
		"description": "The username of the saved user.\n",
		"type": "string",
		"stability": "development",
		"examples": ["operator"]
	},
	"process.runtime.name": {
		"description": "The name of the runtime of this process.\n",
		"type": "string",
		"stability": "development",
		"examples": ["OpenJDK Runtime Environment"]
	},
	"process.runtime.version": {
		"description": "The version of the runtime of this process, as returned by the runtime without modification.\n",
		"type": "string",
		"stability": "development",
		"examples": ["14.0.2"]
	},
	"process.runtime.description": {
		"description": "An additional description about the runtime of the process, for example a specific vendor customization of the runtime environment.\n",
		"type": "string",
		"stability": "development",
		"examples": ["Eclipse OpenJ9 Eclipse OpenJ9 VM openj9-0.21.0"]
	},
	"process.title": {
		"description": "Process title (proctitle)\n",
		"type": "string",
		"note": "In many Unix-like systems, process title (proctitle), is the string that represents the name or command line of a running process, displayed by system monitoring tools like ps, top, and htop.\n",
		"stability": "development",
		"examples": [
			"cat /etc/hostname",
			"xfce4-session",
			"bash"
		]
	},
	"process.creation.time": {
		"description": "The date and time the process was created, in ISO 8601 format.\n",
		"type": "string",
		"stability": "development",
		"examples": ["2023-11-21T09:25:34.853Z"]
	},
	"process.exit.time": {
		"description": "The date and time the process exited, in ISO 8601 format.\n",
		"type": "string",
		"stability": "development",
		"examples": ["2023-11-21T09:26:12.315Z"]
	},
	"process.exit.code": {
		"description": "The exit code of the process.\n",
		"type": "number",
		"stability": "development",
		"examples": ["127"]
	},
	"process.interactive": {
		"description": "Whether the process is connected to an interactive shell.\n",
		"type": "boolean",
		"stability": "development"
	},
	"process.working_directory": {
		"description": "The working directory of the process.\n",
		"type": "string",
		"stability": "development",
		"examples": ["/root"]
	},
	"process.context_switch_type": {
		"description": "Specifies whether the context switches for this data point were voluntary or involuntary.",
		"type": "string",
		"stability": "development",
		"examples": ["voluntary", "involuntary"]
	},
	"process.paging.fault_type": {
		"description": "The type of page fault for this data point. Type `major` is for major/hard page faults, and `minor` is for minor/soft page faults.\n",
		"type": "string",
		"stability": "development",
		"examples": ["major", "minor"]
	},
	"process.environment_variable": {
		"description": "Process environment variables, `<key>` being the environment variable name, the value being the environment variable value.\n",
		"type": "string",
		"note": "Examples:\n\n- an environment variable `USER` with value `\"ubuntu\"` SHOULD be recorded\nas the `process.environment_variable.USER` attribute with value `\"ubuntu\"`.\n\n- an environment variable `PATH` with value `\"/usr/local/bin:/usr/bin\"`\nSHOULD be recorded as the `process.environment_variable.PATH` attribute\nwith value `\"/usr/local/bin:/usr/bin\"`.\n",
		"stability": "development",
		"examples": ["ubuntu", "/usr/local/bin:/usr/bin"]
	}
};
var process_default = {
	namespace: namespace$18,
	description: description$18,
	attributes: attributes$18
};

//#endregion
//#region src/internal/agents/tools/data/profile.json
var namespace$17 = "profile";
var description$17 = "Describes the origin of a single frame in a Profile.\n";
var attributes$17 = { "profile.frame.type": {
	"description": "Describes the interpreter or compiler of a single frame.\n",
	"type": "string",
	"stability": "development",
	"examples": [
		"dotnet",
		"jvm",
		"kernel",
		"native",
		"perl",
		"php",
		"cpython",
		"ruby",
		"v8js",
		"beam",
		"go",
		"rust"
	]
} };
var profile_default = {
	namespace: namespace$17,
	description: description$17,
	attributes: attributes$17
};

//#endregion
//#region src/internal/agents/tools/data/rpc.json
var namespace$16 = "rpc";
var description$16 = "This document defines attributes for remote procedure calls.";
var attributes$16 = {
	"rpc.connect_rpc.error_code": {
		"description": "The [error codes](https://connectrpc.com//docs/protocol/#error-codes) of the Connect request. Error codes are always string values.",
		"type": "string",
		"stability": "development",
		"examples": [
			"cancelled",
			"unknown",
			"invalid_argument",
			"deadline_exceeded",
			"not_found",
			"already_exists",
			"permission_denied",
			"resource_exhausted",
			"failed_precondition",
			"aborted",
			"out_of_range",
			"unimplemented",
			"internal",
			"unavailable",
			"data_loss",
			"unauthenticated"
		]
	},
	"rpc.connect_rpc.request.metadata": {
		"description": "Connect request metadata, `<key>` being the normalized Connect Metadata key (lowercase), the value being the metadata values.\n",
		"type": "string",
		"note": "Instrumentations SHOULD require an explicit configuration of which metadata values are to be captured.\nIncluding all request metadata values can be a security risk - explicit configuration helps avoid leaking sensitive information.\n\nFor example, a property `my-custom-key` with value `[\"1.2.3.4\", \"1.2.3.5\"]` SHOULD be recorded as\nthe `rpc.connect_rpc.request.metadata.my-custom-key` attribute with value `[\"1.2.3.4\", \"1.2.3.5\"]`\n",
		"stability": "development",
		"examples": ["[\"1.2.3.4\",\"1.2.3.5\"]"]
	},
	"rpc.connect_rpc.response.metadata": {
		"description": "Connect response metadata, `<key>` being the normalized Connect Metadata key (lowercase), the value being the metadata values.\n",
		"type": "string",
		"note": "Instrumentations SHOULD require an explicit configuration of which metadata values are to be captured.\nIncluding all response metadata values can be a security risk - explicit configuration helps avoid leaking sensitive information.\n\nFor example, a property `my-custom-key` with value `\"attribute_value\"` SHOULD be recorded as\nthe `rpc.connect_rpc.response.metadata.my-custom-key` attribute with value `[\"attribute_value\"]`\n",
		"stability": "development",
		"examples": ["[\"attribute_value\"]"]
	},
	"rpc.grpc.status_code": {
		"description": "The [numeric status code](https://github.com/grpc/grpc/blob/v1.33.2/doc/statuscodes.md) of the gRPC request.",
		"type": "string",
		"stability": "development",
		"examples": [
			"0",
			"1",
			"2",
			"3",
			"4",
			"5",
			"6",
			"7",
			"8",
			"9",
			"10",
			"11",
			"12",
			"13",
			"14",
			"15",
			"16"
		]
	},
	"rpc.grpc.request.metadata": {
		"description": "gRPC request metadata, `<key>` being the normalized gRPC Metadata key (lowercase), the value being the metadata values.\n",
		"type": "string",
		"note": "Instrumentations SHOULD require an explicit configuration of which metadata values are to be captured.\nIncluding all request metadata values can be a security risk - explicit configuration helps avoid leaking sensitive information.\n\nFor example, a property `my-custom-key` with value `[\"1.2.3.4\", \"1.2.3.5\"]` SHOULD be recorded as\n`rpc.grpc.request.metadata.my-custom-key` attribute with value `[\"1.2.3.4\", \"1.2.3.5\"]`\n",
		"stability": "development",
		"examples": ["[\"1.2.3.4\",\"1.2.3.5\"]"]
	},
	"rpc.grpc.response.metadata": {
		"description": "gRPC response metadata, `<key>` being the normalized gRPC Metadata key (lowercase), the value being the metadata values.\n",
		"type": "string",
		"note": "Instrumentations SHOULD require an explicit configuration of which metadata values are to be captured.\nIncluding all response metadata values can be a security risk - explicit configuration helps avoid leaking sensitive information.\n\nFor example, a property `my-custom-key` with value `[\"attribute_value\"]` SHOULD be recorded as\nthe `rpc.grpc.response.metadata.my-custom-key` attribute with value `[\"attribute_value\"]`\n",
		"stability": "development",
		"examples": ["[\"attribute_value\"]"]
	},
	"rpc.jsonrpc.error_code": {
		"description": "`error.code` property of response if it is an error response.",
		"type": "number",
		"stability": "development",
		"examples": ["-32700", "100"]
	},
	"rpc.jsonrpc.error_message": {
		"description": "`error.message` property of response if it is an error response.",
		"type": "string",
		"stability": "development",
		"examples": ["Parse error", "User already exists"]
	},
	"rpc.jsonrpc.request_id": {
		"description": "`id` property of request or response. Since protocol allows id to be int, string, `null` or missing (for notifications), value is expected to be cast to string for simplicity. Use empty string in case of `null` value. Omit entirely if this is a notification.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"10",
			"request-7",
			""
		]
	},
	"rpc.jsonrpc.version": {
		"description": "Protocol version as in `jsonrpc` property of request/response. Since JSON-RPC 1.0 doesn't specify this, the value can be omitted.",
		"type": "string",
		"stability": "development",
		"examples": ["2.0", "1.0"]
	},
	"rpc.method": {
		"description": "The name of the (logical) method being called, must be equal to the $method part in the span name.",
		"type": "string",
		"note": "This is the logical name of the method from the RPC interface perspective, which can be different from the name of any implementing method/function. The `code.function.name` attribute may be used to store the latter (e.g., method actually executing the call on the server side, RPC client stub method on the client side).\n",
		"stability": "development",
		"examples": ["exampleMethod"]
	},
	"rpc.service": {
		"description": "The full (logical) name of the service being called, including its package name, if applicable.",
		"type": "string",
		"note": "This is the logical name of the service from the RPC interface perspective, which can be different from the name of any implementing class. The `code.namespace` attribute may be used to store the latter (despite the attribute name, it may include a class name; e.g., class with method actually executing the call on the server side, RPC client stub class on the client side).\n",
		"stability": "development",
		"examples": ["myservice.EchoService"]
	},
	"rpc.system": {
		"description": "A string identifying the remoting system. See below for a list of well-known identifiers.",
		"type": "string",
		"stability": "development",
		"examples": [
			"grpc",
			"java_rmi",
			"dotnet_wcf",
			"apache_dubbo",
			"connect_rpc"
		]
	},
	"rpc.message.type": {
		"description": "Whether this is a received or sent message.",
		"type": "string",
		"stability": "development",
		"examples": ["SENT", "RECEIVED"]
	},
	"rpc.message.id": {
		"description": "MUST be calculated as two different counters starting from `1` one for sent messages and one for received message.",
		"type": "number",
		"note": "This way we guarantee that the values will be consistent between different implementations.",
		"stability": "development"
	},
	"rpc.message.compressed_size": {
		"description": "Compressed size of the message in bytes.",
		"type": "number",
		"stability": "development"
	},
	"rpc.message.uncompressed_size": {
		"description": "Uncompressed size of the message in bytes.",
		"type": "number",
		"stability": "development"
	}
};
var rpc_default = {
	namespace: namespace$16,
	description: description$16,
	attributes: attributes$16
};

//#endregion
//#region src/internal/agents/tools/data/server.json
var namespace$15 = "server";
var description$15 = "These attributes may be used to describe the server in a connection-based network interaction where there is one side that initiates the connection (the client is the side that initiates the connection). This covers all TCP network interactions since TCP is connection-based and one side initiates the connection (an exception is made for peer-to-peer communication over TCP where the \"user-facing\" surface of the protocol / API doesn't expose a clear notion of client and server). This also covers UDP network interactions where one side initiates the interaction, e.g. QUIC (HTTP/3) and DNS.\n";
var attributes$15 = {
	"server.address": {
		"description": "Server domain name if available without reverse DNS lookup; otherwise, IP address or Unix domain socket name.",
		"type": "string",
		"note": "When observed from the client side, and when communicating through an intermediary, `server.address` SHOULD represent the server address behind any intermediaries, for example proxies, if it's available.\n",
		"stability": "stable",
		"examples": [
			"example.com",
			"10.1.2.80",
			"/tmp/my.sock"
		]
	},
	"server.port": {
		"description": "Server port number.",
		"type": "number",
		"note": "When observed from the client side, and when communicating through an intermediary, `server.port` SHOULD represent the server port behind any intermediaries, for example proxies, if it's available.\n",
		"stability": "stable",
		"examples": [
			"80",
			"8080",
			"443"
		]
	}
};
var server_default = {
	namespace: namespace$15,
	description: description$15,
	attributes: attributes$15
};

//#endregion
//#region src/internal/agents/tools/data/service.json
var namespace$14 = "service";
var description$14 = "A service instance.\n";
var attributes$14 = {
	"service.name": {
		"description": "Logical name of the service.\n",
		"type": "string",
		"note": "MUST be the same for all instances of horizontally scaled services. If the value was not specified, SDKs MUST fallback to `unknown_service:` concatenated with [`process.executable.name`](process.md), e.g. `unknown_service:bash`. If `process.executable.name` is not available, the value MUST be set to `unknown_service`.\n",
		"stability": "stable",
		"examples": ["shoppingcart"]
	},
	"service.version": {
		"description": "The version string of the service API or implementation. The format is not defined by these conventions.\n",
		"type": "string",
		"stability": "stable",
		"examples": ["2.0.0", "a01dbef8a"]
	},
	"service.namespace": {
		"description": "A namespace for `service.name`.\n",
		"type": "string",
		"note": "A string value having a meaning that helps to distinguish a group of services, for example the team name that owns a group of services. `service.name` is expected to be unique within the same namespace. If `service.namespace` is not specified in the Resource then `service.name` is expected to be unique for all services that have no explicit namespace defined (so the empty/unspecified namespace is simply one more valid namespace). Zero-length namespace string is assumed equal to unspecified namespace.\n",
		"stability": "development",
		"examples": ["Shop"]
	},
	"service.instance.id": {
		"description": "The string ID of the service instance.\n",
		"type": "string",
		"note": "MUST be unique for each instance of the same `service.namespace,service.name` pair (in other words\n`service.namespace,service.name,service.instance.id` triplet MUST be globally unique). The ID helps to\ndistinguish instances of the same service that exist at the same time (e.g. instances of a horizontally scaled\nservice).\n\nImplementations, such as SDKs, are recommended to generate a random Version 1 or Version 4 [RFC\n4122](https://www.ietf.org/rfc/rfc4122.txt) UUID, but are free to use an inherent unique ID as the source of\nthis value if stability is desirable. In that case, the ID SHOULD be used as source of a UUID Version 5 and\nSHOULD use the following UUID as the namespace: `4d63009a-8d0f-11ee-aad7-4c796ed8e320`.\n\nUUIDs are typically recommended, as only an opaque value for the purposes of identifying a service instance is\nneeded. Similar to what can be seen in the man page for the\n[`/etc/machine-id`](https://www.freedesktop.org/software/systemd/man/latest/machine-id.html) file, the underlying\ndata, such as pod name and namespace should be treated as confidential, being the user's choice to expose it\nor not via another resource attribute.\n\nFor applications running behind an application server (like unicorn), we do not recommend using one identifier\nfor all processes participating in the application. Instead, it's recommended each division (e.g. a worker\nthread in unicorn) to have its own instance.id.\n\nIt's not recommended for a Collector to set `service.instance.id` if it can't unambiguously determine the\nservice instance that is generating that telemetry. For instance, creating an UUID based on `pod.name` will\nlikely be wrong, as the Collector might not know from which container within that pod the telemetry originated.\nHowever, Collectors can set the `service.instance.id` if they can unambiguously determine the service instance\nfor that telemetry. This is typically the case for scraping receivers, as they know the target address and\nport.\n",
		"stability": "development",
		"examples": ["627cc493-f310-47de-96bd-71410b7dec09"]
	}
};
var service_default = {
	namespace: namespace$14,
	description: description$14,
	attributes: attributes$14
};

//#endregion
//#region src/internal/agents/tools/data/session.json
var namespace$13 = "session";
var description$13 = "Session is defined as the period of time encompassing all activities performed by the application and the actions executed by the end user.\nConsequently, a Session is represented as a collection of Logs, Events, and Spans emitted by the Client Application throughout the Session's duration. Each Session is assigned a unique identifier, which is included as an attribute in the Logs, Events, and Spans generated during the Session's lifecycle.\nWhen a session reaches end of life, typically due to user inactivity or session timeout, a new session identifier will be assigned. The previous session identifier may be provided by the instrumentation so that telemetry backends can link the two sessions.\n";
var attributes$13 = {
	"session.id": {
		"description": "A unique id to identify a session.",
		"type": "string",
		"stability": "development",
		"examples": ["00112233-4455-6677-8899-aabbccddeeff"]
	},
	"session.previous_id": {
		"description": "The previous `session.id` for this user, when known.",
		"type": "string",
		"stability": "development",
		"examples": ["00112233-4455-6677-8899-aabbccddeeff"]
	}
};
var session_default = {
	namespace: namespace$13,
	description: description$13,
	attributes: attributes$13
};

//#endregion
//#region src/internal/agents/tools/data/signalr.json
var namespace$12 = "signalr";
var description$12 = "SignalR attributes";
var attributes$12 = {
	"signalr.connection.status": {
		"description": "SignalR HTTP connection closure status.",
		"type": "string",
		"stability": "stable",
		"examples": [
			"normal_closure",
			"timeout",
			"app_shutdown"
		]
	},
	"signalr.transport": {
		"description": "[SignalR transport type](https://github.com/dotnet/aspnetcore/blob/main/src/SignalR/docs/specs/TransportProtocols.md)",
		"type": "string",
		"stability": "stable",
		"examples": [
			"server_sent_events",
			"long_polling",
			"web_sockets"
		]
	}
};
var signalr_default = {
	namespace: namespace$12,
	description: description$12,
	attributes: attributes$12
};

//#endregion
//#region src/internal/agents/tools/data/source.json
var namespace$11 = "source";
var description$11 = "These attributes may be used to describe the sender of a network exchange/packet. These should be used when there is no client/server relationship between the two sides, or when that relationship is unknown. This covers low-level network interactions (e.g. packet tracing) where you don't know if there was a connection or which side initiated it. This also covers unidirectional UDP flows and peer-to-peer communication where the \"user-facing\" surface of the protocol / API doesn't expose a clear notion of client and server.\n";
var attributes$11 = {
	"source.address": {
		"description": "Source address - domain name if available without reverse DNS lookup; otherwise, IP address or Unix domain socket name.",
		"type": "string",
		"note": "When observed from the destination side, and when communicating through an intermediary, `source.address` SHOULD represent the source address behind any intermediaries, for example proxies, if it's available.\n",
		"stability": "development",
		"examples": [
			"source.example.com",
			"10.1.2.80",
			"/tmp/my.sock"
		]
	},
	"source.port": {
		"description": "Source port number",
		"type": "number",
		"stability": "development",
		"examples": ["3389", "2888"]
	}
};
var source_default = {
	namespace: namespace$11,
	description: description$11,
	attributes: attributes$11
};

//#endregion
//#region src/internal/agents/tools/data/system.json
var namespace$10 = "system";
var description$10 = "Describes System attributes";
var attributes$10 = { "system.device": {
	"description": "The device identifier",
	"type": "string",
	"stability": "development",
	"examples": ["(identifier)"]
} };
var system_default = {
	namespace: namespace$10,
	description: description$10,
	attributes: attributes$10
};

//#endregion
//#region src/internal/agents/tools/data/telemetry.json
var namespace$9 = "telemetry";
var description$9 = "This document defines attributes for telemetry SDK.\n";
var attributes$9 = {
	"telemetry.sdk.name": {
		"description": "The name of the telemetry SDK as defined above.\n",
		"type": "string",
		"note": "The OpenTelemetry SDK MUST set the `telemetry.sdk.name` attribute to `opentelemetry`.\nIf another SDK, like a fork or a vendor-provided implementation, is used, this SDK MUST set the\n`telemetry.sdk.name` attribute to the fully-qualified class or module name of this SDK's main entry point\nor another suitable identifier depending on the language.\nThe identifier `opentelemetry` is reserved and MUST NOT be used in this case.\nAll custom identifiers SHOULD be stable across different versions of an implementation.\n",
		"stability": "stable",
		"examples": ["opentelemetry"]
	},
	"telemetry.sdk.language": {
		"description": "The language of the telemetry SDK.\n",
		"type": "string",
		"stability": "stable",
		"examples": [
			"cpp",
			"dotnet",
			"erlang",
			"go",
			"java",
			"nodejs",
			"php",
			"python",
			"ruby",
			"rust",
			"swift",
			"webjs"
		]
	},
	"telemetry.sdk.version": {
		"description": "The version string of the telemetry SDK.\n",
		"type": "string",
		"stability": "stable",
		"examples": ["1.2.3"]
	},
	"telemetry.distro.name": {
		"description": "The name of the auto instrumentation agent or distribution, if used.\n",
		"type": "string",
		"note": "Official auto instrumentation agents and distributions SHOULD set the `telemetry.distro.name` attribute to\na string starting with `opentelemetry-`, e.g. `opentelemetry-java-instrumentation`.\n",
		"stability": "development",
		"examples": ["parts-unlimited-java"]
	},
	"telemetry.distro.version": {
		"description": "The version string of the auto instrumentation agent or distribution, if used.\n",
		"type": "string",
		"stability": "development",
		"examples": ["1.2.3"]
	}
};
var telemetry_default = {
	namespace: namespace$9,
	description: description$9,
	attributes: attributes$9
};

//#endregion
//#region src/internal/agents/tools/data/test.json
var namespace$8 = "test";
var description$8 = "This group describes attributes specific to [software tests](https://wikipedia.org/wiki/Software_testing).\n";
var attributes$8 = {
	"test.suite.name": {
		"description": "The human readable name of a [test suite](https://wikipedia.org/wiki/Test_suite).\n",
		"type": "string",
		"stability": "development",
		"examples": ["TestSuite1"]
	},
	"test.suite.run.status": {
		"description": "The status of the test suite run.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"success",
			"failure",
			"skipped",
			"aborted",
			"timed_out",
			"in_progress"
		]
	},
	"test.case.name": {
		"description": "The fully qualified human readable name of the [test case](https://wikipedia.org/wiki/Test_case).\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"org.example.TestCase1.test1",
			"example/tests/TestCase1.test1",
			"ExampleTestCase1_test1"
		]
	},
	"test.case.result.status": {
		"description": "The status of the actual test case result from test execution.\n",
		"type": "string",
		"stability": "development",
		"examples": ["pass", "fail"]
	}
};
var test_default = {
	namespace: namespace$8,
	description: description$8,
	attributes: attributes$8
};

//#endregion
//#region src/internal/agents/tools/data/thread.json
var namespace$7 = "thread";
var description$7 = "These attributes may be used for any operation to store information about a thread that started a span.\n";
var attributes$7 = {
	"thread.id": {
		"description": "Current \"managed\" thread ID (as opposed to OS thread ID).\n",
		"type": "number",
		"stability": "development",
		"examples": ["42"]
	},
	"thread.name": {
		"description": "Current thread name.\n",
		"type": "string",
		"stability": "development",
		"examples": ["main"]
	}
};
var thread_default = {
	namespace: namespace$7,
	description: description$7,
	attributes: attributes$7
};

//#endregion
//#region src/internal/agents/tools/data/tls.json
var namespace$6 = "tls";
var description$6 = "This document defines semantic convention attributes in the TLS namespace.";
var attributes$6 = {
	"tls.cipher": {
		"description": "String indicating the [cipher](https://datatracker.ietf.org/doc/html/rfc5246#appendix-A.5) used during the current connection.\n",
		"type": "string",
		"note": "The values allowed for `tls.cipher` MUST be one of the `Descriptions` of the [registered TLS Cipher Suits](https://www.iana.org/assignments/tls-parameters/tls-parameters.xhtml#table-tls-parameters-4).\n",
		"stability": "development",
		"examples": ["TLS_RSA_WITH_3DES_EDE_CBC_SHA", "TLS_ECDHE_RSA_WITH_AES_128_CBC_SHA256"]
	},
	"tls.client.certificate": {
		"description": "PEM-encoded stand-alone certificate offered by the client. This is usually mutually-exclusive of `client.certificate_chain` since this value also exists in that list.\n",
		"type": "string",
		"stability": "development",
		"examples": ["MII..."]
	},
	"tls.client.certificate_chain": {
		"description": "Array of PEM-encoded certificates that make up the certificate chain offered by the client. This is usually mutually-exclusive of `client.certificate` since that value should be the first certificate in the chain.\n",
		"type": "string",
		"stability": "development",
		"examples": ["[\"MII...\",\"MI...\"]"]
	},
	"tls.client.hash.md5": {
		"description": "Certificate fingerprint using the MD5 digest of DER-encoded version of certificate offered by the client. For consistency with other hash values, this value should be formatted as an uppercase hash.\n",
		"type": "string",
		"stability": "development",
		"examples": ["0F76C7F2C55BFD7D8E8B8F4BFBF0C9EC"]
	},
	"tls.client.hash.sha1": {
		"description": "Certificate fingerprint using the SHA1 digest of DER-encoded version of certificate offered by the client. For consistency with other hash values, this value should be formatted as an uppercase hash.\n",
		"type": "string",
		"stability": "development",
		"examples": ["9E393D93138888D288266C2D915214D1D1CCEB2A"]
	},
	"tls.client.hash.sha256": {
		"description": "Certificate fingerprint using the SHA256 digest of DER-encoded version of certificate offered by the client. For consistency with other hash values, this value should be formatted as an uppercase hash.\n",
		"type": "string",
		"stability": "development",
		"examples": ["0687F666A054EF17A08E2F2162EAB4CBC0D265E1D7875BE74BF3C712CA92DAF0"]
	},
	"tls.client.issuer": {
		"description": "Distinguished name of [subject](https://datatracker.ietf.org/doc/html/rfc5280#section-4.1.2.6) of the issuer of the x.509 certificate presented by the client.",
		"type": "string",
		"stability": "development",
		"examples": ["CN=Example Root CA, OU=Infrastructure Team, DC=example, DC=com"]
	},
	"tls.client.ja3": {
		"description": "A hash that identifies clients based on how they perform an SSL/TLS handshake.",
		"type": "string",
		"stability": "development",
		"examples": ["d4e5b18d6b55c71272893221c96ba240"]
	},
	"tls.client.not_after": {
		"description": "Date/Time indicating when client certificate is no longer considered valid.",
		"type": "string",
		"stability": "development",
		"examples": ["2021-01-01T00:00:00.000Z"]
	},
	"tls.client.not_before": {
		"description": "Date/Time indicating when client certificate is first considered valid.",
		"type": "string",
		"stability": "development",
		"examples": ["1970-01-01T00:00:00.000Z"]
	},
	"tls.client.subject": {
		"description": "Distinguished name of subject of the x.509 certificate presented by the client.",
		"type": "string",
		"stability": "development",
		"examples": ["CN=myclient, OU=Documentation Team, DC=example, DC=com"]
	},
	"tls.client.supported_ciphers": {
		"description": "Array of ciphers offered by the client during the client hello.",
		"type": "string",
		"stability": "development",
		"examples": ["[\"TLS_ECDHE_RSA_WITH_AES_256_GCM_SHA384\",\"TLS_ECDHE_ECDSA_WITH_AES_256_GCM_SHA384\"]"]
	},
	"tls.curve": {
		"description": "String indicating the curve used for the given cipher, when applicable",
		"type": "string",
		"stability": "development",
		"examples": ["secp256r1"]
	},
	"tls.established": {
		"description": "Boolean flag indicating if the TLS negotiation was successful and transitioned to an encrypted tunnel.",
		"type": "boolean",
		"stability": "development",
		"examples": ["true"]
	},
	"tls.next_protocol": {
		"description": "String indicating the protocol being tunneled. Per the values in the [IANA registry](https://www.iana.org/assignments/tls-extensiontype-values/tls-extensiontype-values.xhtml#alpn-protocol-ids), this string should be lower case.\n",
		"type": "string",
		"stability": "development",
		"examples": ["http/1.1"]
	},
	"tls.protocol.name": {
		"description": "Normalized lowercase protocol name parsed from original string of the negotiated [SSL/TLS protocol version](https://docs.openssl.org/1.1.1/man3/SSL_get_version/#return-values)\n",
		"type": "string",
		"stability": "development",
		"examples": ["ssl", "tls"]
	},
	"tls.protocol.version": {
		"description": "Numeric part of the version parsed from the original string of the negotiated [SSL/TLS protocol version](https://docs.openssl.org/1.1.1/man3/SSL_get_version/#return-values)\n",
		"type": "string",
		"stability": "development",
		"examples": ["1.2", "3"]
	},
	"tls.resumed": {
		"description": "Boolean flag indicating if this TLS connection was resumed from an existing TLS negotiation.",
		"type": "boolean",
		"stability": "development",
		"examples": ["true"]
	},
	"tls.server.certificate": {
		"description": "PEM-encoded stand-alone certificate offered by the server. This is usually mutually-exclusive of `server.certificate_chain` since this value also exists in that list.\n",
		"type": "string",
		"stability": "development",
		"examples": ["MII..."]
	},
	"tls.server.certificate_chain": {
		"description": "Array of PEM-encoded certificates that make up the certificate chain offered by the server. This is usually mutually-exclusive of `server.certificate` since that value should be the first certificate in the chain.\n",
		"type": "string",
		"stability": "development",
		"examples": ["[\"MII...\",\"MI...\"]"]
	},
	"tls.server.hash.md5": {
		"description": "Certificate fingerprint using the MD5 digest of DER-encoded version of certificate offered by the server. For consistency with other hash values, this value should be formatted as an uppercase hash.\n",
		"type": "string",
		"stability": "development",
		"examples": ["0F76C7F2C55BFD7D8E8B8F4BFBF0C9EC"]
	},
	"tls.server.hash.sha1": {
		"description": "Certificate fingerprint using the SHA1 digest of DER-encoded version of certificate offered by the server. For consistency with other hash values, this value should be formatted as an uppercase hash.\n",
		"type": "string",
		"stability": "development",
		"examples": ["9E393D93138888D288266C2D915214D1D1CCEB2A"]
	},
	"tls.server.hash.sha256": {
		"description": "Certificate fingerprint using the SHA256 digest of DER-encoded version of certificate offered by the server. For consistency with other hash values, this value should be formatted as an uppercase hash.\n",
		"type": "string",
		"stability": "development",
		"examples": ["0687F666A054EF17A08E2F2162EAB4CBC0D265E1D7875BE74BF3C712CA92DAF0"]
	},
	"tls.server.issuer": {
		"description": "Distinguished name of [subject](https://datatracker.ietf.org/doc/html/rfc5280#section-4.1.2.6) of the issuer of the x.509 certificate presented by the client.",
		"type": "string",
		"stability": "development",
		"examples": ["CN=Example Root CA, OU=Infrastructure Team, DC=example, DC=com"]
	},
	"tls.server.ja3s": {
		"description": "A hash that identifies servers based on how they perform an SSL/TLS handshake.",
		"type": "string",
		"stability": "development",
		"examples": ["d4e5b18d6b55c71272893221c96ba240"]
	},
	"tls.server.not_after": {
		"description": "Date/Time indicating when server certificate is no longer considered valid.",
		"type": "string",
		"stability": "development",
		"examples": ["2021-01-01T00:00:00.000Z"]
	},
	"tls.server.not_before": {
		"description": "Date/Time indicating when server certificate is first considered valid.",
		"type": "string",
		"stability": "development",
		"examples": ["1970-01-01T00:00:00.000Z"]
	},
	"tls.server.subject": {
		"description": "Distinguished name of subject of the x.509 certificate presented by the server.",
		"type": "string",
		"stability": "development",
		"examples": ["CN=myserver, OU=Documentation Team, DC=example, DC=com"]
	}
};
var tls_default = {
	namespace: namespace$6,
	description: description$6,
	attributes: attributes$6
};

//#endregion
//#region src/internal/agents/tools/data/url.json
var namespace$5 = "url";
var description$5 = "Attributes describing URL.";
var attributes$5 = {
	"url.domain": {
		"description": "Domain extracted from the `url.full`, such as \"opentelemetry.io\".\n",
		"type": "string",
		"note": "In some cases a URL may refer to an IP and/or port directly, without a domain name. In this case, the IP address would go to the domain field. If the URL contains a [literal IPv6 address](https://www.rfc-editor.org/rfc/rfc2732#section-2) enclosed by `[` and `]`, the `[` and `]` characters should also be captured in the domain field.\n",
		"stability": "development",
		"examples": [
			"www.foo.bar",
			"opentelemetry.io",
			"3.12.167.2",
			"[1080:0:0:0:8:800:200C:417A]"
		]
	},
	"url.extension": {
		"description": "The file extension extracted from the `url.full`, excluding the leading dot.\n",
		"type": "string",
		"note": "The file extension is only set if it exists, as not every url has a file extension. When the file name has multiple extensions `example.tar.gz`, only the last one should be captured `gz`, not `tar.gz`.\n",
		"stability": "development",
		"examples": ["png", "gz"]
	},
	"url.fragment": {
		"description": "The [URI fragment](https://www.rfc-editor.org/rfc/rfc3986#section-3.5) component\n",
		"type": "string",
		"stability": "stable",
		"examples": ["SemConv"]
	},
	"url.full": {
		"description": "Absolute URL describing a network resource according to [RFC3986](https://www.rfc-editor.org/rfc/rfc3986)",
		"type": "string",
		"note": "For network calls, URL usually has `scheme://host[:port][path][?query][#fragment]` format, where the fragment\nis not transmitted over HTTP, but if it is known, it SHOULD be included nevertheless.\n\n`url.full` MUST NOT contain credentials passed via URL in form of `https://username:password@www.example.com/`.\nIn such case username and password SHOULD be redacted and attribute's value SHOULD be `https://REDACTED:REDACTED@www.example.com/`.\n\n`url.full` SHOULD capture the absolute URL when it is available (or can be reconstructed).\n\nSensitive content provided in `url.full` SHOULD be scrubbed when instrumentations can identify it.\n\n![Development](https://img.shields.io/badge/-development-blue)\nQuery string values for the following keys SHOULD be redacted by default and replaced by the\nvalue `REDACTED`:\n\n* [`AWSAccessKeyId`](https://docs.aws.amazon.com/AmazonS3/latest/userguide/RESTAuthentication.html#RESTAuthenticationQueryStringAuth)\n* [`Signature`](https://docs.aws.amazon.com/AmazonS3/latest/userguide/RESTAuthentication.html#RESTAuthenticationQueryStringAuth)\n* [`sig`](https://learn.microsoft.com/azure/storage/common/storage-sas-overview#sas-token)\n* [`X-Goog-Signature`](https://cloud.google.com/storage/docs/access-control/signed-urls)\n\nThis list is subject to change over time.\n\nWhen a query string value is redacted, the query string key SHOULD still be preserved, e.g.\n`https://www.example.com/path?color=blue&sig=REDACTED`.\n",
		"stability": "stable",
		"examples": ["https://www.foo.bar/search?q=OpenTelemetry#SemConv", "//localhost"]
	},
	"url.original": {
		"description": "Unmodified original URL as seen in the event source.\n",
		"type": "string",
		"note": "In network monitoring, the observed URL may be a full URL, whereas in access logs, the URL is often just represented as a path. This field is meant to represent the URL as it was observed, complete or not.\n`url.original` might contain credentials passed via URL in form of `https://username:password@www.example.com/`. In such case password and username SHOULD NOT be redacted and attribute's value SHOULD remain the same.\n",
		"stability": "development",
		"examples": ["https://www.foo.bar/search?q=OpenTelemetry#SemConv", "search?q=OpenTelemetry"]
	},
	"url.path": {
		"description": "The [URI path](https://www.rfc-editor.org/rfc/rfc3986#section-3.3) component\n",
		"type": "string",
		"note": "Sensitive content provided in `url.path` SHOULD be scrubbed when instrumentations can identify it.\n",
		"stability": "stable",
		"examples": ["/search"]
	},
	"url.port": {
		"description": "Port extracted from the `url.full`\n",
		"type": "number",
		"stability": "development",
		"examples": ["443"]
	},
	"url.query": {
		"description": "The [URI query](https://www.rfc-editor.org/rfc/rfc3986#section-3.4) component\n",
		"type": "string",
		"note": "Sensitive content provided in `url.query` SHOULD be scrubbed when instrumentations can identify it.\n\n![Development](https://img.shields.io/badge/-development-blue)\nQuery string values for the following keys SHOULD be redacted by default and replaced by the value `REDACTED`:\n\n* [`AWSAccessKeyId`](https://docs.aws.amazon.com/AmazonS3/latest/userguide/RESTAuthentication.html#RESTAuthenticationQueryStringAuth)\n* [`Signature`](https://docs.aws.amazon.com/AmazonS3/latest/userguide/RESTAuthentication.html#RESTAuthenticationQueryStringAuth)\n* [`sig`](https://learn.microsoft.com/azure/storage/common/storage-sas-overview#sas-token)\n* [`X-Goog-Signature`](https://cloud.google.com/storage/docs/access-control/signed-urls)\n\nThis list is subject to change over time.\n\nWhen a query string value is redacted, the query string key SHOULD still be preserved, e.g.\n`q=OpenTelemetry&sig=REDACTED`.\n",
		"stability": "stable",
		"examples": ["q=OpenTelemetry"]
	},
	"url.registered_domain": {
		"description": "The highest registered url domain, stripped of the subdomain.\n",
		"type": "string",
		"note": "This value can be determined precisely with the [public suffix list](https://publicsuffix.org/). For example, the registered domain for `foo.example.com` is `example.com`. Trying to approximate this by simply taking the last two labels will not work well for TLDs such as `co.uk`.\n",
		"stability": "development",
		"examples": ["example.com", "foo.co.uk"]
	},
	"url.scheme": {
		"description": "The [URI scheme](https://www.rfc-editor.org/rfc/rfc3986#section-3.1) component identifying the used protocol.\n",
		"type": "string",
		"stability": "stable",
		"examples": [
			"https",
			"ftp",
			"telnet"
		]
	},
	"url.subdomain": {
		"description": "The subdomain portion of a fully qualified domain name includes all of the names except the host name under the registered_domain. In a partially qualified domain, or if the qualification level of the full name cannot be determined, subdomain contains all of the names below the registered domain.\n",
		"type": "string",
		"note": "The subdomain portion of `www.east.mydomain.co.uk` is `east`. If the domain has multiple levels of subdomain, such as `sub2.sub1.example.com`, the subdomain field should contain `sub2.sub1`, with no trailing period.\n",
		"stability": "development",
		"examples": ["east", "sub2.sub1"]
	},
	"url.template": {
		"description": "The low-cardinality template of an [absolute path reference](https://www.rfc-editor.org/rfc/rfc3986#section-4.2).\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"/users/{id}",
			"/users/:id",
			"/users?id={id}"
		]
	},
	"url.top_level_domain": {
		"description": "The effective top level domain (eTLD), also known as the domain suffix, is the last part of the domain name. For example, the top level domain for example.com is `com`.\n",
		"type": "string",
		"note": "This value can be determined precisely with the [public suffix list](https://publicsuffix.org/).\n",
		"stability": "development",
		"examples": ["com", "co.uk"]
	}
};
var url_default = {
	namespace: namespace$5,
	description: description$5,
	attributes: attributes$5
};

//#endregion
//#region src/internal/agents/tools/data/user.json
var namespace$4 = "user";
var description$4 = "Describes information about the user.";
var attributes$4 = {
	"user.email": {
		"description": "User email address.\n",
		"type": "string",
		"stability": "development",
		"examples": ["a.einstein@example.com"]
	},
	"user.full_name": {
		"description": "User's full name\n",
		"type": "string",
		"stability": "development",
		"examples": ["Albert Einstein"]
	},
	"user.hash": {
		"description": "Unique user hash to correlate information for a user in anonymized form.\n",
		"type": "string",
		"note": "Useful if `user.id` or `user.name` contain confidential information and cannot be used.\n",
		"stability": "development",
		"examples": ["364fc68eaf4c8acec74a4e52d7d1feaa"]
	},
	"user.id": {
		"description": "Unique identifier of the user.\n",
		"type": "string",
		"stability": "development",
		"examples": ["S-1-5-21-202424912787-2692429404-2351956786-1000"]
	},
	"user.name": {
		"description": "Short name or login/username of the user.\n",
		"type": "string",
		"stability": "development",
		"examples": ["a.einstein"]
	},
	"user.roles": {
		"description": "Array of user roles at the time of the event.\n",
		"type": "string",
		"stability": "development",
		"examples": ["[\"admin\",\"reporting_user\"]"]
	}
};
var user_default = {
	namespace: namespace$4,
	description: description$4,
	attributes: attributes$4
};

//#endregion
//#region src/internal/agents/tools/data/v8js.json
var namespace$3 = "v8js";
var description$3 = "Describes V8 JS Engine Runtime related attributes.";
var attributes$3 = {
	"v8js.gc.type": {
		"description": "The type of garbage collection.",
		"type": "string",
		"stability": "development",
		"examples": [
			"major",
			"minor",
			"incremental",
			"weakcb"
		]
	},
	"v8js.heap.space.name": {
		"description": "The name of the space type of heap memory.",
		"type": "string",
		"note": "Value can be retrieved from value `space_name` of [`v8.getHeapSpaceStatistics()`](https://nodejs.org/api/v8.html#v8getheapspacestatistics)\n",
		"stability": "development",
		"examples": [
			"new_space",
			"old_space",
			"code_space",
			"map_space",
			"large_object_space"
		]
	}
};
var v8js_default = {
	namespace: namespace$3,
	description: description$3,
	attributes: attributes$3
};

//#endregion
//#region src/internal/agents/tools/data/vcs.json
var namespace$2 = "vcs";
var description$2 = "This group defines the attributes for [Version Control Systems (VCS)](https://wikipedia.org/wiki/Version_control).\n";
var attributes$2 = {
	"vcs.repository.url.full": {
		"description": "The [canonical URL](https://support.google.com/webmasters/answer/10347851?hl=en#:~:text=A%20canonical%20URL%20is%20the,Google%20chooses%20one%20as%20canonical.) of the repository providing the complete HTTP(S) address in order to locate and identify the repository through a browser.\n",
		"type": "string",
		"note": "In Git Version Control Systems, the canonical URL SHOULD NOT include\nthe `.git` extension.\n",
		"stability": "development",
		"examples": ["https://github.com/opentelemetry/open-telemetry-collector-contrib", "https://gitlab.com/my-org/my-project/my-projects-project/repo"]
	},
	"vcs.repository.name": {
		"description": "The human readable name of the repository. It SHOULD NOT include any additional identifier like Group/SubGroup in GitLab or organization in GitHub.\n",
		"type": "string",
		"note": "Due to it only being the name, it can clash with forks of the same\nrepository if collecting telemetry across multiple orgs or groups in\nthe same backends.\n",
		"stability": "development",
		"examples": ["semantic-conventions", "my-cool-repo"]
	},
	"vcs.ref.base.name": {
		"description": "The name of the [reference](https://git-scm.com/docs/gitglossary#def_ref) such as **branch** or **tag** in the repository.\n",
		"type": "string",
		"note": "`base` refers to the starting point of a change. For example, `main`\nwould be the base reference of type branch if you've created a new\nreference of type branch from it and created new commits.\n",
		"stability": "development",
		"examples": ["my-feature-branch", "tag-1-test"]
	},
	"vcs.ref.base.type": {
		"description": "The type of the [reference](https://git-scm.com/docs/gitglossary#def_ref) in the repository.\n",
		"type": "string",
		"note": "`base` refers to the starting point of a change. For example, `main`\nwould be the base reference of type branch if you've created a new\nreference of type branch from it and created new commits.\n",
		"stability": "development",
		"examples": ["branch", "tag"]
	},
	"vcs.ref.base.revision": {
		"description": "The revision, literally [revised version](https://www.merriam-webster.com/dictionary/revision), The revision most often refers to a commit object in Git, or a revision number in SVN.\n",
		"type": "string",
		"note": "`base` refers to the starting point of a change. For example, `main`\nwould be the base reference of type branch if you've created a new\nreference of type branch from it and created new commits. The\nrevision can be a full [hash value (see\nglossary)](https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.186-5.pdf),\nof the recorded change to a ref within a repository pointing to a\ncommit [commit](https://git-scm.com/docs/git-commit) object. It does\nnot necessarily have to be a hash; it can simply define a [revision\nnumber](https://svnbook.red-bean.com/en/1.7/svn.tour.revs.specifiers.html)\nwhich is an integer that is monotonically increasing. In cases where\nit is identical to the `ref.base.name`, it SHOULD still be included.\nIt is up to the implementer to decide which value to set as the\nrevision based on the VCS system and situational context.\n",
		"stability": "development",
		"examples": [
			"9d59409acf479dfa0df1aa568182e43e43df8bbe28d60fcf2bc52e30068802cc",
			"main",
			"123",
			"HEAD"
		]
	},
	"vcs.ref.head.name": {
		"description": "The name of the [reference](https://git-scm.com/docs/gitglossary#def_ref) such as **branch** or **tag** in the repository.\n",
		"type": "string",
		"note": "`head` refers to where you are right now; the current reference at a\ngiven time.\n",
		"stability": "development",
		"examples": ["my-feature-branch", "tag-1-test"]
	},
	"vcs.ref.head.type": {
		"description": "The type of the [reference](https://git-scm.com/docs/gitglossary#def_ref) in the repository.\n",
		"type": "string",
		"note": "`head` refers to where you are right now; the current reference at a\ngiven time.\n",
		"stability": "development",
		"examples": ["branch", "tag"]
	},
	"vcs.ref.head.revision": {
		"description": "The revision, literally [revised version](https://www.merriam-webster.com/dictionary/revision), The revision most often refers to a commit object in Git, or a revision number in SVN.\n",
		"type": "string",
		"note": "`head` refers to where you are right now; the current reference at a\ngiven time.The revision can be a full [hash value (see\nglossary)](https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.186-5.pdf),\nof the recorded change to a ref within a repository pointing to a\ncommit [commit](https://git-scm.com/docs/git-commit) object. It does\nnot necessarily have to be a hash; it can simply define a [revision\nnumber](https://svnbook.red-bean.com/en/1.7/svn.tour.revs.specifiers.html)\nwhich is an integer that is monotonically increasing. In cases where\nit is identical to the `ref.head.name`, it SHOULD still be included.\nIt is up to the implementer to decide which value to set as the\nrevision based on the VCS system and situational context.\n",
		"stability": "development",
		"examples": [
			"9d59409acf479dfa0df1aa568182e43e43df8bbe28d60fcf2bc52e30068802cc",
			"main",
			"123",
			"HEAD"
		]
	},
	"vcs.ref.type": {
		"description": "The type of the [reference](https://git-scm.com/docs/gitglossary#def_ref) in the repository.\n",
		"type": "string",
		"stability": "development",
		"examples": ["branch", "tag"]
	},
	"vcs.revision_delta.direction": {
		"description": "The type of revision comparison.\n",
		"type": "string",
		"stability": "development",
		"examples": ["behind", "ahead"]
	},
	"vcs.line_change.type": {
		"description": "The type of line change being measured on a branch or change.\n",
		"type": "string",
		"stability": "development",
		"examples": ["added", "removed"]
	},
	"vcs.change.title": {
		"description": "The human readable title of the change (pull request/merge request/changelist). This title is often a brief summary of the change and may get merged in to a ref as the commit summary.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"Fixes broken thing",
			"feat: add my new feature",
			"[chore] update dependency"
		]
	},
	"vcs.change.id": {
		"description": "The ID of the change (pull request/merge request/changelist) if applicable. This is usually a unique (within repository) identifier generated by the VCS system.\n",
		"type": "string",
		"stability": "development",
		"examples": ["123"]
	},
	"vcs.change.state": {
		"description": "The state of the change (pull request/merge request/changelist).\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"open",
			"wip",
			"closed",
			"merged"
		]
	},
	"vcs.owner.name": {
		"description": "The group owner within the version control system.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"my-org",
			"myteam",
			"business-unit"
		]
	},
	"vcs.provider.name": {
		"description": "The name of the version control system provider.\n",
		"type": "string",
		"stability": "development",
		"examples": [
			"github",
			"gitlab",
			"gittea",
			"gitea",
			"bitbucket"
		]
	}
};
var vcs_default = {
	namespace: namespace$2,
	description: description$2,
	attributes: attributes$2
};

//#endregion
//#region src/internal/agents/tools/data/webengine.json
var namespace$1 = "webengine";
var description$1 = "This document defines the attributes used to describe the packaged software running the application code.\n";
var attributes$1 = {
	"webengine.name": {
		"description": "The name of the web engine.\n",
		"type": "string",
		"stability": "development",
		"examples": ["WildFly"]
	},
	"webengine.version": {
		"description": "The version of the web engine.\n",
		"type": "string",
		"stability": "development",
		"examples": ["21.0.0"]
	},
	"webengine.description": {
		"description": "Additional description of the web engine (e.g. detailed version and edition information).\n",
		"type": "string",
		"stability": "development",
		"examples": ["WildFly Full 21.0.0.Final (WildFly Core 13.0.1.Final) - 2.2.2.Final"]
	}
};
var webengine_default = {
	namespace: namespace$1,
	description: description$1,
	attributes: attributes$1
};

//#endregion
//#region src/internal/agents/tools/data/zos.json
var namespace = "zos";
var description = "This document defines attributes of a z/OS resource.\n";
var attributes = {
	"zos.smf.id": {
		"description": "The System Management Facility (SMF) Identifier uniquely identified a z/OS system within a SYSPLEX or mainframe environment and is used for system and performance analysis.",
		"type": "string",
		"stability": "development",
		"examples": ["SYS1"]
	},
	"zos.sysplex.name": {
		"description": "The name of the SYSPLEX to which the z/OS system belongs too.",
		"type": "string",
		"stability": "development",
		"examples": ["SYSPLEX1"]
	}
};
var zos_default = {
	namespace,
	description,
	attributes
};

//#endregion
//#region src/internal/agents/tools/otel-semantics.ts
const namespaceData = {
	android: android_default,
	app: app_default,
	artifact: artifact_default,
	aspnetcore: aspnetcore_default,
	aws: aws_default,
	azure: azure_default,
	browser: browser_default,
	cassandra: cassandra_default,
	cicd: cicd_default,
	client: client_default,
	cloud: cloud_default,
	cloudevents: cloudevents_default,
	cloudfoundry: cloudfoundry_default,
	code: code_default,
	container: container_default,
	cpu: cpu_default,
	cpython: cpython_default,
	database: database_default,
	db: db_default,
	deployment: deployment_default,
	destination: destination_default,
	device: device_default,
	disk: disk_default,
	dns: dns_default,
	dotnet: dotnet_default,
	elasticsearch: elasticsearch_default,
	enduser: enduser_default,
	error: error_default,
	faas: faas_default,
	feature_flags: feature_flags_default,
	file: file_default,
	gcp: gcp_default,
	gen_ai: gen_ai_default,
	geo: geo_default,
	go: go_default,
	graphql: graphql_default,
	hardware: hardware_default,
	heroku: heroku_default,
	host: host_default,
	http: http_default,
	ios: ios_default,
	jvm: jvm_default,
	k8s: k8s_default,
	linux: linux_default,
	log: log_default,
	mcp: mcp_default,
	messaging: messaging_default,
	network: network_default,
	nodejs: nodejs_default,
	oci: oci_default,
	opentracing: opentracing_default,
	os: os_default,
	otel: otel_default,
	peer: peer_default,
	process: process_default,
	profile: profile_default,
	rpc: rpc_default,
	server: server_default,
	service: service_default,
	session: session_default,
	signalr: signalr_default,
	source: source_default,
	system: system_default,
	telemetry: telemetry_default,
	test: test_default,
	thread: thread_default,
	tls: tls_default,
	url: url_default,
	user: user_default,
	v8js: v8js_default,
	vcs: vcs_default,
	webengine: webengine_default,
	zos: zos_default
};
/**
* Lookup OpenTelemetry semantic convention attributes for a given namespace
*/
async function lookupOtelSemantics(namespace$73, dataset, apiService, organizationSlug, projectId) {
	const normalizedNamespace = namespace$73.replace(/-/g, "_");
	const data = namespaceData[normalizedNamespace];
	if (!data) {
		const allNamespaces = Object.keys(namespaceData);
		const suggestions = allNamespaces.filter((ns) => ns.includes(namespace$73) || namespace$73.includes(ns)).slice(0, 3);
		return suggestions.length > 0 ? `Namespace '${namespace$73}' not found. Did you mean: ${suggestions.join(", ")}?` : `Namespace '${namespace$73}' not found. Use 'list' to see all available namespaces.`;
	}
	let response = `# OpenTelemetry Semantic Conventions: ${data.namespace}\n\n`;
	response += `${data.description}\n\n`;
	if (data.custom) response += "**Note:** This is a custom namespace, not part of standard OpenTelemetry conventions.\n\n";
	const attributes$73 = Object.entries(data.attributes);
	recordAgentToolResultCount(attributes$73.length);
	response += `## Attributes (${attributes$73.length} total)\n\n`;
	const sortedAttributes = attributes$73.sort(([a], [b]) => a.localeCompare(b));
	for (const [key, attr] of sortedAttributes) {
		response += `### \`${key}\`\n`;
		response += `- **Type:** ${attr.type}\n`;
		response += `- **Description:** ${attr.description}\n`;
		if (attr.stability) response += `- **Stability:** ${attr.stability}\n`;
		if (attr.examples && attr.examples.length > 0) response += `- **Examples:** ${attr.examples.map((ex) => `\`${typeof ex === "object" ? JSON.stringify(ex) : String(ex)}\``).join(", ")}\n`;
		if (attr.note) response += `- **Note:** ${attr.note}\n`;
		response += "\n";
	}
	return response;
}
/**
* Create the otel-semantics-lookup tool for AI agents
*/
/**
* Create a tool for looking up OpenTelemetry semantic convention attributes
* The tool is pre-bound with the API service and organization configured for the appropriate region
*/
function createOtelLookupTool(options) {
	const { apiService, organizationSlug, projectId } = options;
	return agentTool({
		description: "Look up OpenTelemetry semantic convention attributes for a specific namespace. OpenTelemetry attributes are universal standards that work across all datasets.",
		parameters: z.object({
			namespace: z.string().describe("The OpenTelemetry namespace to look up (e.g., 'gen_ai', 'db', 'http', 'mcp')"),
			dataset: z.enum(PUBLIC_EVENTS_DATASETS).describe("REQUIRED: Dataset to check attribute availability in. The agent MUST specify this based on their chosen dataset.")
		}),
		execute: async ({ namespace: namespace$73, dataset }) => {
			return await lookupOtelSemantics(namespace$73, dataset, apiService, organizationSlug, projectId);
		}
	});
}

//#endregion
export { createOtelLookupTool, lookupOtelSemantics };
//# sourceMappingURL=otel-semantics-DGeu0HI-.js.map